import type { Dayjs } from "dayjs";
import { FilterKeyTypes } from "../../shared/types/FilterKeys.types";

// Browser addresses (not API paths) that more than one file needs.
export const ROUTE_CONSTANT = {
  HEAD_OFFICE: "/headoffice",
  ENTITIES: "/clients",
  ENGAGEMENTS: "/engagements",
  // The project details page: the address plus "/" and the project id.
  PROJECT_DETAILS: "/calculations/project",
  // The old address of the Entities pages; kept only so old bookmarks and links are sent on.
  LEGACY_ENTITIES: "/entities",
};

export const COUNT_OF_SHEET_INSTRUCTION_ROWS = 9;
export const HEADER_ROWS_COUNT = 6;
export const HEADER_ROWS_COUNT_WITHOUT_INSTRUCTIONS = 3;
export const EXCEL_BASE_DATE = 25567;
export const EXTRA_ROWS = 30;
export const MAX_ROW_SIZE = 120;
export const ROW_SIZE_FOR_API_CALL = 50;
export const SCROLL_THRESHOLD_VALUE = 50;

export const CONDITION_TRUE = "Yes";
export const CONDITION_FALSE = "No";
export const SCROLL_EVENT_LISTENER_TYPE = "scroll";

//Place holder text for the base components
export const ACCOUNTS_PLACEHOLDER = "Accounts";
export const CHART_OF_ACCOUNTS_PLACEHOLDER = "Chart of Accounts";
export const CLIENT_ENGAGEMENT_ENTITY_PLACEHOLDER = "Client-Engagement-Entity";
export const DEFAULT_SELECT_PLACEHOLDER = "Select...";
export const USERNAME_PLACEHOLDER = "Username";
export const PASSWORD_PLACEHOLDER = "Password";
export const ENTITY_PROJECT_PLACEHOLDER = "Entity Projects";
export const FETCHING_FILTERS_PLACEHOLDER = "Fetching filters if available";
export const LOADING_PLACEHOLDER = "Loading...";
export const NO_COA_PLACEHOLDER = "No Coa Found";
export const NO_COMBINATION_FOUND_PLACEHOLDER = "No Combination Found";
export const NO_DATA_PLACEHOLDER = "No data found";
export const NO_MATCHES_FOUND_PLACEHOLDER = "No matches found...";
export const NO_OPTIONS_FOUND_PLACEHOLDER = "No options found";
export const NO_RULE_ENGINE_PLACEHOLDER = "No Rule Engine Found";
export const PROJECT_MENU_PLACEHOLDER = "Project Menu";
export const PROJECT_PLACEHOLDER = "Project";
export const RULE_ENGINE_PLACEHOLDER = "Rule Engine";
export const SHEET_DOESNOT_EXIST_PLACEHOLDER = "Sheet does not exist";
export const FILTER_OPTIONS_PLACEHOLDER = "Filter Options";
//Navigation tab names
export const NAVIGATION_TABS = [
  "Deliverables",
  "Templates",
  "Chart of Accounts",
  "Administration",
];

// Keys for the base components
export const EXCEL_VIEW_ROUTE_KEY = "Allocation";
export const LANDING_ROUTE_KEY = "Landing";
export const LOGIN_ROUTE_KEY = "Login";
export const PROJECT_TYPE_REF_KEY = "projectType";
export const TAX_END_YEAR_REF_KEY = "taxEndYear";
export const TAX_START_YEAR_REF_KEY = "taxStartYear";
export const TAX_YEAR_TYPE_REF_KEY = "taxYearType";
export const USER_NAME_KEY = "userNameValue";
export const USER_PASSWORD_KEY = "userSecretValue";
export const LOGOUT = "Log Out";
export const NAVBAR_KEY = "navbar";

// Status message for the components
export const IMPORT_IN_PROGRESS_MESSAGE =
  " There is an import task for this project which is in progress. Please try after some time.";
export const NO_SHEETS_IN_HIERARCHY = "No sheets to display...";
export const TOAST_CONTAINER_CLASS = "mainContainerToast";
export const TOAST_DELIVERABLE_CREATED_MESSAGE =
  "Deliverable created successfully.";
export const TOAST_POSITION = "top-center";

// Labels values for the components
export const NEW_TEMPLATE_LABEL = "New Template";
export const NEW_COA_LABEL = "New CoA Template";
export const GLOBAL_COA_LABEL = "Global Chart of Accounts";
export const CLIENT_NAME_LABEL = "Client ";
export const ENGAGEMENT_NAME_LABEL = "Engagement";
export const TAX_YEAR_NAME_LABEL = "Tax Year";
export const PROJECT_TYPE_LABEL = "Project Type";
export const TAX_YEAR_END_DATE_LABEL = "Tax Year End Date";
export const TAX_YEAR_START_DATE_LABEL = "Tax Year Start Date";
export const TAX_YEAR_TYPE_LABEL = "Tax Year Type";
export const FILTER_NAME_LABEL = "Name";
export const VERSION = "v1.7";
export const CANCEL_BUTTON_LABEL = String.fromCharCode(0x2716);
export const APPLY_BUTTON_LABEL = String.fromCharCode(0x2714);
export const CLEAR_FILTER_TEXT = "Clear Filter";

export const FILTER_ITEMS_PROJECT: string[] = [
  "Clients",
  "Engagements",
  "Entity",
  "Tax Year",
];
export const PATHS_WITH_NO_SIDEBAR = ["login", "project"];
export const DEFAULT_DATE_FORMAT = "d-MMM-yy";
export const CHARACTERS_FOR_GETTING_COLUMN_INDEX = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
export const ITEMS_PER_PAGE = 10;

//Regex patterns used in the application
export const CELL_FORMULA_ROW_INDEX_INDETIFIER_REGEX = /[A-Za-z]{1,2}(?:\d+)/g;
export const CELL_ROW_INDEX_REPLACER_REGEX = /\d+/;
export const COLUMN_IDENTIFIER_REGEX = /[A-Za-z]+/;
export const DATE_REGEX = /^(?=.*d)(?=.*m)(?=.*y).+$/;
export const PERCENTAGE_END_REGEX = /%$/;
export const ROW_IDENTIFIER_REGEX = /\d+/;
export const ALPHANUMERICAL_IDENTIFIER_REGEX = /[A-Za-z]/;
export const CSV_FORMULA_START_REGEX = /^[=+\-@\t\r]/;
export const NUMERIC_VALUE_REGEX = /^-?\d+(\.\d+)?$/;

//Error messages
export const TEMPLATE_NOT_FOUND = "Template Not Found";
export const OPTION_NOT_FOUND = "Option Not Found";
export const NO_ACTIVE_SHEET = "No active sheet found.";
//Deliverable Menu  constants
export const DELIVERABLE_NAME_UPDATED = "Deliverable name updated successfully";

//Create Template constants
export const SELECT_FILE_TO_UPLOAD = "Please select a file to upload";
export const ATTACH_FILE = "Please attach a .xlsx file";
export const CREATE_TEMPLATE = "Create Template";
export const CREATE_COA = "Create CoA";

//Default filter keys
export const DEFAULT_FILTER_KEYS: FilterKeyTypes = {
  Key1: "",
  Key2: "",
  Key3: "",
  Key4: "",
};

export const AG_GRID_HEADER_CHAR_WIDTH_PX = 8;
export const AG_GRID_HEADER_PADDING_PX = 80;
export const AG_GRID_COL_MIN_WIDTH_PX = 80;
export const AG_GRID_EMPTY_COL_WIDTH_PX = 52;

//Pagination footer constants
export const PAGINATION_FOOTER_CONSTANT = {
  FOOTER_ARIA_LABEL: "Pagination",
  PAGE_SIZE: 20,
  RANGE_TEXT: (start: number, end: number, total: number) => `${start} - ${end} of ${total}`,
  PREVIOUS_PAGE_ARIA_LABEL: "Previous page",
  NEXT_PAGE_ARIA_LABEL: "Next page",
};

//Global constants — used across 2+ features
export const GLOBAL_CONSTANT = {
  PROMISE_STATUS: {
    FULFILLED: "fulfilled",
    REJECTED: "rejected",
  } as const,
  TOAST_DEFAULT_ERROR_MESSAGE: "Something went wrong",
  EMPTY_VALUE_PLACEHOLDER: "—",
  GENERIC_ERROR_MESSAGE: "An error occurred",
  ACTIONS_ARIA_LABEL: "Actions",
  APPLY_TEXT: "Apply",
  DATE_FORMAT_MM_DD_YYYY: "MM/DD/YYYY",
  TABLE_PAGINATION_SHOW_TOTAL: (total: number, range: [number, number]) =>
    `${range[0]}-${range[1]} of ${total}`,
  ADMINISTRATOR_ROLE: "Administrator",
  CANCEL_TEXT: "Cancel",
  CREATE_TEXT: "Create",
  SAVE_TEXT: "Save",
  CLOSE_TEXT: "Close",
  BACK_BUTTON_LABEL: "Back",
  EDIT_TEXT: "Edit",
  DELETE_TEXT: "Delete",
  DEACTIVATE_TEXT: "Deactivate",
  REACTIVATE_TEXT: "Reactivate",
  RESET_TEXT: "Reset",
  LOCALE_EN_US: "en-US",
  IMPORT_BUTTON_LABEL: "Import",
  EXPORT_BUTTON_LABEL: "Export",
  ADDROW_TEXT: "Add Row",
  EDIT_ONLINE_TEXT: "Edit Online",
  ACTIVE_TABS_KEY: "activeTab",
  NAME_KEY: "templateName",
  MULTI_SHEET_KEY: "multiSheet",
  DELIVERABLE_NAME_LABEL: "Deliverable Name",
  FILTER_TAGS_LABEL: "Tags",
  FILTER_CREATED_BY_LABEL: "Created By",
  FILTER_CREATED_ON_LABEL: "Created On",
  GLOBAL_TEMPLATE_LABEL: "Global Template",
  DEFAULT_GUID: "00000000-0000-0000-0000-000000000000",
  TEMPLATE: "template",
  USER_ACCESS: "User Access",
  IMPORT_FAILED_MESSAGE: "Import failed",
  COA_NAME_LABEL: "CoA Name",
  CREATE_COA_BUTTON_LABEL: "Create CoA",
  CREATE_TEMPLATE_BUTTON_LABEL: "Create Template",
  NO_OF_DELIVERABLES_LABEL: "No. of Deliverables",
  TASK_POLLING_INTERVAL: 2000,
  ACCESS_TYPE: "Access Type",
  NO_PROJECTS_FOUND_PLACEHOLDER: "No data found...",
  USER_NAME_LABEL: "User Name",
  WRITE_ACCESS: "Write",
  ONLY_XLSX_FILES_ACCEPTED_MESSAGE: "Only .xlsx files are accepted",
  TEMPLATE_NAME_LABEL: "Template Name",
  UPLOADING_TEXT: "Uploading…",
  UPLOAD_FAILED_MESSAGE: "Upload failed",
  SAVING_TEXT: "Saving...",
  ACCOUNT_NAME_COLUMN_HEADER: "Account Name",
  CATEGORY_COLUMN_HEADER: "Category",
  COMPUTED_COLUMN_HEADER_TEXT: (name: string) => `∑ ${name}`,
  FORMULA_TOOLTIP_TEXT: (formula?: string | null) =>
    formula ? `Formula: ${formula}` : "Calculated",
  TAX_YEARS: ["FY2025", "FY2024", "FY2023", "FY2022"],
  PARTNERSHIP_HIDDEN_ROUTES: [ROUTE_CONSTANT.ENTITIES, "/engagements", "/calculations/structure"],
  // Header Tax Year pill is hidden on these route prefixes (segment-aware: /engagements and /engagements/:id).
  TAX_YEAR_HIDDEN_ROUTES: ["/engagements"],
  // The whole header strip is hidden on these route prefixes (segment-aware: a prefix and the pages under it).
  BANNER_HIDDEN_ROUTE_PREFIXES: ["/calculations/project", ROUTE_CONSTANT.ENTITIES, "/engagements"],
  // TC-PRJ-5.1: header pills use the Head office / Client terms (backend records stay Client / Entity).
  CLIENT_BANNER: {
    HEAD_OFFICE_LABEL: "HEAD OFFICE",
    HEAD_OFFICE_PLACEHOLDER: "Select head office...",
    HEAD_OFFICE_OVERVIEW_TITLE: "View head office overview",
    CLIENT_LABEL: "CLIENT",
    CLIENT_PLACEHOLDER: "Select client...",
  },
  SIDEBAR_HIDDEN_ROUTE_PATTERN: /^\/calculations\/project\/[^/]+\/workspace\/?$/,
  AG_GRID_ROW_BORDER_PARAMS: {
    rowBorder: { style: "solid", width: 1, color: "rgba(255, 255, 255, 0.3)" },
  },
  TASK_STATUS: {
    COMPLETED: "completed",
    IN_PROGRESS: "inprogress",
    FAILED: "failed",
    NO_TASK_FOUND: "No Task Found",
  },
  TASK_TYPE: {
    IMPORT: "import",
    RERUN: "RERUN",
  },
};

//AdminView screen constants
export const ADMIN_VIEW_CONSTANT = {
  IN_DEVELOPMENT_BADGE_LABEL: "In Development",
  ADMINISTRATION_TITLE: "Administration",
  ADMIN_DESCRIPTION: "User management, permissions, and system configuration",
};

//AuthenticateView / Login screen constants
export const AUTHENTICATE_VIEW_CONSTANT = {
  MSAL_LOGIN_SCOPES: ["User.Read", "openid", "profile"],
  TOKEN_REFRESH_INTERVAL_MS: (3 * 60 * 60 - 60) * 1000, // 2h 59m
  SSO_SIGNIN_FAILED_TITLE: "Sign-in failed",
  SSO_SIGNIN_FAILED_DESCRIPTION:
    "Could not sign in with Microsoft. Please try again.",
  SSO_TOKEN_REFRESH_FAILED_MESSAGE: "Your session could not be renewed. Please sign in again.",
  SSO_SESSION_REFRESH_FAILED_MESSAGE: "We couldn't refresh your permissions. Please try again.",
  LOGIN_SUCCESS_MESSAGE: "Login Successful",
  INVALID_CREDENTIALS: "Invalid credentials",
  INVALID_CREDENTIALS_DESCRIPTION: "Invalid username or password",
  LOGO_ALT_TEXT: "Propel",
  APP_VERSION_LABEL: "v0.1",
  SIGN_IN_HEADING: "Sign in",
  SIGN_IN_SUBTEXT: "Welcome back — enter your credentials to continue.",
  EMAIL_LABEL: "Email Address",
  EMAIL_PLACEHOLDER: "you@firm.com",
  PASSWORD_LABEL: "Password",
  PASSWORD_FIELD_PLACEHOLDER: "••••••••",
  USERNAME_REQUIRED_TEXT: "UserName is required",
  PASSWORD_REQUIRED_TEXT: "Password is required",
  FORGOT_PASSWORD_TEXT: "Forgot Password?",
  SIGN_IN_BUTTON_TEXT: "Sign in",
  SSO_DIVIDER_TEXT: "— or —",
  SSO_BUTTON_TEXT: "Sign in with Microsoft",
  BRAND_TITLE: "Calc Engine",
  BRAND_SUBTITLE: "Building Calculation Infrastructure for Growing Firms",
};

//Sidebar constants
export const SIDEBAR_CONSTANT = {
  NAV_COLLAPSED_STORAGE_KEY: "main-nav-collapsed",
  COLLAPSED_MENU_WIDTH_PX: 48,
  TOGGLE_SHORTCUT_KEY: "\\",
  EXPAND_SHORTCUT_KEY: "Escape",
  LOGO_EXPANDED_SRC: "/propel-logo-white.png",
  LOGO_COLLAPSED_SRC: "/favicon.ico",
  CALCULATIONS_LABEL: "Calculations",
  PROJECT_LABEL: "Project",
  TIER_FLOW_LABEL: "Tier Flow",
  STRUCTURE_LABEL: "Structure",
  HEAD_OFFICE_GROUP_LABEL: "Head office",
  HEAD_OFFICES_ITEM_LABEL: "Head office",
  ENGAGEMENTS_LABEL: "Engagements",
  CLIENTS_ITEM_LABEL: "Clients",
  CONFIGURATIONS_LABEL: "Configurations",
  CALCULATION_PROFILE_LABEL: "Calculation Profile",
  TEMPLATE_LIBRARY_LABEL: "Template Library",
  STANDARD_CALCULATIONS_LABEL: "Standard Calculations",
  GENERATE_TAX_FORM_LABEL: "Generate Tax Form",
  SETTINGS_LABEL: "Settings",
  USER_MANAGEMENT_LABEL: "User Management",
  ROLES_LABEL: "Roles",
  TIERING_CONFIGURATION_LABEL: "Tiering Configuration",
  EXTERNAL_K1_MAPPING_LABEL: "External K1 Mapping",
  LOG_OUT_LABEL: "Log out",
  EXPAND_MENU_TITLE: "Expand menu (\\)",
  COLLAPSE_MENU_TITLE: "Collapse menu (\\)",
  EXPAND_MENU_LABEL: "Expand menu",
  COLLAPSE_MENU_LABEL: "Collapse menu",
  EXPAND_GLYPH_TEXT: "›",
  COLLAPSE_GLYPH_TEXT: "‹",
};

export const LIST_GRID_THEME_PARAMS = {
  backgroundColor: "transparent",
  chromeBackgroundColor: "transparent",
  foregroundColor: "var(--ce-text-primary)",
  headerBackgroundColor: "rgba(var(--ce-white-rgb), 0.04)",
  headerTextColor: "var(--colorGray)",
  headerFontSize: "0.6875rem",
  headerFontWeight: 600,
  fontSize: "0.8125rem",
  fontFamily: "inherit",
  borderColor: "var(--ce-border)",
  rowBorder: { style: "solid", width: 1, color: "var(--ce-table-border)" },
  headerRowBorder: { style: "solid", width: 1, color: "var(--ce-border)" },
  wrapperBorder: false,
  rowHoverColor: "var(--ce-table-row-hover)",
  selectedRowBackgroundColor: "var(--ce-accent-dim)",
  menuBackgroundColor: "rgba(var(--ce-navy-mid-rgb), 0.97)",
  panelBackgroundColor: "rgba(var(--ce-navy-mid-rgb), 0.97)",
  sideBarBackgroundColor: "rgba(var(--ce-navy-mid-rgb), 0.97)",
} as const;

//Core constants
export const CORE_CONSTANT = {
  ACTIVITIES_COMING_SOON_TITLE: "Activities",
  PARTNERSHIP_CONTEXT: {
    DEFAULT_TAX_YEAR: `FY${new Date().getFullYear()}`,
  },
};

//CoaView screen constants
export const COA_VIEW_CONSTANT = {
  SEARCH_PLACEHOLDER: "Search CoA",
};

//DeliverableView screen constants
export const DELIVERABLE_VIEW_CONSTANT = {
  SEARCH_PLACEHOLDER: "Search Deliverables",
};

//FinancialsView constants
export const FINANCIALS_VIEW_CONSTANT = {
  COA_MAPPING_COMING_SOON_TITLE: "CoA Mapping",
  SAVING_TEXT: "Saving…",
  EMPTY_VALUE_PLACEHOLDER: "—",
  IMPORT_COMING_SOON_MESSAGE: "Import coming soon",
  EDIT_COMING_SOON_MESSAGE: "Edit coming soon",
  EXPORT_COMING_SOON_MESSAGE: "Export coming soon",

  FIXED_ASSETS: {
    PAGE_TITLE: "Asset Master",
    PAGE_SUBTITLE: (partnershipName: string) =>
      `${partnershipName} · Complete register of long-term assets`,
    ADD_ASSET_BUTTON_LABEL: "Add Asset",
    SELECT_PARTNERSHIP_TEXT: "Select a client and partnership to view fixed assets",
    TOTAL_ORIGINAL_COST_LABEL: "Total Original Cost",
    ACCUMULATED_DEPRECIATION_LABEL: "Accumulated Depreciation",
    ADJUSTED_TAX_BASIS_LABEL: "Adjusted Tax Basis",
    ASSETS_IN_REGISTER_LABEL: "Assets in Register",
    ACTIVE_COUNT_LABEL: "active",
    DISPOSED_COUNT_LABEL: "disposed",
    SEARCH_PLACEHOLDER: "Search by asset name or class...",
    ASSET_NAME_COLUMN_HEADER: "Asset Name",
    ASSET_CLASS_COLUMN_HEADER: "Asset Class",
    DEPRECIATION_METHOD_COLUMN_HEADER: "Depr. Method",
    IN_SERVICE_DATE_COLUMN_HEADER: "In-Service Date",
    ORIGINAL_COST_COLUMN_HEADER: "Original Cost",
    ACCUMULATED_DEPRECIATION_COLUMN_HEADER: "Accum. Depr.",
    TAX_BASIS_COLUMN_HEADER: "Tax Basis",
    NO_SEARCH_MATCHES_TEXT: "No assets match your search",
    NO_ASSETS_TEXT:
      "No fixed assets recorded for this partnership. Add an asset or use Import.",
    TABLE_FOOTER_TEXT: (shown: number, total: number) =>
      `Showing ${shown} of ${total} asset${total !== 1 ? "s" : ""}`,
    DISPOSED_BADGE_LABEL: "Disposed",
    FAILED_TO_LOAD_MESSAGE: "Failed to load fixed assets",
    DEFAULT_ASSET_CLASS: "Machinery",
    DEFAULT_DEPRECIATION_METHOD: "MACRS 7-yr",
    DEPRECIATION_METHODS: [
      "MACRS 7-yr",
      "MACRS 39-yr",
      "MACRS 5-yr",
      "Bonus 100%",
      "Straight-line",
      "Section 179",
      "Non-depreciable",
    ],
    FORM: {
      ADD_MODAL_TITLE: "Add Fixed Asset",
      EDIT_MODAL_TITLE: "Edit Fixed Asset",
      ASSET_NAME_LABEL: "Asset Name",
      ASSET_CLASS_LABEL: "Asset Class",
      DEPRECIATION_METHOD_LABEL: "Depreciation Method",
      IN_SERVICE_DATE_LABEL: "In-Service Date",
      ORIGINAL_COST_LABEL: "Original Cost",
      TAGS_LABEL: "Tags",
      ASSET_NAME_PLACEHOLDER: "e.g. Manufacturing Line A",
      SELECT_DATE_PLACEHOLDER: "Select date",
      ORIGINAL_COST_PLACEHOLDER: "0",
      TAGS_PLACEHOLDER: "e.g. 704(c) asset",
      ASSET_NAME_REQUIRED_MESSAGE: "Asset name is required",
      IN_SERVICE_DATE_REQUIRED_MESSAGE: "In-service date is required",
      SAVE_BUTTON_LABEL: "Save Asset",
      UPDATE_BUTTON_LABEL: "Update Asset",
      ASSET_SAVED_MESSAGE: (mode: "create" | "edit") =>
        `Asset ${mode === "create" ? "created" : "updated"}`,
      FAILED_TO_SAVE_MESSAGE: (mode: "create" | "edit") =>
        `Failed to ${mode === "create" ? "create" : "update"} asset`,
    },
  },

  M1_DIFFERENCES: {
    PAGE_TITLE: "M-1 Differences",
    PAGE_SUBTITLE: (partnershipName: string) =>
      `${partnershipName} · Book vs. tax differences by category`,
    ADD_BALANCE_BUTTON_LABEL: "Add M-1 Balance",
    SELECT_PARTNERSHIP_TEXT: "Select a client and partnership to view M-1 differences",
    TOTAL_BOOK_AMOUNT_LABEL: "Total Cumulative Book Amount",
    TOTAL_TAX_AMOUNT_LABEL: "Total Cumulative Tax Amount",
    NET_DIFFERENCE_LABEL: "Net Cumulative Difference",
    SEARCH_PLACEHOLDER: "Search by category name...",
    STATUS_FILTER_OPTIONS: ["All", "Active"],
    CATEGORY_COLUMN_HEADER: "M-1 Category",
    BOOK_AMOUNT_COLUMN_HEADER: "Book Amount",
    TAX_AMOUNT_COLUMN_HEADER: "Tax Amount",
    CUMULATIVE_DIFFERENCE_COLUMN_HEADER: "Cumulative Difference",
    NO_SEARCH_MATCHES_TEXT: "No categories match your search",
    NO_CATEGORIES_TEXT:
      "No M-1 differences recorded for this partnership. Add categories manually or use Import.",
    TABLE_FOOTER_TEXT: (shown: number, total: number) =>
      `Showing ${shown} of ${total} categor${total !== 1 ? "ies" : "y"}`,
    DELETE_CONFIRM_TITLE: "Delete M-1 balance?",
    DELETE_CONFIRM_CONTENT: (categoryName: string) =>
      `This will permanently remove "${categoryName}".`,
    DELETE_PENDING_MESSAGE: "Delete not yet available — backend pending",
    FAILED_TO_LOAD_MESSAGE: "Failed to load M-1 differences",
    DEFAULT_CATEGORY_TYPE: "Depreciation",
    CATEGORY_TYPES: ["Depreciation", "Accrual", "Prepaid", "Reserve", "Inventory", "Other"],
    FORM: {
      ADD_MODAL_TITLE: "Add M-1 Balance",
      EDIT_MODAL_TITLE: "Edit M-1 Balance",
      CATEGORY_NAME_LABEL: "Category Name",
      CATEGORY_TYPE_LABEL: "Category Type",
      BOOK_AMOUNT_LABEL: "Book Amount",
      TAX_AMOUNT_LABEL: "Tax Amount",
      CATEGORY_NAME_PLACEHOLDER: "e.g. Depreciation difference",
      AMOUNT_PLACEHOLDER: "0",
      AUTO_DIFFERENCE_LABEL: "Auto-computed difference",
      CATEGORY_NAME_REQUIRED_MESSAGE: "Category name is required",
      SAVE_BUTTON_LABEL: "Save M-1 Balance",
      UPDATE_BUTTON_LABEL: "Update",
      BALANCE_SAVED_MESSAGE: (mode: "create" | "edit") =>
        `M-1 balance ${mode === "create" ? "created" : "updated"}`,
      FAILED_TO_SAVE_MESSAGE: (mode: "create" | "edit") =>
        `Failed to ${mode === "create" ? "create" : "update"} M-1 balance`,
    },
  },

  PRIOR_YEAR_CAPITAL: {
    PAGE_TITLE: "Prior Year Capital",
    PAGE_SUBTITLE: (partnershipName: string) =>
      `${partnershipName} · Partner capital account balances`,
    SELECT_PARTNERSHIP_TEXT: "Select a client and partnership to view capital accounts",
    FAILED_TO_LOAD_MESSAGE: "Failed to load capital account data",
    BASIS_LABELS: {
      tax: "Tax Basis",
      gaap: "GAAP Basis",
      "704b": "704(b) Basis",
    },
    BEGINNING_CAPITAL_LABEL: "Beginning Capital",
    CONTRIBUTIONS_LABEL: "Contributions",
    DISTRIBUTIONS_LABEL: "Distributions",
    INCOME_LOSS_LABEL: "Income / Loss",
    ENDING_CAPITAL_LABEL: "Ending Capital",
    PARTNER_COLUMN_HEADER: "Partner",
    TYPE_COLUMN_HEADER: "Type",
    OWNERSHIP_COLUMN_HEADER: "Ownership %",
    NO_DATA_TEXT: "No capital account data found for this partnership and tax year.",
    TOTAL_LABEL: "Total",
    TOTAL_OWNERSHIP_TEXT: "100.00%",
    TABLE_FOOTER_TEXT: (partnerCount: number, basisLabel?: string) =>
      `${partnerCount} partner${partnerCount !== 1 ? "s" : ""} · ${basisLabel ?? ""}`,
  },
};

//SettingsView constants
export const SETTINGS_VIEW_CONSTANT = {
  ROLES: {
    PAGE_TITLE: "Roles",
    CREATE_ROLE_BUTTON_LABEL: "Create Role",
    FAILED_TO_LOAD_ROLES_MESSAGE: "Failed to load roles",
    FAILED_TO_LOAD_PERMISSION_CATALOG_MESSAGE:
      "Failed to load the permission catalog",
    FAILED_TO_DELETE_ROLE_MESSAGE: "Failed to delete role",
    ROLE_TABLE_HEADER: "Role",
    PERMISSIONS_TABLE_HEADER: "Permissions",
    PERMISSIONS_COLUMN_WIDTH_PX: 200,
    ACTIONS_COLUMN_WIDTH_PX: 60,
    NO_ROLES_FOUND_MESSAGE: "No roles found",
    SYSTEM_BADGE_LABEL: "System",
    DELETE_ROLE_ARIA_LABEL: "Delete role",
    PERMISSION_LABEL: "permission",
    DELETE_ROLE_CONFIRM_TITLE: (roleName: string) => `Delete ${roleName}?`,
    ROLE_DELETED_MESSAGE: (roleName: string) => `${roleName} deleted`,

    // CreateRoleModal
    CREATE_ROLE_MODAL_TITLE: "Create Role",
    CREATE_ROLE_MODAL_WIDTH_PX: 560,
    SELECT_PERMISSION_REQUIRED_MESSAGE:
      "Select at least one permission for this role",
    ROLE_CREATED_MESSAGE: "Role created",
    FAILED_TO_CREATE_ROLE_MESSAGE: "Failed to create role",
    ROLE_NAME_LABEL: "Role Name",
    ROLE_NAME_PLACEHOLDER: "e.g. Reviewer — East Region",
    ROLE_NAME_REQUIRED_MESSAGE: "Role name is required",
    DESCRIPTION_LABEL: "Description",
    DESCRIPTION_PLACEHOLDER: "What this role is for",
    DESCRIPTION_REQUIRED_MESSAGE: "Description is required",

    // RoleDetailModal
    ROLE_DETAIL_MODAL_WIDTH_PX: 640,
    FAILED_TO_LOAD_ROLE_DETAILS_MESSAGE: "Failed to load role details",
    ROLE_UPDATED_MESSAGE: "Role updated",
    FAILED_TO_SAVE_ROLE_MESSAGE: "Failed to save role",
    SYSTEM_ROLE_TAG_LABEL: "System Role",
    PERMISSIONS_LABEL: "Permissions",

    // PermissionGroupsPanel
    OTHER_PERMISSIONS_GROUP_LABEL: "Other Permissions",
    CLIENT_MANAGEMENT_GROUP_LABEL: "Client Management",
    TEMPLATE_LIBRARY_GROUP_LABEL: "Template Library",
    CALC_PROFILE_STANDARD_CALCULATIONS_GROUP_LABEL:
      "Calc Profile & Standard Calculations",
    PROJECT_WORKSPACE_GROUP_LABEL: "Project Workspace",
    UPLOAD_RUN_GROUP_LABEL: "Upload & Run",
    WORK_PAPERS_GROUP_LABEL: "Work Papers",
    USER_MANAGEMENT_GROUP_LABEL: "User Management",
    SETTINGS_COA_GROUP_LABEL: "Settings & CoA",
    COLLAPSE_GROUP_ARIA_LABEL: "Collapse group",
    EXPAND_GROUP_ARIA_LABEL: "Expand group",
  },

  USER_MANAGEMENT: {
    PAGE_TITLE: "User Management",

    // Tabs
    TAB_ALL_USERS_LABEL: "All Users",
    TAB_GROUPS_LABEL: "Groups",

    // Groups list
    CREATE_GROUP_BUTTON_LABEL: "Create group",
    SEARCH_GROUPS_PLACEHOLDER: "Search by group name",
    GROUP_NAME_TABLE_HEADER: "Group name",
    CREATED_BY_TABLE_HEADER: "Created by",
    HEAD_OFFICES_TABLE_HEADER: "Head offices",
    USERS_TABLE_HEADER: "Users",
    LAST_MODIFIED_TABLE_HEADER: "Last modified",
    NO_GROUPS_FOUND_MESSAGE: "No groups yet",
    NO_GROUPS_MATCH_SEARCH_MESSAGE: "No groups match your search",
    FAILED_TO_LOAD_GROUPS_MESSAGE: "Failed to load groups",
    INACTIVE_HEAD_OFFICES_BADGE: (count: number) => `${count} inactive`,
    LAST_MODIFIED_BY_TEXT: (name: string) => `by ${name}`,
    VIEW_GROUP_LABEL: "View",
    EDIT_GROUP_LABEL: "Edit",
    DELETE_GROUP_LABEL: "Delete",
    DELETE_GROUP_CONFIRM_TITLE: (name: string) => `Delete the group "${name}"?`,
    DELETE_GROUP_CONFIRM_TEXT: "Its users keep their roles on head offices that other groups still map them to.",
    DELETE_GROUP_SUCCESS_MESSAGE: "Group deleted",
    DELETE_GROUP_FAILED_MESSAGE: "Failed to delete group",
    DELETE_GROUP_BLOCKED_MESSAGE: (names: string) => `This group cannot be deleted while these head offices have projects that are not archived: ${names}`,

    // Group form
    CREATE_GROUP_TITLE: "Create group",
    EDIT_GROUP_TITLE: "Edit group",
    VIEW_GROUP_TITLE: "View group",
    BACK_TO_GROUPS_LABEL: "Back to groups",
    GROUP_NAME_LABEL: "Group name",
    GROUP_NAME_PLACEHOLDER: "Enter a group name",
    ADD_HEAD_OFFICE_BUTTON_LABEL: "Add head office",
    ADD_HEAD_OFFICE_PLACEHOLDER: "Select head offices",
    ADD_HEAD_OFFICES_CONFIRM_LABEL: "Add",
    ADD_USER_PLACEHOLDER: "Add a user",
    NO_HEAD_OFFICES_YET_TEXT: "No head offices yet. Add a head office to choose its users.",
    NO_USERS_ON_HEAD_OFFICE_TEXT: "No users yet.",
    REMOVE_HEAD_OFFICE_LABEL: (name: string) => `Remove ${name} from the group`,
    REMOVE_USER_LABEL: (userName: string, headOfficeName: string) => `Remove ${userName} from ${headOfficeName}`,
    INACTIVE_HEAD_OFFICE_TAG: "Inactive",
    DEFAULT_ROLE_LABEL: "Default role",
    HEAD_OFFICE_ROLE_LABEL: "Head office role",
    HEAD_OFFICE_ROLE_ARIA_LABEL: (userName: string, headOfficeName: string) => `Head office role for ${userName} on ${headOfficeName}`,
    SELECT_HEAD_OFFICE_ROLE_PLACEHOLDER: "Select a role",
    NO_DEFAULT_ROLE_TEXT: "No role",
    OVERRIDDEN_TAG: "Overridden",
    LOCKED_ROLE_TOOLTIP: (groupName: string, adminName: string) =>
      `This user is already mapped to the head office in the group "${groupName}". To change the role, please contact the admin ${adminName}.`,
    FIXED_ROLE_TOOLTIP: "This user holds their own role on every head office.",
    SAVE_GROUP_BUTTON_LABEL: "Save",
    CANCEL_BUTTON_LABEL: "Cancel",
    CLOSE_BUTTON_LABEL: "Close",
    DISCARD_CHANGES_TITLE: "Discard your changes to this group?",
    DISCARD_CHANGES_OK_LABEL: "Discard",
    KEEP_EDITING_LABEL: "Keep editing",
    GROUP_SAVED_SUCCESS_MESSAGE: "Group saved",
    GROUP_SAVE_FAILED_MESSAGE: "Failed to save group",
    GROUP_FIX_ERRORS_MESSAGE: "Fix the highlighted fields and save again.",
    FAILED_TO_LOAD_GROUP_MESSAGE: "Failed to load the group",
    FAILED_TO_LOAD_PICKERS_MESSAGE: "Failed to load head offices or users",
    GROUP_NAME_REQUIRED_TEXT: "Enter a group name.",
    GROUP_NAME_TOO_LONG_TEXT: "Group name must be 100 characters or fewer.",
    GROUP_NEEDS_HEAD_OFFICE_TEXT: "Add at least one head office to the group.",
    HEAD_OFFICE_NEEDS_USER_TEXT: (headOfficeName: string) => `Add at least one user to ${headOfficeName}.`,
    MEMBER_ROLE_REQUIRED_TEXT: (userName: string, headOfficeName: string) => `Select a role for ${userName} on ${headOfficeName}.`,
    GROUP_SUMMARY_TEXT: (headOffices: number, users: number) => `${headOffices} head offices, ${users} users`,
    CREATED_BY_TEXT: (name: string) => `Created by ${name}`,

    // All Users list
    INVITE_USER_BUTTON_LABEL: "Invite User",
    EDIT_BUTTON_LABEL: "Edit",
    FAILED_TO_LOAD_USERS_MESSAGE: "Failed to load users",
    FAILED_TO_LOAD_ROLES_MESSAGE: "Failed to load roles",
    SEARCH_USERS_PLACEHOLDER: "Search by name or email",
    NO_USERS_MATCH_SEARCH_MESSAGE: "No users match your search",
    NO_USERS_FOUND_MESSAGE: "No users found",
    USER_TABLE_HEADER: "User",
    ROLE_TABLE_HEADER: "Application role",
    GROUPS_TABLE_HEADER: "Groups",
    STATUS_TABLE_HEADER: "Status",
    LAST_LOGIN_TABLE_HEADER: "Last login",
    ROLE_COLUMN_WIDTH_PX: 200,
    GROUPS_COLUMN_WIDTH_PX: 220,
    STATUS_COLUMN_WIDTH_PX: 110,
    LAST_LOGIN_COLUMN_WIDTH_PX: 140,
    ACTIONS_COLUMN_WIDTH_PX: 48,
    NO_GROUPS_LABEL: "No groups",
    NO_ROLE_LABEL: "No role",
    NO_LAST_LOGIN_TEXT: "\u2014",
    ACTIVE_STATUS_LABEL: "Active",
    INACTIVE_STATUS_LABEL: "Inactive",
    ACTIONS_ARIA_LABEL: "Actions",

    // Edit mode (application role dropdowns)
    ROLE_SELECT_ARIA_LABEL: (userName: string) => `Application role for ${userName}`,
    SELECT_ROLE_PLACEHOLDER: "Select a role",
    ROLES_SAVED_SUCCESS_MESSAGE: "Roles updated",
    ROLES_SAVE_FAILED_MESSAGE: (names: string) => `Could not update the role for: ${names}`,

    // Row actions
    DEACTIVATE_CONFIRM_TITLE: "Deactivate this user?",
    DEACTIVATE_SUCCESS_MESSAGE: "User deactivated",
    DEACTIVATE_FAILED_MESSAGE: "Failed to deactivate user",
    REACTIVATE_TEXT: "Reactivate",

    // ReactivateUserModal
    REACTIVATE_MODAL_TITLE: "Reactivate User",
    REACTIVATE_MODAL_WIDTH_PX: 420,
    REACTIVATE_ROLE_HELP_TEXT: "Choose the application role the user comes back with.",
    REACTIVATE_SUCCESS_MESSAGE: "User reactivated",
    REACTIVATE_FAILED_MESSAGE: "Failed to reactivate user",

    // InviteUserModal
    INVITE_USER_MODAL_TITLE: "Invite User",
    INVITE_USER_MODAL_WIDTH_PX: 480,
    SEND_INVITE_BUTTON_LABEL: "Send Invite",
    FIRST_NAME_LABEL: "First Name",
    LAST_NAME_LABEL: "Last Name",
    EMAIL_LABEL: "Email",
    ROLE_LABEL: "Role",
    FIRST_NAME_PLACEHOLDER: "Jane",
    LAST_NAME_PLACEHOLDER: "Smith",
    EMAIL_PLACEHOLDER: "jane.smith@firm.com",
    FIRST_NAME_REQUIRED_TEXT: "First Name is required",
    LAST_NAME_REQUIRED_TEXT: "Last Name is required",
    EMAIL_REQUIRED_TEXT: "Email is required",
    VALID_EMAIL_REQUIRED_TEXT: "Enter a valid email",
    ROLE_REQUIRED_TEXT: "Role is required",
    INVITE_VALIDATION_MESSAGE: "Enter a name, a valid email and a role.",
    INVITATION_FAILED_MESSAGE: "Failed to send invitation",
    USER_ALREADY_EXISTS_MESSAGE: "A user with this email already exists.",
    TEMP_PASSWORD_LABEL: "Temporary Password",
    TEMP_PASSWORD_INSTRUCTION:
      "Share this temporary password with the user. They will be prompted to change it on first login.",
  },

  TIERING_CONFIGURATION: {
    PAGE_TITLE: "Tiering Configuration",
    OVERRIDES_TAB_LABEL: "Line Map Overrides",
    EFFECTIVE_TAB_LABEL: "Effective Mapping",
    ADD_OVERRIDE_BUTTON_LABEL: "Add Override",
    FAILED_TO_LOAD_MESSAGE: "Failed to load the line map",
    FAILED_TO_LOAD_EFFECTIVE_MESSAGE: "Failed to load the effective mapping",
    NO_ROWS_FOUND_MESSAGE: "No override rows found",
    SCOPE_FILTER_LABEL: "Scope",
    SCOPE_FILTER_ALL_OPTION: "All Scopes",
    CLIENT_FILTER_PLACEHOLDER: "Filter by client...",
    SCOPE_TABLE_HEADER: "Scope",
    CLIENT_TABLE_HEADER: "Client",
    SOURCE_LINE_TABLE_HEADER: "Source Line",
    TARGET_LINE_TABLE_HEADER: "Target Line",
    CODE_TABLE_HEADER: "Code",
    TAX_YEAR_TABLE_HEADER: "Tax Year",
    ACTIONS_TABLE_HEADER: "",
    ALL_YEARS_LABEL: "All years",
    WHOLE_BOX_LABEL: "Whole box",
    DELETE_ARIA_LABEL: "Deactivate row",
    DEACTIVATE_CONFIRM_TITLE: "Deactivate this override?",
    DEACTIVATE_SUCCESS_MESSAGE: "Override deactivated",
    DEACTIVATE_FAILED_MESSAGE: "Failed to deactivate override",
    SOURCE_LINE_TABLE_HEADER_EFFECTIVE: "K-1 Line",
    TARGET_LINE_TABLE_HEADER_EFFECTIVE: "Resolves To",
    OVERRIDE_BADGE_LABEL: "Override",
    IDENTITY_BADGE_LABEL: "Identity",
    MATCHED_SCOPE_LABEL: (scope: string) => `at ${scope} scope`,

    // AddEditLineMapModal
    ADD_MODAL_TITLE: "Add Line Map Override",
    EDIT_MODAL_TITLE: "Edit Line Map Override",
    MODAL_WIDTH_PX: 560,
    SCOPE_LABEL: "Scope",
    SCOPE_REQUIRED_MESSAGE: "Scope is required",
    CLIENT_LABEL: "Client",
    CLIENT_REQUIRED_MESSAGE: "Client is required for client scope",
    SELECT_CLIENT_PLACEHOLDER: "Select a client",
    SOURCE_LINE_LABEL: "Source Line",
    SOURCE_LINE_REQUIRED_MESSAGE: "Source line is required",
    SELECT_LINE_PLACEHOLDER: "Select a K-1 line",
    SOURCE_CODE_LABEL: "Source Code (optional)",
    SOURCE_CODE_PLACEHOLDER: "e.g. W — leave blank for the whole box",
    TARGET_LINE_LABEL: "Target Line",
    TARGET_LINE_REQUIRED_MESSAGE: "Target line is required",
    TARGET_CODE_LABEL: "Target Code (optional)",
    TAX_YEAR_LABEL: "Tax Year (optional)",
    TAX_YEAR_PLACEHOLDER: "Leave blank to apply to all years",
    SAME_LINE_WARNING_TITLE: "Source and target are the same line",
    SAME_LINE_WARNING_DESCRIPTION:
      "This pins the line to itself instead of leaving no row (identity is already the default). Continue anyway?",
    SAVE_BUTTON_LABEL: "Save",
    OVERRIDE_CREATED_MESSAGE: "Override created",
    OVERRIDE_UPDATED_MESSAGE: "Override updated",
    FAILED_TO_LOAD_LINES_MESSAGE: "Failed to load K-1 lines",
    FAILED_TO_SAVE_MESSAGE: "Failed to save override",
    DUPLICATE_MESSAGE: "A row already exists for this scope, client, line, code and year",
  },

  TAX_FORM_LINES: {
    PAGE_TITLE: "Tax Form Lines",
    FORM_FILTER_LABEL: "Form",
    SELECT_FORM_PLACEHOLDER: "Select a form",
    PART_FILTER_PLACEHOLDER: "Part (e.g. II)",
    SECTION_FILTER_PLACEHOLDER: "Section (e.g. 1)",
    SUB_ROW_FILTER_LABEL: "Sub-row",
    COLUMN_KEY_FILTER_LABEL: "Column",
    NEEDS_REVIEW_FILTER_LABEL: "Needs Review",
    MAPPED_FILTER_LABEL: "Mapped",
    FILTER_ALL_OPTION: "All",
    FILTER_YES_OPTION: "Yes",
    FILTER_NO_OPTION: "No",
    LINE_TABLE_HEADER: "Line",
    DESCRIPTION_TABLE_HEADER: "Description",
    SECTION_TABLE_HEADER: "Section",
    SUB_SECTION_TABLE_HEADER: "Sub-Section",
    SORT_TABLE_HEADER: "Sort",
    MAPPING_TABLE_HEADER: "Mapping",
    NEEDS_REVIEW_TABLE_HEADER: "Needs Review",
    UNMAPPED_LABEL: "Unmapped",
    NO_ROWS_FOUND_MESSAGE: "No lines found for this form and filter set",
    FAILED_TO_LOAD_FORMS_MESSAGE: "Failed to load tax forms",
    FAILED_TO_LOAD_LINES_MESSAGE: "Failed to load tax form lines",
    NO_ACCESS_TITLE: "You don't have permission to view Tax Form Lines",
    NO_ACCESS_DESCRIPTION:
      "This screen requires the Tax Form Admin permission. Contact an administrator if you believe you should have access.",
    GENERIC_LOCK_MESSAGE: "This form is currently locked for editing by another admin.",
    LOCK_MESSAGE: (until: string) => `This form is being edited by another admin until ${until}.`,

    // EditTaxFormLineModal
    EDIT_MODAL_TITLE: "Edit Line Mapping",
    MODAL_WIDTH_PX: 560,
    MAPPING_TYPE_LABEL: "Mapping Type",
    MAPPING_TYPE_PLACEHOLDER: "Unmapped",
    MAPPING_REF_LABEL: "Mapping Reference (JSON, optional)",
    MAPPING_REF_PLACEHOLDER: 'e.g. {"lineCodes":["K1_L11"]}',
    OPERATION_TYPE_LABEL: "Operation Type",
    OPERATION_TYPE_REQUIRED_MESSAGE: "Operation type is required for a Calculated line",
    OPERATION_TYPE_PLACEHOLDER: "e.g. sum",
    LINE_ID_LIST_LABEL: "Referenced Line IDs (JSON array, optional)",
    LINE_ID_LIST_PLACEHOLDER: '["<line-id-1>", "<line-id-2>"]',
    LINE_ID_LIST_INVALID_MESSAGE: "Must be a JSON array of line ids, e.g. [\"<line-id-1>\"]",
    EXPECTED_VALUE_LABEL: "Expected Value (optional)",
    EXPECTED_VALUE_PLACEHOLDER: "e.g. /1 — for a FixedCheckbox line",
    REVERSE_SIGN_LABEL: "Reverse Sign",
    NEEDS_REVIEW_LABEL: "Needs Review",
    SAVE_BUTTON_LABEL: "Save",
    LINE_UPDATED_MESSAGE: "Line mapping updated",
    FAILED_TO_SAVE_MESSAGE: "Failed to save the line mapping",
    LINE_NOT_FOUND_MESSAGE: "This line no longer exists",

    // Import (K-2/K-3 only)
    IMPORT_BUTTON_LABEL: "Import K-2/K-3 Lines",
    IMPORT_MODAL_TITLE: "Import K-2/K-3 Lines",
    FORM_CODE_LABEL: "Form",
    FORM_YEAR_LABEL: "Form Year",
    FILE_LABEL: "Line Import Excel File",
    SELECT_FILE_BUTTON_LABEL: "Select File",
    PREVIEW_BUTTON_LABEL: "Preview",
    COMMIT_BUTTON_LABEL: "Commit Import",
    IMPORT_PREVIEW_SUCCESS_MESSAGE: "Preview complete — review the results below before committing",
    IMPORT_COMMIT_SUCCESS_MESSAGE: "Import committed",
    IMPORT_FAILED_MESSAGE: "Import failed",
    FILE_REQUIRED_MESSAGE: "Select a file first",
    TOTAL_ROWS_LABEL: "Rows in file",
    NEW_LABEL: "New",
    CHANGED_LABEL: "Changed",
    UNCHANGED_LABEL: "Unchanged",
    ERRORS_LABEL: "Errors",
    ROW_TABLE_HEADER: "Row",
    NAME_TABLE_HEADER: "Name",
    REASON_TABLE_HEADER: "Reason",

    ACTIONS_TABLE_HEADER: "",
    EDIT_BUTTON_LABEL: "Edit",
    EDIT_BUTTON_ARIA_LABEL: "Edit line mapping",
    PDF_NOT_FOUND_MESSAGE: "No PDF template is available for this form",
    PDF_ACCESS_ERROR_MESSAGE: "Couldn't load this form's preview — check your access and try again",
    PDF_PAGE_OF: (page: number, total: number) => `Page ${page} of ${total}`,
    FIELD_NOT_IN_VIEW_MESSAGE: "That line is not in the current page or filter",
    FIELD_LINE_LOOKUP_FAILED_MESSAGE: "Failed to find that line in the form",

    LOCK_FORBIDDEN_MESSAGE: "You don't have permission to edit this form.",
    LOCK_FORM_NOT_FOUND_MESSAGE: "This form no longer exists or isn't visible to you.",
    LOCK_FAILED_MESSAGE: "Couldn't acquire the edit lock for this form. Try again.",
    LOCK_LOST_MESSAGE: "Your edit lock on this form was lost. Re-enter Edit mode to continue.",
    EDIT_MODE_LABEL: "Edit mode",
    EDIT_MODE_ON_NOTICE: "Edit mode: you hold the edit lock on this form. Leave Edit mode to release it.",
    EDIT_MODE_VIEW_ONLY_NOTICE: (detail: string) => `View only — ${detail}`,
    UPLOAD_BUTTON_LABEL: "Upload Form",
    UPLOAD_MODAL_TITLE: "Upload a new form",
    UPLOAD_SUBMIT_LABEL: "Upload",
    UPLOAD_NAME_LABEL: "Name",
    UPLOAD_NAME_REQUIRED_MESSAGE: "Enter a form name",
    UPLOAD_CLASSIFICATION_LABEL: "Classification",
    UPLOAD_CLASSIFICATION_HELP: "Letters, digits, _ and - only. K1, K2 and K3 are reserved.",
    UPLOAD_CLASSIFICATION_REQUIRED_MESSAGE: "Enter a classification",
    UPLOAD_CLASSIFICATION_FORMAT_MESSAGE: "Use letters, digits, _ and - only",
    UPLOAD_CLASSIFICATION_RESERVED_MESSAGE: "K1, K2 and K3 are reserved for the shared forms",
    UPLOAD_SCOPE_LABEL: "Scope",
    UPLOAD_SCOPE_PARTNER: "Partner (one per partner)",
    UPLOAD_SCOPE_PARTNERSHIP: "Partnership",
    UPLOAD_TAX_YEAR_LABEL: "Tax year (optional)",
    UPLOAD_JURISDICTION_LABEL: "Jurisdiction (optional)",
    UPLOAD_JURISDICTION_HELP: "Federal or a two-letter state code",
    UPLOAD_JURISDICTION_FORMAT_MESSAGE: "Enter Federal or a two-letter state code",
    UPLOAD_FILE_LABEL: "PDF file",
    UPLOAD_FILE_REQUIRED_MESSAGE: "Select a PDF file",
    UPLOAD_FILE_TYPE_MESSAGE: "Only .pdf files are accepted",
    UPLOAD_FILE_SIZE_MESSAGE: "The PDF must be 10 MB or smaller",
    UPLOAD_FILE_EMPTY_MESSAGE: "The selected file is empty",
    UPLOAD_SUCCESS_MESSAGE: "Form uploaded",
    UPLOAD_FAILED_MESSAGE: "Failed to upload the form",
    DELETE_FORM_BUTTON_LABEL: "Delete Form",
    DELETE_FORM_CONFIRM_TITLE: "Delete this form?",
    DELETE_FORM_CONFIRM_DESCRIPTION:
      "This form and its lines will no longer be available. Other users of the form lose access immediately.",
    DELETE_FORM_OK_TEXT: "Delete",
    DELETE_FORM_SUCCESS_MESSAGE: "Form deleted",
    DELETE_FORM_FAILED_MESSAGE: "Failed to delete the form",
    SHARED_FORM_RESTRICTED_MESSAGE: "Shared forms (K-1, K-2, K-3) can't be deleted or have lines added or removed.",
    FIELD_LIST_NAME_HEADER: "PDF field",
    FIELD_LIST_TYPE_HEADER: "Type",
    FIELD_LIST_PAGE_HEADER: "Page",
    FIELD_LIST_STATUS_HEADER: "Status",
    FIELD_HAS_LINE_LABEL: "Has line",
    FIELD_FREE_LABEL: "No line",
    FIELD_LIST_SEARCH_PLACEHOLDER: "Search fields",
    FIELD_LIST_FREE_ONLY_LABEL: "No line only",
    FIELD_LIST_EMPTY_MESSAGE: "No fields match",
    FIELD_LIST_SUMMARY: (total: number, free: number) => `${total} fields, ${free} without a line`,
    FIELD_LIST_FAILED_MESSAGE: "Failed to load the form's PDF fields",
    PICK_MODE_VIEW: "View",
    PICK_MODE_PICK: "Pick fields",
    PICK_COUNT: (n: number) => `${n} field${n === 1 ? "" : "s"} selected`,
    NOT_APPLICABLE_FILTER_LABEL: "Not applicable",
    COUNTS_CHIP_LABELS: {
      total: "Total",
      mapped: "Lines mapped",
      unmapped: "Unmapped",
      needsReview: "Needs Review",
      notApplicable: "Not applicable",
      blankLine: "Blank Line",
      blankDescription: "Blank Description",
    },
    COUNTS_FAILED_MESSAGE: "Failed to load line counts",
    BULK_ASSIGN_BUTTON_LABEL: (n: number) => `Bulk assign (${n})`,
    BULK_SELECT_PAGE_BANNER: (shown: number, total: number) =>
      `All ${shown} lines on this page are selected.`,
    BULK_SELECT_ALL_MATCHING_LABEL: (total: number) => `Select all ${total} matching this filter`,
    BULK_SELECT_ALL_OVER_CAP_MESSAGE: (total: number, cap: number) =>
      `${total} lines match, more than the ${cap} a single bulk update can change. Narrow the filter first.`,
    BULK_ALL_MATCHING_SELECTED: (total: number) => `All ${total} lines matching this filter are selected.`,
    BULK_CLEAR_SELECTION_LABEL: "Clear selection",
    BULK_MODAL_TITLE: (n: number) => `Bulk assign ${n} line${n === 1 ? "" : "s"}`,
    BULK_MODAL_INTRO:
      "Line and Description are never changed. Only the mapping and the Needs Review flag are.",
    BULK_ACTION_LABEL: "Mapping",
    BULK_ACTION_NOT_APPLICABLE: "Mark as Not applicable",
    BULK_ACTION_CLEAR: "Clear mapping (back to Unmapped)",
    BULK_ACTION_NONE: "Leave the mapping as it is",
    BULK_CLEAR_REVIEW_LABEL: "Clear Needs Review",
    BULK_PREVIEW_BUTTON_LABEL: "Preview changes",
    BULK_APPLY_BUTTON_LABEL: "Apply",
    BULK_PREVIEW_STALE_HINT: "Preview the change before applying it.",
    BULK_PREVIEW_SUMMARY: (r: { selected: number; wouldUpdate: number; mappingChanged: number; needsReviewCleared: number; unchanged: number }) =>
      `${r.selected} selected · ${r.wouldUpdate} will change (${r.mappingChanged} mapping, ${r.needsReviewCleared} review flag) · ${r.unchanged} already as requested`,
    BULK_ERRORS_TITLE: "These lines block the update. Nothing was changed.",
    BULK_ERROR_LINE_HEADER: "Line",
    BULK_ERROR_REASON_HEADER: "Reason",
    BULK_APPLIED_MESSAGE: (n: number) => `Updated ${n} line${n === 1 ? "" : "s"}`,
    BULK_FAILED_MESSAGE: "Bulk update failed",
    BULK_K1_LOCKED_MESSAGE: "Bulk assign is not available on the K-1 form. Edit K-1 lines one at a time.",
    BULK_COUNT_MISMATCH_MESSAGE: "The matching lines changed since you selected them. Reload and select again.",
    BULK_TOO_MANY_MESSAGE: "Too many lines for one bulk update. Narrow the selection.",
    LINE_LOCKED_MESSAGE: "The Line number cannot be changed on the K-1 form.",
    LINE_FIELD_LABEL: "Line",
    PICK_CLEAR_LABEL: "Clear",
    PICK_CAP_REACHED_MESSAGE: (max: number) => `You can pick at most ${max} fields at a time.`,
    PICK_HAS_LINE_MESSAGE: "That field already has a line.",
    FIELD_PICK_ARIA_LABEL: "Pick this field",
    OUTPUT_CODES_LABEL: "Calculation output",
    OUTPUT_CODES_PLACEHOLDER: "Search and pick calculation outputs",
    OUTPUT_CODES_FAILED_MESSAGE: "Failed to load output codes",
    OUTPUT_CODES_K1_GROUP: "Standard outputs",
    OUTPUT_CODES_TEMPLATE_GROUP: "Template outputs",
    OUTPUT_CODES_CURRENT_GROUP: "Already on this line",
    OUTPUT_CODES_BAD_JSON_MESSAGE: "The mapping reference isn't a valid JSON object, so outputs can't be picked here. Fix it under Advanced first.",
    ADD_LINE_BUTTON_LABEL: "Add Line",
    ADD_LINE_MODAL_TITLE: "Add a line",
    ADD_LINE_SUBMIT_LABEL: "Add line",
    ADD_LINE_SUCCESS_MESSAGE: "Line added",
    ADD_LINE_FAILED_MESSAGE: "Failed to add the line",
    LABEL_FIELD_LABEL: "Label",
    LABEL_REQUIRED_MESSAGE: "Enter a label",
    LABEL_BLANK_HINT: "This line has no label yet. Type one to edit its details, or change only its mapping below.",
    IMPORT_BLANK_FLAGGED_TITLE: (n: number) =>
      `${n} row${n === 1 ? "" : "s"} with a blank Line or Description ${n === 1 ? "is" : "are"} imported flagged Needs Review`,
    IMPORT_FLAGGED_FIELD_HEADER: "PDF field",
    IMPORT_FLAGGED_BLANK_HEADER: "Blank",
    IMPORT_FLAGGED_BLANK_LINE: "Line",
    IMPORT_FLAGGED_BLANK_DESCRIPTION: "Description",
    IMPORT_PRESERVED_EDITS_MESSAGE: (n: number) =>
      `${n} line${n === 1 ? " was" : "s were"} edited by an admin since the last import. The file's values were not applied to ${n === 1 ? "it" : "them"}.`,
    PDF_FIELD_LABEL: "PDF field",
    PDF_FIELD_REQUIRED_MESSAGE: "Choose a PDF field",
    PDF_FIELD_PLACEHOLDER: "Search fields without a line",
    DATA_TYPE_LABEL: "Data type",
    DATA_TYPE_HELP: "Leave empty to default from the PDF field (checkbox = boolean, otherwise string)",
    SORT_HELP: "Leave empty to place the line at the end",
    PDF_FIELD_LOCKED_MESSAGE: "The PDF field of a shared form's line can't be changed.",
    BULK_CREATE_BUTTON_LABEL: (n: number) => `Create ${n} line${n === 1 ? "" : "s"}`,
    BULK_CREATE_FAILED_MESSAGE: "Failed to create the lines",
    BULK_SKIPPED_TITLE: "Some fields were skipped",
    BULK_SKIPPED_FIELD_HEADER: "PDF field",
    BULK_SKIPPED_REASON_HEADER: "Reason",
    DETAILS_SECTION_TITLE: "Details",
    MAPPING_SECTION_TITLE: "Mapping",
    ADVANCED_TOGGLE_LABEL: "Advanced",
    FEEDS_LABEL: "What feeds this line",
    FEEDS_PLACEHOLDER: "Choose what feeds this line",
    FEEDS_CALC_OUTPUT: "A calculation output",
    FEEDS_TEMPLATE_CELL: "A template cell",
    FEEDS_NONE: "Nothing (not used on this form)",
    FEEDS_OTHER: "Another source (change it under Advanced)",
    DETAILS_NO_PDF_FIELD_MESSAGE: "This line has no PDF field, so its details can't be edited here. Its mapping can still be edited below.",
    DELETE_LINE_ARIA_LABEL: "Delete line",
    DELETE_LINE_CONFIRM_TITLE: "Delete this line?",
    DELETE_LINE_SUCCESS_MESSAGE: "Line deleted",
    DELETE_LINE_FAILED_MESSAGE: "Failed to delete the line",
    DELETE_LINE_ALREADY_GONE_MESSAGE: "That line was already deleted",
    TEMPLATE_CELL_TYPE: "TemplateCell",
    TEMPLATE_CELL_LABEL: "Template cell",
    TEMPLATE_CELL_TEMPLATE_LABEL: "Template",
    TEMPLATE_CELL_SHOW_ALL_LABEL: "Show all templates in your firm",
    TEMPLATE_CELL_IN_USE_FAILED_MESSAGE: "Could not tell which templates are in use, so all templates are shown",
    TEMPLATE_CELL_NONE_IN_USE_MESSAGE: "No templates are in use for this client and tax year. Turn on Show all templates in your firm to pick another.",
    TEMPLATE_CELL_TEMPLATE_PLACEHOLDER: "Choose a template",
    TEMPLATE_CELL_TEMPLATES_FAILED_MESSAGE: "Failed to load the templates",
    TEMPLATE_CELL_CURRENT_TEMPLATE: (id: string) => `Template ${id} (current)`,
    TEMPLATE_CELL_SHEET_LABEL: "Sheet",
    TEMPLATE_CELL_SHEET_PLACEHOLDER: "Choose a sheet",
    TEMPLATE_CELL_SHEETS_FAILED_MESSAGE: "Failed to load the template's sheets",
    TEMPLATE_CELL_KIND_CELL: "Cell",
    TEMPLATE_CELL_KIND_NAME: "Defined name",
    TEMPLATE_CELL_ADDRESS_LABEL: "Cell address",
    TEMPLATE_CELL_NAME_LABEL: "Defined name to use",
    TEMPLATE_CELL_CELL_PLACEHOLDER: "For example C7",
    TEMPLATE_CELL_NAME_PLACEHOLDER: "Choose or type a defined name",
    TEMPLATE_CELL_CHECKING: "Checking…",
    TEMPLATE_CELL_VALID_MESSAGE: (address: string | null) => (address ? `Found at ${address}` : "Found"),
    TEMPLATE_CELL_NOT_FOUND_MESSAGE: "That cell was not found in the template",
    TEMPLATE_CELL_INCOMPLETE_MESSAGE: "Choose a template, a sheet, and a cell or defined name",
    TEMPLATE_CELL_VALIDATE_FAILED_MESSAGE: "Could not check the cell",
    TEMPLATE_CELL_MAPPINGS: {
      TITLE: "Template cell mappings",
      ENTITY_LABEL: "Show for entity",
      ENTITY_PLACEHOLDER: "All entities for this client",
      ENTITY_NEEDS_CLIENT: "Pick a client in the header to choose an entity",
      ENTITIES_FAILED_MESSAGE: "Failed to load the entities",
      PROFILE_FAILED_MESSAGE: "Failed to load the calculation profiles",
      NO_PROFILE_MESSAGE: "This entity has no calculation profile for the selected tax year, so the table shows what applies to the client.",
      STATUS_CURRENT: "Current",
      STATUS_OTHER_VERSION: "Other version of the template (save the line again to validate it)",
      EMPTY: "No lines on this form are mapped to a template cell",
      LINE_HEADER: "Line",
      TEMPLATE_HEADER: "Template",
      VERSION_HEADER: "Version",
      SHEET_HEADER: "Sheet",
      CELL_HEADER: "Cell",
      STATUS_HEADER: "Status",
    },
    SHARED_FORM_BADGE: "Shared",
    TENANT_FORM_BADGE: "Yours",
    FED_BY: {
      COLUMN_HEADER: "Fed by",
      EMPTY_FEEDERS: "No calculation output feeds this line yet",
      EMPTY_FEEDERS_SHORT: "Not fed yet",
      NOT_USED: "Not used by calculations",
      SHADOWED: (lineName: string | null) =>
        lineName ? `Fed through another line (${lineName})` : "Fed through another line",
      CONFLICT_CHIP: "More than one output feeds this line",
      FAILED_MESSAGE: "Could not load which calculations feed these lines",
      PANEL_TITLE: "Fed by",
      FIRM_WIDE_NOTICE:
        "This view covers every template in your firm, so some conflicts shown may not apply to the client you work on. Pick a client and tax year in the header to see only what applies.",
      CLIENT_NOTICE: (clientName: string | null, taxYear: string | null) =>
        `Showing what applies to ${clientName ?? "the selected client"}${taxYear ? `, ${taxYear}` : ""}.`,
      PROFILE_NOTICE: (taxYear: string | null) =>
        `Showing what applies to the selected calculation profile${taxYear ? `, ${taxYear}` : ""}.`,
      COUNTS: (placed: number, unplaced: number, conflict: number, unfed: number) =>
        `${placed} calculation ${placed === 1 ? "output is" : "outputs are"} placed on a line. ` +
        `${unfed} mapped ${unfed === 1 ? "line is" : "lines are"} not fed by any calculation. ` +
        `${unplaced} ${unplaced === 1 ? "output has" : "outputs have"} no line, ` +
        `${conflict} ${conflict === 1 ? "output is" : "outputs are"} fed by more than one template.`,
      DETAILS_TOGGLE: "Form details",
      TEMPLATES_TITLE: "Templates",
      NO_TEMPLATES: "No templates to show",
      CODE_STATUS_PLACED: "Placed",
      CODE_STATUS_UNPLACED: "Not placed on a line",
      CODE_STATUS_CONFLICT: "In conflict",
      PLACED_ON: (lineName: string) => `on ${lineName}`,
      NOT_SHOWN: (count: number) => `Not shown here: ${count} calculation${count === 1 ? "" : "s"} skipped`,
      UNNAMED_ITEM: "Unnamed calculation",
    },
  },

  COA_MAPPING_GLOBAL: {
    PAGE_TITLE: "Global CoA Mapping",
    PAGE_SUBTITLE:
      "Tenant-wide default account-name-to-K-1-line mapping. Individual clients can still override specific accounts on their own CoA Mapping screen.",
    DOWNLOAD_BUTTON_TEXT: "Download",
    UPLOAD_BUTTON_TEXT: "Upload",
    RESTORE_DEFAULT_BUTTON_TEXT: "Restore Default",
    REVERT_CONFIRM_TITLE: "Revert to CalcEngine default?",
    REVERT_CONFIRM_DESCRIPTION:
      "This removes your tenant's custom mapping and falls back to the seeded default.",
    REVERT_OK_TEXT: "Revert",
    TENANT_OVERRIDE_LABEL: "Tenant Override",
    CALCENGINE_DEFAULT_LABEL: "CalcEngine Default",
    ACCOUNTS_MAPPED_TEXT: (count: number) =>
      `${count} account${count === 1 ? "" : "s"} mapped`,
    LOADING_TEXT: "Loading…",
    EMPTY_STATE_MESSAGE: "No global mapping rows found.",
    FAILED_TO_LOAD_MESSAGE: "Failed to load global CoA mapping",
    DOWNLOAD_FILE_NAME: "coa-global-mapping.xlsx",
    FAILED_TO_DOWNLOAD_MESSAGE: "Failed to download global CoA mapping",
    REVERTED_SUCCESS_MESSAGE: "Reverted to CalcEngine default mapping",
    FAILED_TO_RESTORE_MESSAGE: "Failed to restore default mapping",
    UPLOAD_SUCCESS_MESSAGE: (saved: number, skippedNote: string) =>
      `Uploaded — ${saved} row(s) saved${skippedNote}`,
    CATEGORY_COLUMN_HEADER: "Category (K-1 Line)",
  },

  STANDARD_CALCULATIONS: {
    NAME_PATTERN: /^[A-Za-z0-9\s]+$/,
    INVALID_NAME_MESSAGE:
      "Invalid name - only letters and numbers are allowed.",
    DUPLICATE_NAME_MESSAGE: (name: string) =>
      `Calculation "${name}" already exists`,
  },

  EXTERNAL_K1_MAPPING: {
    PAGE_TITLE: "External K1 Mapping",
    ADD_BUTTON_TEXT: "Add Mapping",
    EDIT_BUTTON_TEXT: "Edit",
    DELETE_BUTTON_TEXT: "Delete",
    DELETE_CONFIRM_TITLE: "Deactivate this mapping?",
    SCOPE_COLUMN_HEADER: "Scope",
    CLIENT_COLUMN_HEADER: "Client",
    BOX_COLUMN_HEADER: "Box",
    CODE_COLUMN_HEADER: "Code",
    DESCRIPTION_COLUMN_HEADER: "Description",
    TARGET_LINE_COLUMN_HEADER: "Target K-1 Line",
    TAX_YEAR_COLUMN_HEADER: "Tax Year",
    SCOPE_FILTER_PLACEHOLDER: "Filter by scope",
    MODAL_TITLE_CREATE: "Add External K1 Mapping",
    MODAL_TITLE_EDIT: "Edit External K1 Mapping",
    SCOPE_LABEL: "Scope",
    CLIENT_LABEL: "Client",
    BOX_LABEL: "Box",
    BOX_PLACEHOLDER: "e.g. 13",
    CODE_LABEL: "Code (optional — blank applies to the whole box)",
    CODE_PLACEHOLDER: "e.g. W",
    DESCRIPTION_LABEL: "Description (optional, for reference)",
    TARGET_LINE_LABEL: "Target K-1 line",
    TARGET_LINE_PLACEHOLDER: "Search K-1 lines…",
    TAX_YEAR_LABEL: "Tax year (optional — blank applies to all years)",
    BOX_REQUIRED_MESSAGE: "Box is required.",
    TARGET_LINE_REQUIRED_MESSAGE: "Target K-1 line is required.",
    CLIENT_REQUIRED_MESSAGE: "Client is required for a client-scoped row.",
    SAVED_MESSAGE: "Mapping saved.",
    DEACTIVATED_MESSAGE: "Mapping deactivated.",
    FAILED_TO_LOAD_MESSAGE: "Failed to load mappings.",
    FAILED_TO_LOAD_LINES_MESSAGE: "Failed to load K-1 tax form lines.",
    GENERIC_ERROR_MESSAGE: "Something went wrong — try again.",
  },
};

//TemplateView screen constants
export const TEMPLATE_VIEW_CONSTANT = {
  ENGINE_NAME_LABEL: "Engine Name",
  IMPORT_TEMPLATE_BUTTON_LABEL: "Import Template",
  SEARCH_TEMPLATES_PLACEHOLDER: "Search Templates",
  PUBLISH_TEMPLATE_BUTTON_LABEL: "Publish Template",
  SHEETS_HEADING: "Sheets",
  ADD_NEW_SHEET_BUTTON_LABEL: "Add New Sheet",
  DRAG_TO_REORDER_TOOLTIP: "Drag to reorder",
  DEPENDENCIES_HEADING: "Dependencies",
  NO_DEPENDENCIES_HEADING: "No Dependencies",

  SHEET_CREATE: {
    SHEET_NAME_PLACEHOLDER: "Sheet Name",
    ADD_COLUMN_BUTTON_LABEL: "Add Column",
    COLUMN_HEADING_1_LABEL: "Heading 1",
    COLUMN_HEADING_2_LABEL: "Heading 2",
    COLUMN_TYPE_LABEL: "Type",
    INPUT_TYPE_LABEL: "Input",
    ROW_REFERENCE_TYPE_LABEL: "Row Reference",
    COLUMN_REFERENCE_TYPE_LABEL: "Column Reference",
    CALCULATION_TYPE_LABEL: "Calculation",
    DIAGNOSTICS_TYPE_LABEL: "Diagnostics",
    REFERENCE_DATA_LABEL: "Reference Data",
    SELECT_SHEET_PLACEHOLDER: "Select Sheet",
    SELECT_COLUMN_PLACEHOLDER: "Select Column",
    SHEET_ONE_OPTION_LABEL: "Sheet 1",
    SHEET_TWO_OPTION_LABEL: "Sheet 2",
    COLUMN_ONE_OPTION_LABEL: "Column 1",
    COLUMN_TWO_OPTION_LABEL: "Column 2",
    LOOP_ORDER_LABEL: "Loop Order",
  },
};

//SheetEditLock constants
export const SHEET_EDIT_LOCK_CONSTANT = {
  STATUS_POLL_INTERVAL_MS: 30000,
  EDITING_BY: (name: string) => `${name} is editing`,
  IMPORTING_BY: (name: string) => `${name} is importing`,
  EDITING_IN_ANOTHER_TAB: "You are editing this sheet in another tab",
  EDIT_BLOCKED: "This sheet is locked by another user",
  LOCK_LOST:
    "Your edit lock has expired. Another user may now edit this sheet; saving may be blocked.",
  CANCEL_RELEASE_FAILED:
    "Edit cancelled, but the lock could not be released. It expires in a few minutes.",
};

//ExcelViewComponent constants
export const EXCEL_VIEW_COMPONENT_CONSTANT = {
  MAX_RECORDS_FOR_SHEET: 1000000,
  IMPORT_SUCCESS_MESSAGE: "Imported successfully",
  IMPORT_TASK_PENDING_MESSAGE: "Import task is in progress.",
  IS_SHEET_CHANGED: "Do you want to save the unsaved changes?",
  SAVE_TASK_PENDING_MESSAGE: "Save task is in progress.",
  SAVE_SUCCESS: "Changes saved successfully",
  SAVE_ERROR: "Error while saving",
};

//CommonCreateComponent constants
export const COMMON_CREATE_COMPONENT_CONSTANT = {
  COA_CREATED_MESSAGE: "CoA created successfully",
  TEMPLATE_CREATED_MESSAGE: "Template created successfully",
  PLEASE_ATTACH_A_FILE_MESSAGE: "Please attach a file",
  ENTER_TEMPLATE_NAME_MESSAGE: "Please enter Template Name",
  ENTER_COA_NAME_MESSAGE: "Please enter Coa Name",
  ATTACH_FILE_LABEL: "Attach File",
  ATTACH_FILE_REQUIRED_MESSAGE: "Please attach a File",
  SELECT_FILE_BUTTON_TEXT: "Select File",
  STANDARD_CALCULATION_LABEL: "Standard Calculation",
  SELECT_STANDARD_CALCULATION_PLACEHOLDER: "Select a standard calculation…",
  STANDARD_CALCULATION_REQUIRED_MESSAGE: "Please select a Standard Calculation",
  STANDARD_CALCULATIONS_LOAD_FAILED_MESSAGE: "Could not load standard calculations",
};

//ExcelFormulaEvaluator constants
export const EXCEL_FORMULA_EVALUATOR_CONSTANT = {
  NODE_TYPE: {
    FUNCTION: "function",
    BINARY_EXPRESSION: "binary-expression",
    CELL: "cell",
    NUMBER: "number",
    TEXT: "text",
  },
  UNSUPPORTED_FUNCTION_MESSAGE: "Unsupported function",
  UNSUPPORTED_NODE_TYPE_MESSAGE: "Unsupported node type",
};

//StandardCalcDetail constants
export const STANDARD_CALC_DETAIL_CONSTANT = {
  CIRCULAR_DEPENDENCY_TOOLTIP:
    "Not allowed: this would create a circular dependency",
  DEPENDENCIES_PICKER_MAX_HEIGHT: "11.25rem",
  CIRCULAR_OPTION_OPACITY: 0.45,
};

export const CONFIGURATIONS_VIEW_CONSTANT = {
  SC_CATEGORY_COLORS: {
    Tax: { bg: "rgba(37,99,235,0.15)", color: "#60a5fa" },
    Allocation: { bg: "rgba(242,101,34,0.15)", color: "#F26522" },
    Capital: { bg: "rgba(124,58,237,0.15)", color: "#a78bfa" },
    Depreciation: { bg: "rgba(20,184,166,0.15)", color: "#2dd4bf" },
    Output: { bg: "rgba(34,197,94,0.15)", color: "#22c55e" },
    Event: { bg: "rgba(255,255,255,0.07)", color: "#9ca3af" },
  } as Record<string, { bg: string; color: string }>,
  UNTAGGED_COLORS: { bg: "rgba(255,255,255,0.06)", color: "#6b7280" },
  MAX_UPLOAD_FILE_SIZE_BYTES: 50 * 1024 * 1024,
  FILE_SIZE_LIMIT_MESSAGE: "File exceeds 50 MB limit",
  READY_TO_UPLOAD_MESSAGE: (fileName: string) => `${fileName} ready to upload`,
  SAVING_TEXT: "Saving…",
  STANDARD_CALCULATION_LABEL: "Standard Calculation",

  CALC_PROFILE: {
    DEPENDS_ON_WARNING: (names: string) =>
      `Depends on ${names} — add it to the profile`,
    DEPENDS_ALSO_ADD_WARNING: (names: string) =>
      `Depends on ${names} — also add it`,

    // AssignTemplateModal
    ASSIGN_TEMPLATE_MODAL_TITLE: "Assign Template",
    ASSIGN_TEMPLATE_MODAL_WIDTH_PX: 520,
    TEMPLATE_ASSIGNED_MESSAGE: (fileName: string) =>
      `Template assigned: ${fileName}`,
    FAILED_TO_ASSIGN_TEMPLATE_MESSAGE: "Failed to assign template",
    DROP_ZONE_UPLOAD_TEXT: "Drag & drop or click to upload",
    XLSX_FILE_HINT_TEXT: ".xlsx files only · max 50 MB",
    UPLOAD_AND_ASSIGN_BUTTON_LABEL: "Upload & Assign",

    // CalcProfileTab
    PERIOD_LABEL: "Period",
    EVENT_LABEL: "Event",
    NO_CALCULATIONS_IN_PROFILE_TEXT: (typeLabel: string) =>
      `No ${typeLabel} calculations in this profile.`,
    ADD_CALCULATION_HINT_TEXT: "Click + Add Calculation to get started.",
    TYPE_COLUMN_HEADER: "Type",
    TEMPLATE_COLUMN_HEADER: "Template",
    CLIENT_COPY_BADGE_LABEL: "Client Copy",
    GLOBAL_LIBRARY_BADGE_LABEL: "Global Library",
    DOWNLOAD_TEMPLATE_TOOLTIP: "Download template",
    REVERT_TEMPLATE_TOOLTIP: "Revert to global library template",
    NO_TEMPLATE_ASSIGNED_TEXT: "No template assigned",
    CONFIGURE_CHAINED_INPUTS_TOOLTIP: "Configure chained inputs",
    REMOVE_ITEM_TOOLTIP: (name: string) => `Remove ${name}`,
    CALCULATIONS_FOOTER_TEXT: (
      count: number,
      typeLabel: string,
      totalCount: number,
    ) =>
      `Showing ${count} ${typeLabel} calculation${count !== 1 ? "s" : ""} · ${totalCount} total in profile`,

    // AddCalcModal
    CALCULATIONS_ADDED_MESSAGE: (count: number) =>
      `${count} calculation${count !== 1 ? "s" : ""} added to profile`,
    FAILED_TO_ADD_CALCULATIONS_MESSAGE: "Failed to add calculations",
    PERIOD_CALCULATIONS_GROUP_LABEL: "Period Calculations",
    EVENT_CALCULATIONS_GROUP_LABEL: "Event Calculations",
    SELECT_CALCULATIONS_STEP_LABEL: "Select calculations",
    ASSIGN_TEMPLATES_STEP_LABEL: "Assign templates",
    ALL_CALCULATIONS_IN_PROFILE_TEXT:
      "All standard calculations are already in this profile.",
    NEXT_ASSIGN_TEMPLATES_BUTTON_LABEL: "Next — Assign Templates",
    TEMPLATE_SELECTION_OPTIONAL_TEXT:
      "Template selection is optional — you can assign templates later.",
    NO_TEMPLATES_AVAILABLE_PLACEHOLDER:
      "No templates available — upload one first",
    ALL_TEMPLATES_FALLBACK_PLACEHOLDER:
      "All templates (none tagged to this SC)",
    NO_TEMPLATE_ASSIGNED_OPTIONAL_PLACEHOLDER:
      "No template assigned (optional)",
    ACTIVE_SUFFIX_LABEL: " (active)",
    UNTAGGED_SUFFIX_LABEL: " (untagged)",
    ADDING_TEXT: "Adding…",
    ADD_CALCULATIONS_TO_PROFILE_BUTTON_LABEL: (count: number) =>
      `Add ${count} Calculation${count !== 1 ? "s" : ""} to Profile`,
    ADD_CALCULATION_MODAL_TITLE: "Add Calculation to Profile",
    ADD_CALC_MODAL_WIDTH_PX: 600,

    PULL_LATEST_TEMPLATE_TOOLTIP: "Pull latest template",
    PULL_LATEST_CONFIRM_TITLE: (name: string) => `Pull the latest template into ${name}?`,
    PULL_LATEST_CONFIRM_TEXT:
      "This calculation will use its rule engine's latest template version. Existing projects keep the version they were created with; projects created afterwards use the new one.",
    PULL_LATEST_CONFIRM_OK: "Pull latest",
    PULLED_LATEST_MESSAGE: (name: string, version: string) => `${name} now uses template ${version}`,
    ALREADY_LATEST_MESSAGE: (name: string) => `${name} is already on the latest template version`,
    FAILED_TO_PULL_LATEST_MESSAGE: "Failed to pull the latest template",
    PULL_LATEST_WARNINGS_TITLE: "Some links no longer match the new template",
    PULL_LATEST_WARNINGS_TEXT:
      "These links were kept, but their sheet is not in the new template version:",

    // ConfigureLinksModal
    FAILED_TO_LOAD_CHAINED_SHEETS_MESSAGE: "Failed to load chained sheets",
    FAILED_TO_LOAD_OUTPUT_SHEETS_MESSAGE:
      "Failed to load output sheets for the selected calculation",
    SHEET_LINKED_MESSAGE: (sheetName: string) => `${sheetName} linked`,
    FAILED_TO_SAVE_LINK_MESSAGE: "Failed to save link",
    SHEET_LINK_REMOVED_MESSAGE: (sheetName: string) =>
      `${sheetName} link removed`,
    FAILED_TO_REMOVE_LINK_MESSAGE: "Failed to remove link",
    CONFIGURE_LINKS_MODAL_TITLE: (name: string) => `Configure Links — ${name}`,
    CONFIGURE_LINKS_MODAL_WIDTH_PX: 560,
    LINK_WORKBOOK_MODAL_WIDTH_PX: 880,
    LINK_WORKBOOK_BUTTON_LABEL: "Link Workbook",
    BACK_TO_LINKS_BUTTON_LABEL: "Back",
    SOURCE_TEMPLATE_PLACEHOLDER: "Source template",
    TARGET_TEMPLATE_PLACEHOLDER: "Target template",
    NO_CHAINED_SHEETS_TEXT:
      "This calculation's template has no sheets configured to accept chained input.",
    SOURCE_CALCULATION_PLACEHOLDER: "Source calculation",
    SOURCE_SHEET_PLACEHOLDER: "Source sheet",
    FAILED_TO_LOAD_TEMPLATE_SHEETS_MESSAGE: "Failed to load the template sheets",
    NOT_MAPPED_TO_RULE_ENGINE_TEXT:
      "This calculation is not mapped to a rule engine template, so its sheets can't be linked.",
    NO_TEMPLATE_SHEETS_TEXT: "The mapped template has no sheets.",
    SOURCE_NOT_MAPPED_SUFFIX: " (no rule engine template)",
    REMOVING_TEXT: "Removing…",
    REMOVE_LINK_BUTTON_LABEL: "Remove Link",
    SAVE_LINK_BUTTON_LABEL: "Save Link",
    FAILED_TO_LOAD_LINK_CONFIG_MESSAGE: "Failed to load the link configuration",
    FAILED_TO_LOAD_TARGET_COLUMNS_MESSAGE: "Failed to load the columns of the target sheet",
    NO_LINK_SOURCES_TEXT:
      "No calculation in this profile has a mapped template to link from.",
    NO_LINK_TARGET_SHEETS_TEXT:
      "No mapped template has input sheets that can receive a link.",
    RETRY_BUTTON_LABEL: "Retry",
    SOURCE_PANE_TITLE: "Source",
    TARGET_PANE_TITLE: "Target",
    SOURCE_COLUMN_PLACEHOLDER: "Source column",
    TARGET_SHEET_PLACEHOLDER: "Target sheet",
    TARGET_COLUMN_PLACEHOLDER: "Target column",
    NO_COLUMNS_TEXT: "No columns",
    ADD_MAPPING_BUTTON_LABEL: "Add Mapping",
    REMOVE_MAPPING_BUTTON_LABEL: "Remove",
    SAVE_MAPPINGS_BUTTON_LABEL: "Save Mappings",
    MAPPING_ENTRY_TITLE: (n: number) => `Mapping ${n}`,
    MAPPINGS_SAVED_MESSAGE: (count: number) =>
      `${count} mapping${count !== 1 ? "s" : ""} saved`,
    FAILED_TO_SAVE_MAPPINGS_MESSAGE: "Failed to save the column mappings",
    MAPPING_OVERRIDE_NOTE:
      "A target column has one source. Saving a mapping for a column that is already mapped, or mapping the same column twice, overrides the earlier mapping.",
    MAPPING_OVERRIDES_EARLIER_TEXT:
      "Same target column as an earlier mapping — this mapping overrides it.",

    // CalcProfile (main)
    FAILED_TO_LOAD_PROFILE_MESSAGE: "Failed to load calculation profile",
    ITEM_REMOVED_MESSAGE: (name: string) => `${name} removed from profile`,
    FAILED_TO_REMOVE_CALCULATION_MESSAGE: "Failed to remove calculation",
    FAILED_TO_DOWNLOAD_TEMPLATE_MESSAGE: "Failed to download template",
    TEMPLATE_REVERTED_MESSAGE: (name: string) =>
      `${name} reverted to the global library template`,
    FAILED_TO_REVERT_TEMPLATE_MESSAGE: "Failed to revert template",
    SELECT_CLIENT_PARTNERSHIP_TITLE: "Select a client and partnership",
    SELECT_CLIENT_PARTNERSHIP_TEXT: "to view the calculation profile",
    PAGE_TITLE: "Calculation Profile",
    PAGE_SUBTITLE: (name: string) =>
      `${name} · The calculations that apply to this partnership.`,
    COPY_PROFILE_NO_PROFILE_TOOLTIP:
      "Add a calculation before copying this profile",
    COPY_PROFILE_MODAL_TITLE: "Copy Profile",
    COPY_PROFILE_MODAL_INTRO: (source: string, year: string) =>
      `Copy the calculations, templates and chained inputs from ${source} (${year}) to other partnerships of this client. Targets that already have calculations are skipped, never overwritten.`,
    COPY_PROFILE_LOADING_TEXT: "Loading partnerships…",
    COPY_PROFILE_LOAD_FAILED_MESSAGE: "Failed to load the client's partnerships",
    COPY_PROFILE_NO_TARGETS_TEXT: "This client has no other partnerships to copy to.",
    COPY_PROFILE_TAX_YEAR_PLACEHOLDER: "FY2025",
    COPY_PROFILE_TAX_YEAR_ARIA_LABEL: (name: string) => `Target tax year for ${name}`,
    COPY_PROFILE_INVALID_YEAR_TEXT: "Enter a 4-digit tax year",
    COPY_PROFILE_SUBMIT_LABEL: (count: number) =>
      `Copy to ${count} partnership${count === 1 ? "" : "s"}`,
    COPY_PROFILE_FAILED_MESSAGE: "Failed to copy the profile",
    COPY_PROFILE_DONE_LABEL: "Done",
    COPY_PROFILE_RESULT_TITLE: "Copy results",
    COPY_PROFILE_GROUP_CREATED: "Created",
    COPY_PROFILE_GROUP_FILLED: "Filled (existing empty profile)",
    COPY_PROFILE_GROUP_SKIPPED: "Skipped",
    COPY_PROFILE_GROUP_FAILED: "Failed",
    COPY_PROFILE_RESULT_COUNTS: (items: number, links: number) =>
      `${items} calculation${items === 1 ? "" : "s"}, ${links} link${links === 1 ? "" : "s"}`,
    COPY_PROFILE_LINKS_SKIPPED_TEXT: (count: number) =>
      `${count} link${count === 1 ? "" : "s"} skipped (one end is outside the source profile)`,
    DELETE_BLOCKED_TITLE: "Can't remove this calculation",
    DELETE_BLOCKED_OK_LABEL: "Got it",
    DELETE_BLOCKED_REASON_DEPENDENT: "Other calculations depend on it",
    DELETE_BLOCKED_REASON_PROJECT: "Projects have it selected",
    ITEM_REMOVED_LINKS_MESSAGE: (name: string, links: number) =>
      `${name} removed (${links} chained-input link${links === 1 ? "" : "s"} also removed)`,
    COPY_PROFILE_BUTTON_LABEL: "Copy Profile",
    ADD_CALCULATION_BUTTON_LABEL: "Add Calculation",
    AUTO_ADDED_SINGLE_MESSAGE: (name: string) =>
      `${name} was automatically added as a dependency.`,
    AUTO_ADDED_MULTIPLE_MESSAGE: (names: string) =>
      `${names} were automatically added as dependencies.`,
    DISMISS_ARIA_LABEL: "Dismiss",
    SEQUENCING_TAB_LABEL: "Sequencing",

    // SequencingTab
    NO_CALCULATIONS_TITLE: "No calculations in profile.",
    ADD_CALCULATIONS_FIRST_TEXT:
      "Add calculations first to see the execution order.",
    NO_UPSTREAM_DEPENDENCY_TEXT: "no upstream dependency — can run first",
    MUST_RUN_AFTER_TEXT: "must run after",
    EXECUTION_ORDER_EXPLANATION_TEXT:
      "Execution order is set automatically based on declared dependencies. Adding a calculation will auto-include any missing dependencies.",
  },

  TEMPLATES: {
    ONLY_XLSX_XLSM_FILES_ACCEPTED_MESSAGE:
      "Only .xlsx and .xlsm files are accepted",
    XLSX_XLSM_FILE_HINT_TEXT: "Only .xlsx and .xlsm files accepted (max 50MB)",
    TEMPLATE_EDITOR_DRAWER_WIDTH: "92vw",

    // formatBytes / formatRelative helpers
    NO_VALUE_PLACEHOLDER: "—",
    FILE_SIZE_LABEL_BYTES: (bytes: number) => `${bytes} B`,
    FILE_SIZE_LABEL_KB: (bytes: number) => `${(bytes / 1024).toFixed(0)} KB`,
    FILE_SIZE_LABEL_MB: (bytes: number) =>
      `${(bytes / 1024 / 1024).toFixed(1)} MB`,
    RELATIVE_TIME_MINUTES: (mins: number) => `${mins}m ago`,
    RELATIVE_TIME_HOURS: (hrs: number) => `${hrs}h ago`,
    RELATIVE_TIME_DAYS: (days: number) =>
      `${days} day${days !== 1 ? "s" : ""} ago`,

    // ScBadge
    UNTAGGED_BADGE_LABEL: "Untagged",

    // TemplateDetailsModal
    TEMPLATE_DETAILS_MODAL_TITLE: (fileName: string) =>
      `Template Details — ${fileName}`,
    DETAILS_MODAL_WIDTH_PX: 640,
    SHEETS_TAB_LABEL: "Sheets",
    VERSIONS_TAB_LABEL: (count: number) => `Versions (${count})`,
    USED_BY_TAB_LABEL: "Used By",
    NO_SHEET_CONFIG_TEXT:
      "No sheet configuration detected — legacy-format template.",
    SHEET_COLUMN_HEADER: "Sheet",
    ROLE_COLUMN_HEADER: "Role",
    VERSION_COLUMN_HEADER: "Version",
    UPLOADED_COLUMN_HEADER: "Uploaded",
    STATUS_COLUMN_HEADER: "Status",
    ACTIVE_BADGE_LABEL: "Active",
    INACTIVE_BADGE_LABEL: "Inactive",
    NO_USAGE_TEXT:
      "No calculation profile item currently references this template.",
    ACTIVE_REFERENCES_COLUMN_HEADER: "Active References",

    // VersionsDrawer
    VERSION_ACTIVATED_MESSAGE: (versionNumber: number) =>
      `v${versionNumber} set as active`,
    FAILED_TO_ACTIVATE_VERSION_MESSAGE: "Failed to activate version",
    DEACTIVATE_VERSION_CONFIRM_TITLE: (versionNumber: number) =>
      `Deactivate v${versionNumber}?`,
    DEACTIVATE_VERSION_CONFIRM_CONTENT:
      "This version will no longer be usable by the calculation engine.",
    VERSION_DEACTIVATED_MESSAGE: (versionNumber: number) =>
      `v${versionNumber} deactivated`,
    FAILED_TO_DEACTIVATE_VERSION_MESSAGE: "Failed to deactivate version",
    DOWNLOAD_FAILED_MESSAGE: "Download failed",
    FAILED_TO_LOAD_TEMPLATES_MESSAGE: "Failed to load templates",
    FAILED_TO_LOAD_STANDARD_CALCULATIONS_MESSAGE: "Failed to load standard calculations",
    VERSION_HISTORY_DRAWER_TITLE: "Version History",
    VERSIONS_DRAWER_WIDTH_PX: 580,
    UPLOADED_BY_COLUMN_HEADER: "Uploaded By",
    DATE_COLUMN_HEADER: "Date",
    SIZE_COLUMN_HEADER: "Size",
    ACTION_COLUMN_HEADER: "Action",
    ACTIONS_COLUMN_HEADER: "Actions",
    DEFAULT_UPLOADED_BY_LABEL: "admin",
    DOWNLOAD_BUTTON_LABEL: "Download",
    ACTIVATE_BUTTON_LABEL: "Activate",
    LATEST_NOTE_PREFIX: (notes: string) => `Latest: ${notes}`,

    // TagScModal
    SC_ASSIGNED_MESSAGE: "Standard Calculation assigned",
    SC_TAG_ENDPOINT_NOT_IMPLEMENTED_MESSAGE:
      "BackEnd endpoint not yet implemented — SC tagging requires PATCH /api/template/{id}/sc",
    FAILED_TO_ASSIGN_SC_MESSAGE: "Failed to assign Standard Calculation",
    ASSIGN_SC_MODAL_TITLE: "Assign Standard Calculation",
    ASSIGN_SC_MODAL_WIDTH_PX: 480,
    TEMPLATE_LABEL_PREFIX: "Template:",
    SELECT_STANDARD_CALCULATION_PLACEHOLDER: "Select a standard calculation…",
    ASSIGN_BUTTON_LABEL: "Assign",

    // EditDetailsModal
    FAILED_TO_UPDATE_TEMPLATE_MESSAGE: "Failed to update template",
    TEMPLATE_UPDATED_MESSAGE: "Template updated",
    EDIT_TEMPLATE_MODAL_TITLE: "Edit Template",
    EDIT_TEMPLATE_MODAL_WIDTH_PX: 480,
    TEMPLATE_NAME_REQUIRED_MESSAGE: "Template name cannot be empty",
    TEMPLATE_NAME_PLACEHOLDER: "Template name",
    DESCRIPTION_OPTIONAL_LABEL: "Description (optional)",
    DESCRIPTION_PLACEHOLDER: "What this template is used for",

    // UploadModal
    UPLOAD_NEW_TEMPLATE_MODAL_TITLE: "Upload New Template",
    UPLOAD_MODAL_WIDTH_PX: 520,
    DRAG_DROP_EXCEL_TEXT:
      "Drag & drop your Excel template here, or click to browse",
    TEMPLATE_UPLOADED_FAILED_VALIDATION_MESSAGE:
      "Template uploaded but failed structure validation",
    TEMPLATE_UPLOADED_SUCCESS_MESSAGE: (fileName: string) =>
      `${fileName} uploaded successfully`,
    INVALID_TEMPLATE_STRUCTURE_TEXT:
      "Invalid template structure — cannot be assigned to a calc profile item",
    SHEETS_DETECTED_TEXT: (count: number) =>
      `${count} sheet${count !== 1 ? "s" : ""} detected:`,
    LEGACY_TEMPLATE_NO_SHEETS_TEXT:
      "Legacy-format template — no new-convention sheets detected.",
    STANDARD_CALCULATION_REQUIRED_MESSAGE:
      "Please select a standard calculation",
    VERSION_NOTES_OPTIONAL_LABEL: "Version Notes (optional)",
    VERSION_NOTES_PLACEHOLDER: "Version notes or changelog",
    DONE_BUTTON_LABEL: "Done",
    UPLOAD_TEMPLATE_BUTTON_LABEL: "Upload Template",

    // Templates (main)
    PAGE_TITLE: "Template Management",
    UPLOAD_TEMPLATE_HEAD_BUTTON_LABEL: "+ Upload Template",
    SEARCH_TEMPLATES_PLACEHOLDER: "Search templates…",
    ALL_STANDARD_CALCULATIONS_OPTION_LABEL: "All Standard Calculations",
    ALL_STATUSES_OPTION_LABEL: "All Statuses",
    ACTIVE_OPTION_LABEL: "Active",
    INACTIVE_OPTION_LABEL: "Inactive",
    NO_TEMPLATES_FOUND_TITLE: "No templates found",
    TRY_CLEARING_FILTERS_TEXT: "Try clearing the filters.",
    UPLOAD_FIRST_TEMPLATE_HINT_TEXT:
      'Click "+ Upload Template" to add the first template.',
    TEMPLATE_NAME_COLUMN_HEADER: "Template Name",
    ACTIONS_MORE_ARIA_LABEL: "More actions",
    DETAILS_MENU_LABEL: "Details",
    EDIT_MENU_LABEL: "Edit",
    OPEN_EDITOR_MENU_LABEL: "Open Editor",
    VERSIONS_MENU_LABEL: (count: number) => `Versions (${count})`,
    TAG_SC_MENU_LABEL: "Tag SC",
    TEMPLATE_COUNT_FOOTER_TEXT: (count: number) =>
      `${count} template${count !== 1 ? "s" : ""}`,
    FILTERED_FROM_SUFFIX_TEXT: (totalCount: number) =>
      ` (filtered from ${totalCount})`,
    TEMPLATE_EDITOR_DRAWER_TITLE: "Template Editor",
  },

  TEMPLATE_EDITOR: {
    GRID_THEME_PARAMS: {
      backgroundColor: "rgb(8,12,28)",
      foregroundColor: "rgba(255,255,255,0.80)",
      headerBackgroundColor: "rgba(26,39,68,0.95)",
      headerTextColor: "rgba(255,255,255,0.50)",
      rowHoverColor: "rgba(255,255,255,0.04)",
      borderColor: "rgba(255,255,255,0.08)",
      fontSize: 13,
    },
    SAVED_VERSION_MESSAGE: "Saved as new template version",
    SAVE_FAILED_MESSAGE: "Save failed",
    INPUT_LEGEND_LABEL: "Input",
    FORMULA_LEGEND_LABEL: "Formula",
    OUTPUT_LEGEND_LABEL: "Output",
    SAVE_VERSION_BUTTON_LABEL: "Save Version",
    LOADING_TEMPLATE_TEXT: "Loading template...",
    TEMPLATE_UNAVAILABLE_TEXT:
      "Template data unavailable — check template upload and try again.",
    LIVE_K1_OUTPUT_LABEL: "Live K-1 Output",
    RECALCULATING_TEXT: "Recalculating...",
    PARTNERS_LABEL: "Partners",
    NO_VALUE_PLACEHOLDER: "—",
    K1_OUTPUT_LINES: [
      { key: "L1", label: "Ordinary Income", icon: "↗" },
      { key: "L5", label: "Interest Income", icon: "↗" },
      { key: "L9a", label: "Net LT Cap Gain", icon: "↗" },
      { key: "L13", label: "Depreciation", icon: "↘" },
      { key: "L16", label: "Foreign Tax", icon: "→" },
      { key: "L20", label: "Ending Capital", icon: "↗" },
    ],
  },

  PARTNERSHIP_SETTINGS: {
    TAX_YEAR_END_DATE_FORMAT: "DD/MM/YYYY",
    TAX_YEAR_END_SAVED_MESSAGE: "Tax year end saved",
    MODULE_PROCESSING_SECTION_LABEL: "Module & Processing",
    FIXED_ASSET_MODULE_TITLE: "Fixed Asset Module",
    FIXED_ASSET_MODULE_DESC:
      "Enable or disable the fixed asset module for this partnership. Disable for investment-only funds.",
    PROCESSING_MODE_TITLE: "Calculation Processing Mode",
    PROCESSING_MODE_DESC:
      "How calculations are processed. External partnerships are excluded from the engine but remain in tiering structure.",
    CALC_ENGINE_MODE_LABEL: "CalcEngine",
    EXTERNAL_MODE_LABEL: "External",
    GUARANTEED_PAYMENTS_TITLE: "Guaranteed Payments",
    GUARANTEED_PAYMENTS_DESC:
      "Flag whether any partners receive guaranteed payments.",
    TAX_ACCOUNTING_SECTION_LABEL: "Tax & Accounting",
    TAX_YEAR_END_TITLE: "Tax Year End",
    TAX_YEAR_END_DESC: "Partnership tax year-end date.",
    SAVE_SETTINGS_BUTTON_LABEL: "Save Settings",
    FAILED_TO_LOAD_SETTINGS_MESSAGE: "Failed to load partnership settings",
    FAILED_TO_SAVE_SETTINGS_MESSAGE: "Failed to save settings",
    PAGE_TITLE: "Settings",
    PAGE_SUBTITLE: (name: string) =>
      `${name} · Configure before running any calculations.`,
    PARTNERS_TAB_LABEL: "Partners",
    EXTERNAL_K1_INPUT_LABEL: "External K-1 Input",
    SALT_TAB_LABEL: "State & Local Tax",
    SALT_COMING_SOON_TITLE: "State & Local Tax Settings",
    SELECT_CLIENT_PARTNERSHIP_TEXT:
      "Select a client and partnership to view settings",
    SETTINGS_SAVED_AUTOMATICALLY_TEXT: "Settings saved automatically",
  },
};

//CalculationsView constants
export const CALCULATIONS_VIEW_CONSTANT = {
  ACCOUNT_ID_COLUMN_HEADER: "Account ID",
  AMOUNT_COLUMN_HEADER: "Amount",

  UPLOAD_INPUT: {
    PAGE_TITLE: "Upload Input",
    PAGE_SUBTITLE:
      "Upload Excel files to populate calculation inputs for this engagement.",
    UPLOADING_FOR_PREFIX: "Uploading for:",
    INPUT_TYPE_COLUMN_HEADER: "Input Type",
    DESCRIPTION_COLUMN_HEADER: "Description",
    UPLOAD_FILE_COLUMN_HEADER: "Upload File",
    TEMPLATE_COLUMN_HEADER: "Template",
    STATUS_COLUMN_HEADER: "Status",
    GO_TO_COA_MAPPING_TEXT: "Go to CoA Mapping",
    UPLOAD_FILE_BUTTON_TEXT: "Upload File",
    READY_STATUS_LABEL: "Ready",
    CONFIGURE_STATUS_LABEL: "Configure →",
    COMING_SOON_STATUS_LABEL: "Coming Soon",
    INPUTS_TAB_BANNER_TEXT: "Inputs for a project are loaded on the project's Inputs tab.",
    UPLOAD_ANOTHER_TEXT: "Upload another",
    RETRY_TEXT: "Retry",
    TRIAL_BALANCE_ROWS_LOADED_TEXT: (count: number) =>
      `${count} trial balance row${count === 1 ? "" : "s"} already loaded`,
    MAP_ACCOUNTS_TEXT: "Map Accounts",
    UPLOAD_ANOTHER_FILE_TEXT: "Upload another file",
    DOWNLOAD_TEMPLATE_BUTTON_TEXT: "Download Template",
    FAILED_TO_DOWNLOAD_TEMPLATE_MESSAGE: (name: string) =>
      `Failed to download template for ${name}`,
    PARTNERS_IMPORTED_MESSAGE: (imported: number) =>
      `${imported} partners imported`,
    TRIAL_BALANCE_IMPORTED_MESSAGE: (imported: number) =>
      `Trial balance imported — ${imported} rows`,
    UPLOADED_SUCCESSFULLY_MESSAGE: "Uploaded successfully",
    PARTIAL_IMPORT_MESSAGE: (imported: number, skipped: number) =>
      `${imported} imported, ${skipped} skipped — see errors for detail`,
    NO_RECORDS_IMPORTED_MESSAGE: "0 records imported — check file format",
    ROWS_IMPORTED_MESSAGE: (count: number) => `${count} row(s) imported.`,
    UPLOAD_REJECTED_MESSAGE: "Upload rejected — see the errors below.",
    RESOLVING_PROJECT_TEXT: "Resolving project…",
    PROJECT_CHECK_FAILED_TEXT: "Could not check for a project. Reload and try again.",
    NO_PROJECT_TEXT: "No project exists for this partnership/tax year.",
    CREATE_PROJECT_TEXT: "Create a project",
    DOWNLOAD_ERROR_REPORT_TEXT: "Download error report",
    DOWNLOAD_ERROR_REPORT_FAILED_MESSAGE: "Failed to download error report.",
    ENTITY_META_TEXT: (entityId: number | string | undefined, taxYear: string | undefined) => `Entity ${entityId} · ${taxYear}`,
    PROJECT_OPTION_LABEL: (name: string, engagementType: string | null | undefined, allocationMethod: string | null | undefined) =>
      `${name} (${engagementType ?? "—"} / ${allocationMethod ?? "—"})`,
    TRIAL_BALANCE_STATE_FAILED_MESSAGE: (status: number) => `Failed to load trial balance state (${status})`,
    TRIAL_BALANCE_ROW_COUNT_FAILED_MESSAGE: "Could not check whether trial balance rows are already loaded. Reload and try again.",
    TEMPLATE_DOWNLOAD_FAILED_MESSAGE: (status: number) => `Failed to download template (${status})`,
    ERROR_COUNT_TEXT: (count: number) => `${count} error${count === 1 ? "" : "s"}`,
    WARNING_COUNT_TEXT: (count: number) => `${count} warning${count === 1 ? "" : "s"}`,
    COUNT_SEPARATOR_TEXT: ", ",
    ERROR_ROW_COLUMN_HEADER: "Row",
    ERROR_COLUMN_COLUMN_HEADER: "Column",
    ERROR_SEVERITY_COLUMN_HEADER: "Severity",
    ERROR_MESSAGE_COLUMN_HEADER: "Message",
    EMPTY_VALUE_PLACEHOLDER: "—",
    ACCEPTED_FILE_EXTENSION: ".xlsx",
    TEMPLATE_FILE_NAME: {
      TRIAL_BALANCE: "CalcEngine_TrialBalance_Template.xlsx",
      PARTNER_DATA: "CalcEngine_PartnerData_Template.xlsx",
      PRIOR_YEAR_CAPITAL: "CalcEngine_PriorYearCapital_Template.xlsx",
      ACTIVITIES: "CalcEngine_activities_Template.xlsx",
    },
    INPUT_TYPE: {
      PARTNER_DATA: {
        NAME_LABEL: "Partner Data",
        DESCRIPTION_TEXT: "Partner roster, interest classes, and beginning capital accounts for this engagement.",
      },
      TRIAL_BALANCE: {
        NAME_LABEL: "Trial Balance",
        DESCRIPTION_TEXT: "GAAP trial balance with account mapping to the Chart of Accounts for income allocation.",
      },
      PRIOR_YEAR_CAPITAL: {
        NAME_LABEL: "Prior Year Capital Accounts",
        DESCRIPTION_TEXT: "Beginning capital balances, contributions, and distributions for prior-year roll-forward.",
      },
      FIXED_ASSETS: {
        NAME_LABEL: "Fixed Assets",
        DESCRIPTION_TEXT: "Asset register for MACRS depreciation and basis adjustment calculations.",
      },
      PRIOR_YEAR_M1: {
        NAME_LABEL: "Prior Year M1 Adjustments",
        DESCRIPTION_TEXT: "Book-to-tax differences from the prior year for roll-forward calculations.",
      },
      CASH_FLOW: {
        NAME_LABEL: "Cash Flow",
        DESCRIPTION_TEXT: "Partnership cash flow statements for distribution and allocation calculations.",
      },
      DISTRIBUTIONS: {
        NAME_LABEL: "Distributions",
        DESCRIPTION_TEXT: "Partner distribution history and scheduled distributions for the tax year.",
      },
      EVENTS: {
        NAME_LABEL: "Events",
        DESCRIPTION_TEXT: "Partnership events including contributions, transfers, and revaluations.",
      },
      ACTIVITIES: {
        NAME_LABEL: "Activities",
        DESCRIPTION_TEXT: "Business activity data for state apportionment and allocation purposes.",
      },
      EXTERNAL_K1S: {
        NAME_LABEL: "External K-1s",
        DESCRIPTION_TEXT: "K-1s received from lower-tier partnerships for income pass-through.",
      },
      LAYERS: {
        NAME_LABEL: "Layers",
        DESCRIPTION_TEXT: "704(c) layers and built-in gain/loss tracking for contributed property.",
      },
      RATIOS: {
        NAME_LABEL: "Ratios",
        DESCRIPTION_TEXT: "Allocation ratios and special allocation schedules for partnership items.",
      },
      OUTSTANDING_DEBT: {
        NAME_LABEL: "Outstanding Debt",
        DESCRIPTION_TEXT: "Partnership debt schedule for basis allocation and at-risk calculations.",
      },
      NONRECOURSE_DEBT: {
        NAME_LABEL: "Non-Recourse Debt — Asset Mapping",
        DESCRIPTION_TEXT: "Mapping of non-recourse debt to specific partnership assets.",
      },
      RECOURSE_DEBT: {
        NAME_LABEL: "Recourse Debt Allocation",
        DESCRIPTION_TEXT: "Partner-level recourse debt allocation for outside basis calculations.",
      },
      ACCOUNT_MAPPING: {
        NAME_LABEL: "Account Mapping",
        DESCRIPTION_TEXT: "Map trial balance accounts to income categories for calculation templates.",
      },
      FULL_SETUP: {
        NAME_LABEL: "Full Setup Import",
        DESCRIPTION_TEXT: "Import everything at once from a single configuration Excel file.",
      },
    },
  },

  ROUTE_TITLE: {
    ROLL_FORWARD: "Roll Forward",
    REVISION_HISTORY: "Revision History",
  },

  EXTERNAL_K1_ROW: {
    SECTION_ARIA_LABEL: "External K-1s",
    SECTION_TITLE: "External K-1s",
    ROW_TITLE: (name: string) => `External K-1 — ${name}`,
    YEAR_TEXT: (year: number) => `Tax year ${year}`,
    CHIP_NEEDED_LABEL: "Needed",
    CHIP_UPLOADED_LABEL: "Uploaded",
    CHIP_UPLOADED_ON_LABEL: (date: string) => `Uploaded ${date}`,
    UPLOAD_ACTION_TEXT: "Upload",
    UPLOAD_ARIA_LABEL: (name: string) => `Upload External K-1 for ${name}`,
  },

  INPUT_REPOSITORY: {
    NAV_LABEL: "Input Repository",
    PAGE_TITLE: "Input Repository",
    ENTITY_BANNER_PREFIX: "Input repository for:",
    NO_ENTITY_TEXT: "Select a client, a partnership and a tax year to see its input repository.",
    CREATE_CATEGORY_BUTTON_TEXT: "Create Category",
    NAME_COLUMN_HEADER: "Name",
    TYPE_COLUMN_HEADER: "Type",
    DESCRIPTION_COLUMN_HEADER: "Description",
    FILES_COLUMN_HEADER: "Files",
    UPDATED_COLUMN_HEADER: "Last updated",
    ACTIONS_COLUMN_HEADER: "Actions",
    EMPTY_VALUE_TEXT: "—",
    TYPE_GENERAL_LABEL: "General",
    TYPE_TRIAL_BALANCE_LABEL: "Trial Balance",
    LOAD_FAILED_TEXT: "We couldn't load the categories right now.",
    RETRY_BUTTON_TEXT: "Retry",
    EMPTY_TITLE: "No categories yet",
    EMPTY_HINT: "Create a category to start keeping input files for this partnership and tax year.",
    CREATE_MODAL_TITLE: "Create Category",
    EDIT_MODAL_TITLE: "Rename Category",
    CREATE_SUBMIT_TEXT: "Create",
    EDIT_SUBMIT_TEXT: "Save",
    CANCEL_BUTTON_TEXT: "Cancel",
    NAME_LABEL: "Name",
    NAME_REQUIRED_MESSAGE: "Enter a category name",
    DESCRIPTION_LABEL: "Description (optional)",
    TYPE_LABEL: "Type",
    TYPE_HELP: "A Trial Balance category also runs the trial balance import for its files.",
    TYPE_LOCKED_HELP: "The type is set when the category is created.",
    RENAME_ACTION_LABEL: "Rename",
    ACTIONS_ARIA_LABEL: (name: string) => `Actions for ${name}`,
    DELETE_ACTION_LABEL: "Delete",
    DELETE_CONFIRM_TITLE: (name: string) => `Delete "${name}"?`,
    DELETE_CONFIRM_OK_TEXT: "Delete",
    DELETE_CONFIRM_CANCEL_TEXT: "Cancel",
    CREATED_MESSAGE: (name: string) => `Category "${name}" created`,
    UPDATED_MESSAGE: (name: string) => `Category "${name}" saved`,
    DELETED_MESSAGE: (name: string) => `Category "${name}" deleted`,
    DUPLICATE_NAME_MESSAGE: "A category with this name already exists for this partnership and tax year.",
    SAVE_FAILED_MESSAGE: "We couldn't save the category. Try again.",
    DELETE_BLOCKED_MESSAGE: "This category can't be deleted while a project still uses it.",
    DELETE_FAILED_MESSAGE: "We couldn't delete the category. Try again.",
    UPLOAD_FILES_ACTION_LABEL: "Upload files",
    UPLOAD_MODAL_TITLE: (category: string) => `Upload files to "${category}"`,
    UPLOAD_SUBMIT_TEXT: "Upload",
    DROP_HINT_TEXT: "Click or drag .xlsx, .xlsm or .csv files here",
    DROP_HINT_SUBTEXT: "A CSV file counts as one sheet.",
    PICKED_FILES_LABEL: "Files to upload",
    REMOVE_PICKED_FILE_LABEL: "Remove",
    HEADER_ROW_LABEL: "Header row (optional)",
    DATA_START_ROW_LABEL: "Data start row (optional)",
    ROWS_HELP: "Leave blank to detect automatically. Rows are numbered from 1, as in Excel.",
    FILE_WRONG_TYPE_MESSAGE: (name: string) => `${name}: only .xlsx, .xlsm and .csv files are accepted.`,
    FILE_EMPTY_MESSAGE: (name: string) => `${name} is empty.`,
    INDEX_INVALID_MESSAGE: "Row numbers must be whole numbers from 1.",
    DATA_START_AFTER_HEADER_MESSAGE: "The data start row must come after the header row.",
    UPLOAD_SUCCESS_MESSAGE: (count: number) => `${count} file${count === 1 ? "" : "s"} uploaded`,
    UPLOAD_FAILED_MESSAGE: "We couldn't upload the files. Try again.",
    UPLOAD_FILE_FAILED_LINE: (name: string, reason: string) => `${name}: ${reason}`,
    UPLOAD_SOME_FAILED_MESSAGE: (count: number) => `${count} file${count === 1 ? " was" : "s were"} not uploaded`,
    FILES_LOAD_FAILED_TEXT: "We couldn't load the files for this category.",
    FILES_EMPTY_TEXT: "No files in this category yet.",
    FILE_NAME_COLUMN_HEADER: "File",
    FILE_SIZE_COLUMN_HEADER: "Size",
    FILE_UPLOADED_COLUMN_HEADER: "Uploaded",
    FILE_SHEETS_COLUMN_HEADER: "Sheets",
    FILE_HEADER_ROW_COLUMN_HEADER: "Header row",
    FILE_DATA_START_COLUMN_HEADER: "Data start",
    REPLACE_ACTION_LABEL: "Replace",
    DOWNLOAD_ACTION_LABEL: "Download",
    REIMPORT_TB_ACTION_LABEL: "Re-import TB",
    REIMPORT_TB_UNSUPPORTED_TOOLTIP: "Trial balance import needs an .xlsx file.",
    FILE_DELETE_CONFIRM_TITLE: (name: string) => `Delete "${name}"?`,
    FILE_DELETED_MESSAGE: (name: string) => `"${name}" deleted`,
    FILE_DELETE_BLOCKED_MESSAGE: "This file can't be deleted while a project maps one of its sheets.",
    FILE_DELETE_FAILED_MESSAGE: "We couldn't delete the file. Try again.",
    REPLACE_SUCCESS_MESSAGE: (name: string, moved: number) =>
      `"${name}" replaced. ${moved} mapping${moved === 1 ? "" : "s"} moved.`,
    REPLACE_FAILED_MESSAGE: "We couldn't replace the file. Try again.",
    REPLACE_BROKEN_TITLE: "Some mappings could not move",
    REPLACE_BROKEN_INTRO:
      "These project sheets pointed at a sheet that is not in the new file. Fix them on the project's Inputs tab.",
    REPLACE_BROKEN_ITEM: (projectId: number, sheet: string) => `Project ${projectId}: ${sheet}`,
    REPLACE_BROKEN_OK_TEXT: "OK",
    DOWNLOAD_FAILED_MESSAGE: "We couldn't download the file. Try again.",
    FILE_ROWS_TITLE: "File rows",
    APPLY_BUTTON_TEXT: "Apply",
    USE_FILE_ROWS_BUTTON_TEXT: "Use file rows",
    INDEXES_SAVED_MESSAGE: "Rows saved",
    INDEXES_FAILED_MESSAGE: "We couldn't save the rows. Try again.",
    SHEET_COLUMN_HEADER: "Sheet",
    SHEET_ROWS_COLUMN_HEADER: "Rows",
    SHEET_HEADERS_COLUMN_HEADER: "Headers",
    SHEET_OVERRIDES_COLUMN_HEADER: "Row overrides",
    PREVIEW_ACTION_LABEL: "Preview",
    PREVIEW_DRAWER_TITLE: (sheet: string, file: string) => `${sheet} — ${file}`,
    PREVIEW_FAILED_TEXT: "We couldn't load the preview for this sheet.",
    PREVIEW_EMPTY_TEXT: "This sheet has no rows to preview.",
    PREVIEW_GRID_ARIA_LABEL: "Sheet preview",
    COLUMN_FALLBACK_LABEL: (position: number) => `Column ${position}`,
  },

  PROJECT_INPUT: {
    // A sheet's Sync opens the Category / File / Sheet dialog for that sheet in its break period (user decision 2026-10-29).
    ROW_SYNC_ACTION: "Sync",
    ROW_SYNC_ARIA: (sheet: string) => `Sync ${sheet} from an Input Repository sheet`,
    SYNCED_FROM_TEXT: (path: string, confidence: string | null) =>
      (confidence ? `Synced from ${path} (${confidence})` : `Synced from ${path}`),
    SYNC_BROKEN_NOTE: "The file or sheet it came from is gone. Sync it again to fix it.",
    SYNC_REVIEW_NOTE: "The file changed, so review the column mapping.",
    SYNC_BUTTON_TEXT: "Sync",
    SYNCING_TEXT: "Syncing…",
    SYNC_FAILED_MESSAGE: "We couldn't read the input repository. Try again.",
    ITEM_ARIA: (name: string) => `${name} input sheets`,
    SHEET_COLUMN_HEADER: "Sheet",
    MAPPING_COLUMN_HEADER: "Mapping",
    ORIGIN_COLUMN_HEADER: "Origin",
    CONFIDENCE_COLUMN_HEADER: "Confidence",
    STATUS_COLUMN_HEADER: "Status",
    ACTIONS_COLUMN_HEADER: "Actions",
    EMPTY_VALUE_TEXT: "—",
    NOT_MAPPED_TEXT: "Not mapped",
    MAPPING_PATH_TEXT: (category: string, file: string, sheet: string) => `${category} › ${file} › ${sheet}`,
    SOURCE_ROSTER_TEXT: "Filled from the partner list",
    SOURCE_ROLL_FORWARD_TEXT: "Rolled forward from last year",
    SOURCE_LOWER_TIER_TEXT: "Lower-tier amounts",
    ORIGIN_AUTO_TEXT: "auto",
    ORIGIN_USER_TEXT: "user",
    ORIGIN_ROSTER_TEXT: "roster",
    ORIGIN_ROLL_FORWARD_TEXT: "rollforward",
    ORIGIN_LOWER_TIER_TEXT: "lower-tier",
    CONFIDENCE_TEXT: (score: number) => `${score}%`,
    STATUS_MAPPED_TEXT: "Mapped",
    STATUS_NOT_MAPPED_TEXT: "Not mapped",
    STATUS_NEEDS_REVIEW_TEXT: "Needs review",
    STATUS_BROKEN_TEXT: "Broken",
    MAP_ACTION: "Map",
    CHANGE_ACTION: "Change",
    MAP_ARIA: (sheet: string) => `Map ${sheet}`,
    CHANGE_ARIA: (sheet: string) => `Change mapping for ${sheet}`,
    UNMAP_ACTION: "Unmap",
    UNMAP_ARIA: (sheet: string) => `Unmap ${sheet}`,
    UNMAP_CONFIRM_TITLE: (sheet: string) => `Unmap "${sheet}"?`,
    UNMAP_CONFIRM_OK_TEXT: "Unmap",
    UNMAP_CONFIRM_CANCEL_TEXT: "Cancel",
    UNMAPPED_MESSAGE: (sheet: string) => `"${sheet}" is no longer mapped`,
    UNMAP_FAILED_MESSAGE: "We couldn't remove the mapping. Try again.",
    VIEW_SHEET_ACTION: "View sheet",
    VIEW_SHEET_ARIA: (sheet: string) => `View sheet ${sheet}`,
    NO_ITEMS_TEXT: "This project has no input sheets to map.",
    BANNER_READY_TEXT: (count: number) => `All ${count} input sheet${count === 1 ? " is" : "s are"} mapped.`,
    BANNER_NEEDS_PREFIX: "Run stays off until every input sheet is mapped: ",
    BANNER_MISSING_TEXT: (count: number) => `${count} not mapped`,
    BANNER_BROKEN_TEXT: (count: number) => `${count} broken`,
    BANNER_REVIEW_TEXT: (count: number) => `${count} need${count === 1 ? "s" : ""} review`,
    PERIOD_ACTIONS_HINT: "Choose a break period to use Fill from roster, Roll forward and View sheet.",
    MAP_DIALOG_TITLE: (sheet: string) => `Sync "${sheet}"`,
    CATEGORY_LABEL: "Category",
    FILE_LABEL: "File",
    REPOSITORY_SHEET_LABEL: "Sheet",
    CATEGORY_PLACEHOLDER: "Choose a category",
    FILE_PLACEHOLDER: "Choose a file",
    REPOSITORY_SHEET_PLACEHOLDER: "Choose a sheet",
    NO_CATEGORIES_TEXT: "No categories yet. Create one in the Input Repository first.",
    NO_FILES_TEXT: "This category has no files.",
    REPOSITORY_LOAD_FAILED_TEXT: "We couldn't load the input repository.",
    AUTO_MAP_BUTTON_TEXT: "Auto-map columns",
    COLUMN_GRID_TITLE: "Column mapping",
    COLUMN_TARGET_HEADER: "Template column",
    COLUMN_SOURCE_HEADER: "File column",
    COLUMN_SAMPLES_HEADER: "Sample values",
    COLUMN_ORIGIN_HEADER: "Origin",
    COLUMN_NOT_MAPPED_TEXT: "Not mapped",
    COLUMN_SOURCE_ARIA: (target: string) => `File column for ${target}`,
    COLUMNS_LOADING_TEXT: "Reading columns…",
    COLUMNS_FAILED_TEXT: "We couldn't propose columns for this sheet.",
    COLUMNS_REPROPOSED_NOTE: "Column choices you saved earlier are not shown here; the columns below are proposed again.",
    SAVE_MAPPING_TEXT: "Save mapping",
    MAPPING_SAVED_MESSAGE: (sheet: string) => `"${sheet}" mapped`,
    MAPPING_SAVE_FAILED_MESSAGE: "We couldn't save the mapping. Try again.",
  },

  EXTERNAL_K1: {
    PDF_ACCEPT: ".pdf",
    ERROR_ACTION: {
      REPREVIEW: "repreview",
      KEEP_PICKS: "keep-picks",
      SHOW: "show",
    } as const,
    CONFIRM_KIND: {
      CREATED: "created",
      NOOP: "noop",
      RESTORED: "restored",
    } as const,
    MODAL_WIDTH_PX: 820,
    BOX_COLUMN_WIDTH_PX: 80,
    CODE_COLUMN_WIDTH_PX: 80,
    AMOUNT_COLUMN_WIDTH_PX: 130,
    STATUS_COLUMN_WIDTH_PX: 110,
    YEAR_MAX_LENGTH: 4,
    YEAR_REGEX: /^\d{4}$/,
    YEAR_IN_TEXT_REGEX: /\d{4}/,
    NON_DIGIT_REGEX: /\D/g,
    LINE_KEY_SEPARATOR: "\u0000",
    FINGERPRINT_SEPARATOR: ":",
    MODAL_TITLE: "Upload External K-1",
    TIER_SOURCE_ACTION: {
      Created: "Created",
      Updated: "Updated",
      Unchanged: "Unchanged",
    } as const,
    ISSUE: {
      UNMAPPED: "EK1-400-UNMAPPED",
      INLINE_MAPPING: "EK1-400-INLINEMAPPING",
      INVALID_TARGET_LINE: "EK1M-400",
    } as const,
    K1_CLASSIFICATION: "K1",
    ENTITY_LABEL: "External partnership",
    ENTITY_PLACEHOLDER: "Select the external lower-tier partnership",
    NO_EXTERNAL_ENTITIES_MESSAGE: "No client for this head office is marked External — mark one on the Clients screen first.",
    NO_CLIENT_MESSAGE: "Select a client before uploading an external K-1.",
    TAX_YEAR_LABEL: "Tax year",
    YEAR_PLACEHOLDER: "e.g. 2024",
    YEAR_INVALID_MESSAGE: "Enter a four-digit year, e.g. 2024.",
    RECEIVING_LABEL: "Receiving partnership",
    RECEIVING_PLACEHOLDER: "Select the partnership that receives this K-1",
    RECEIVING_PROJECT_LABEL: "Receiving project",
    RECEIVING_PROJECT_PLACEHOLDER: "Select the project this K-1 belongs to",
    NO_RECEIVERS_MESSAGE: (external: string) =>
      `${external} isn't linked to any partnership. Add the relationship under Tiering first.`,
    NO_RECEIVING_PROJECT_MESSAGE: (receiving: string, year: number) =>
      `Create the project for ${receiving} FY${year} first.`,
    CHOOSE_PROJECT_HINT: (receiving: string, count: number, year: number) =>
      `${receiving} has ${count} FY${year} projects. Choose the one this K-1 belongs to.`,
    PDF_PARTNERSHIP_LINE: (name: string | null, id: string | null) =>
      `Issuing partnership on the K-1: ${name ?? "—"}${id ? ` · ${id}` : ""}`,
    PDF_PARTNER_LINE: (name: string | null, id: string | null) =>
      `Partner on the K-1: ${name ?? "—"}${id ? ` · ${id}` : ""}`,
    FILE_LABEL: "K-1 PDF",
    PREVIEW_BUTTON_TEXT: "Preview",
    CONFIRM_BUTTON_TEXT: "Confirm",
    PREVIEWING_TEXT: "Previewing…",
    CONFIRMING_TEXT: "Confirming…",
    BOX_COLUMN_HEADER: "Box",
    CODE_COLUMN_HEADER: "Code",
    AMOUNT_COLUMN_HEADER: "Amount",
    MAPPED_TO_COLUMN_HEADER: "Mapped to",
    STATUS_COLUMN_HEADER: "Status",
    MAPPED_BADGE_LABEL: "Mapped",
    UNMAPPED_BADGE_LABEL: "Unmapped",
    ALL_EMPTY_WARNING: "No K-1 box values were found in this PDF.",
    PREVIEWED_STALE_HINT: "The file, entity, year or receiving partnership changed since the last preview — preview again before confirming.",
    UNMAPPED_LINES_HINT: "Some lines have no mapping yet. Pick a K-1 line for each one; every pick is saved as a mapping for this client.",
    PICKS_REQUIRED_HINT: "Pick a K-1 line for every unmapped line to confirm.",
    UPLOAD_ANOTHER_BUTTON_TEXT: "Upload another",
    ALREADY_CURRENT_MESSAGE: "Same file already uploaded — nothing changed.",
    HISTORY_BUTTON_TEXT: "History",
    HISTORY_MODAL_TITLE: "External K-1 Upload History",
    HISTORY_EMPTY_TEXT: "No uploads yet for this partnership.",
    FILE_NAME_COLUMN_HEADER: "File",
    UPLOADED_AT_COLUMN_HEADER: "Uploaded",
    PICK_LINE_PLACEHOLDER: "Pick a K-1 line…",
    PICK_LINE_UNAVAILABLE_PLACEHOLDER: "K-1 line list unavailable",
    PICK_LINE_ARIA_LABEL: (box: string, code: string | null) => `K-1 line for Box ${box}${code ? ` Code ${code}` : ""}`,
    PICK_SAVED_HINT: "Will be saved as a mapping for this client",
    LINES_UNAVAILABLE_MESSAGE:
      "We couldn't load the K-1 line list, so unmapped lines can't be mapped here right now. Lines that are already mapped are not affected.",
    LINES_RETRY_TEXT: "Try again",
    PDF_YEAR_SAME: (year: number) => `Tax year on the PDF: ${year}`,
    PDF_YEAR_RANGE: (begin: number, end: number) => `Tax year on the PDF: ${begin} to ${end}`,
    PLAN_CREATE_PROJECT: (name: string) => `Will create the project "${name}".`,
    PLAN_REUSE_PROJECT: (name: string) => `Will reuse the project "${name}".`,
    PLAN_SOURCE_CREATED: (external: string, receiving: string, year: number) =>
      `Will add ${external} as a source for ${receiving} FY${year}.`,
    PLAN_SOURCE_UPDATED: (receiving: string, year: number) =>
      `Will complete the existing source for ${receiving} FY${year}.`,
    PLAN_SOURCE_UNCHANGED: (receiving: string, year: number) =>
      `The source for ${receiving} FY${year} is already in place.`,
    RESULT_PROJECT_CREATED: (name: string) => `Created the project "${name}".`,
    RESULT_PROJECT_REUSED: (name: string) => `Reused the project "${name}".`,
    RESULT_SOURCE_CREATED: (external: string, receiving: string, year: number) =>
      `Added ${external} as a source for ${receiving} FY${year}.`,
    RESULT_SOURCE_UPDATED: (receiving: string, year: number) =>
      `Completed the source for ${receiving} FY${year}.`,
    RESULT_SOURCE_UNCHANGED: (receiving: string, year: number) =>
      `The source for ${receiving} FY${year} was already in place.`,
    RESULT_K1_SAVED: (receiving: string, year: number) => `K-1 saved for ${receiving} FY${year}.`,
    RESULT_MAPPINGS_SAVED: (count: number) => `Saved ${count} new mapping${count === 1 ? "" : "s"} for this client.`,
    GENERIC_ERROR_MESSAGE: "Something went wrong — try again.",
    NO_SHARED_FORM_MESSAGE: "No shared K-1 tax form is available.",
  },

  TRIAL_BALANCE_IMPORT: {
    MODAL_TITLE: "Import Trial Balance",
    STEP_UPLOAD_TITLE: "Upload",
    STEP_MAP_TITLE: "Map columns",
    STEP_REVIEW_TITLE: "Review",
    STEP_FINALIZE_TITLE: "Finalize",
    DROP_HINT_TEXT: "Click or drag an .xlsx trial balance file here",
    FILE_NAME_LABEL: "Selected file",
    READING_FILE_TEXT: "Reading file…",
    SHEET_SUMMARY: (sheet: string, rows: number) =>
      `Sheet "${sheet}" — ${rows} data row${rows === 1 ? "" : "s"}`,
    FIELD_ACCOUNT_ID: "Account ID",
    FIELD_ACCOUNT_NAME: "Account name",
    FIELD_ACCOUNT_TYPE: "Account type",
    FIELD_ACCOUNT_CLASS: "Account class",
    FIELD_AMOUNT: "Amount",
    FIELD_TAX_FORM_LINE: "Tax form line",
    FIELD_COLUMN_HEADER: "Standard field",
    FILE_COLUMN_HEADER: "File column",
    SAMPLES_COLUMN_HEADER: "Sample values",
    REQUIRED_MARK: "required",
    NOT_MAPPED_PLACEHOLDER: "Not mapped",
    REQUIRED_FIELDS_HINT: "Map Account ID and Amount to continue.",
    SELECT_ARIA_LABEL: (field: string) => `File column for ${field}`,
    BACK_BUTTON_TEXT: "Back",
    CONTINUE_BUTTON_TEXT: "Continue",
    UPDATING_PREVIEW_TEXT: "Updating preview…",
    IMPORT_BUTTON_TEXT: "Approve and Import",
    IMPORTING_TEXT: "Importing…",
    ROWS_IN_FILE_TEXT: (rows: number) => `${rows} row${rows === 1 ? "" : "s"} in file`,
    ERRORS_COUNT_TEXT: (n: number) => `${n} error${n === 1 ? "" : "s"}`,
    WARNINGS_COUNT_TEXT: (n: number) => `${n} warning${n === 1 ? "" : "s"}`,
    BANNER_ALL_CLEAR: "All clear — no errors or warnings.",
    BANNER_WARNINGS: (n: number) =>
      `${n} warning${n === 1 ? "" : "s"} — you can still import.`,
    BANNER_ERRORS: (errors: number, warnings: number) =>
      `${errors} error${errors === 1 ? "" : "s"}${warnings > 0 ? ` and ${warnings} warning${warnings === 1 ? "" : "s"}` : ""} — fix the file or the mapping. Nothing is saved while errors exist.`,
    DIAG_ROW_HEADER: "Row",
    DIAG_COLUMN_HEADER: "Column",
    DIAG_SEVERITY_HEADER: "Severity",
    DIAG_MESSAGE_HEADER: "Message",
    DOWNLOAD_CSV_TEXT: "Download CSV",
    CSV_FILE_NAME: "trial_balance_diagnostics.csv",
    PREVIEW_ROWS_TITLE: (n: number) => `First ${n} mapped row${n === 1 ? "" : "s"}`,
    NO_PREVIEW_ROWS_TEXT: "No rows to preview.",
    RESULT_CURRENT: (rows: number) =>
      `Trial balance imported — ${rows} row${rows === 1 ? "" : "s"}.`,
    RESULT_NO_CHANGE: "Already imported, no changes",
    RESULT_REJECTED: "Import rejected — nothing was saved.",
    RESULT_SUPERSEDED_NOTE: "This import replaced the earlier upload for this entity.",
    START_OVER_TEXT: "Start over",
    DONE_TEXT: "Done",
    PREVIEW_FAILED_MESSAGE: "Could not read the file — check it is a valid .xlsx and try again.",
    CONFIRM_FAILED_MESSAGE: "The import failed — try again.",
    MISMATCH_RESTART_MESSAGE: "The file changed since it was previewed — upload it again.",
  },

  ACCOUNT_MAPPING: {
    FAILED_TO_LOAD_MESSAGE: "Failed to load account mapping data",
    MAPPINGS_SAVED_MESSAGE: "Mappings saved successfully",
    FAILED_TO_SAVE_MAPPINGS_MESSAGE: "Failed to save mappings",
    SELECT_PARTNERSHIP_TEXT: "Select a partnership to view account mappings.",
    PAGE_TITLE: "CoA Mapping",
    PAGE_SUBTITLE_PREFIX: "Map trial balance accounts to income categories for",
    LOADING_TEXT: "Loading accounts and categories…",
    TRIAL_BALANCE_ACCOUNTS_PANEL_TITLE: "Trial Balance Accounts",
    MAPPED_COUNT_BADGE: (mapped: number, total: number) =>
      `${mapped} of ${total} mapped`,
    ACCOUNT_ID_COLUMN_HEADER: "Account ID",
    ACCOUNT_NAME_COLUMN_HEADER: "Account Name",
    AMOUNT_COLUMN_HEADER: "Amount",
    MAPPED_TO_COLUMN_HEADER: "Mapped To",
    SELECT_CATEGORY_PLACEHOLDER: "— Select category —",
    NO_TRIAL_BALANCE_ACCOUNTS_TEXT:
      "No trial balance accounts found. Upload a trial balance first.",
    INCOME_CATEGORIES_PANEL_TITLE: "Income Categories",
    NO_INCOME_CATEGORIES_TEXT:
      "No income categories found. Assign a template to Income Allocation in the Calculation Profile first.",
    NO_ACCOUNTS_LOADED_TEXT: "No accounts loaded",
    ALL_ACCOUNTS_MAPPED_TEXT: (total: number) => `All ${total} accounts mapped`,
    ACCOUNTS_MAPPED_STATUS_TEXT: (mapped: number, total: number) =>
      `${mapped} of ${total} accounts mapped`,
    SAVING_TEXT: "Saving…",
    SAVE_MAPPINGS_BUTTON_LABEL: "Save Mappings",
  },

  PROJECT_WORKSPACE: {
    GRID_THEME_PARAMS: {
      backgroundColor: "transparent",
      foregroundColor: "#e2e8f0",
      borderColor: "rgba(255,255,255,0.08)",
      oddRowBackgroundColor: "rgba(255,255,255,0.02)",
      headerBackgroundColor: "rgba(26,39,68,0.80)",
      headerTextColor: "#94a3b8",
      rowHoverColor: "rgba(242,101,34,0.08)",
      accentColor: "#F26522",
      fontFamily: "Inter, sans-serif",
      fontSize: 13,
    },
    OUTPUT_GRID_THEME_PARAMS: {
      backgroundColor: "rgba(15,8,0,0.95)",
      foregroundColor: "#e2d5c0",
      borderColor: "rgba(242,101,34,0.15)",
      oddRowBackgroundColor: "rgba(242,101,34,0.04)",
      headerBackgroundColor: "rgba(40,18,0,0.90)",
      headerTextColor: "#F26522",
      rowHoverColor: "rgba(242,101,34,0.08)",
      accentColor: "#F26522",
      fontFamily: "Inter, sans-serif",
      fontSize: 13,
    },
    CALC_GRID_THEME_PARAMS: {
      backgroundColor: "rgba(20,16,0,0.90)",
      foregroundColor: "#e8dcc0",
      borderColor: "rgba(245,158,11,0.18)",
      oddRowBackgroundColor: "rgba(245,158,11,0.04)",
      headerBackgroundColor: "rgba(46,34,0,0.90)",
      headerTextColor: "#F59E0B",
      rowHoverColor: "rgba(245,158,11,0.08)",
      accentColor: "#F59E0B",
      fontFamily: "Inter, sans-serif",
      fontSize: 13,
    },

    DEFAULT_COL_DEF: {
      resizable: true,
      sortable: true,
      filter: true,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      minWidth: 80,
      maxWidth: 400,
    },
    STATUS_BAR: {
      statusPanels: [
        {
          statusPanel: "agTotalAndFilteredRowCountComponent",
          align: "left" as const,
        },
        { statusPanel: "agTotalRowCountComponent", align: "center" as const },
        { statusPanel: "agAggregationComponent" },
      ],
    },

    CALC_COLUMN_DESCRIPTIONS: {
      "asset id": "Identifier for the depreciable asset this row belongs to.",
      "asset description": "Human-readable name of the asset.",
      "asset type":
        "Asset class used to look up the asset's depreciation method/rate.",
      method:
        "Depreciation method applied to this asset (e.g. MACRS, straight-line).",
      convention:
        "Depreciation convention governing the first-year deduction (e.g. half-year, mid-quarter).",
      life: "Recovery period (in years) used to depreciate this asset.",
      "current year boy net tax basis":
        "Net tax basis at the beginning of the current tax year.",
      "current year annual depreciation":
        "Depreciation expense computed for the current tax year.",
      "current year eoy net tax basis":
        "Net tax basis remaining at the end of the current tax year.",
      "sec. 704(c) layer id":
        "Identifier for the Sec. 704(c) built-in gain/loss layer this row allocates.",
      "sec. 704(c) type":
        "Type of Sec. 704(c) layer (e.g. contributed property, revaluation).",
      "current year boy net sec. 704(c) gain/loss":
        "Remaining Sec. 704(c) built-in gain/loss at the start of the year.",
      "current year eoy net sec. 704(c) gain/loss":
        "Remaining Sec. 704(c) built-in gain/loss at the end of the year.",
      "tax depreciation":
        "Tax depreciation carried over from R1 Tax Depreciation for this asset.",
      "sec. 704(c) depreciation":
        "Depreciation attributable to the Sec. 704(c) built-in gain/loss layer.",
      "sec. 704(b) depreciation":
        "Book (Sec. 704(b)) depreciation for this asset, used for capital account maintenance.",
      "sec. 704(c) layer type":
        "Type of Sec. 704(c) layer this basis-allocation row belongs to.",
      "net sec. 704(c) gain/loss":
        "Remaining Sec. 704(c) built-in gain/loss being allocated across partners.",
      ratio:
        "Partner's Sec. 704(b) sharing ratio, used to allocate this layer's basis/cost recovery.",
      "net tax basis assigned":
        "Portion of the asset's net tax basis assigned to this partner.",
      "sec. 704(c) cost recovery":
        "Partner's share of cost recovery attributable to the Sec. 704(c) layer.",
      "tax cost recovery":
        "Partner's total tax cost recovery (depreciation) for this asset layer.",
      "sec. 704(b) cr":
        "Partner's Sec. 704(b) (book) cost recovery for this asset layer.",
    } as Record<string, string>,
    CALC_COLUMN_TOOLTIP_FALLBACK:
      "Intermediate value computed in this calculation step — see the Excel template for the exact formula.",
    CALC_COLUMN_YEAR_TOOLTIP: (year: string) =>
      `Depreciation amount for year ${year} of this asset's recovery schedule.`,

    // Data / workspace load
    WORKSPACE_LOAD_FAILED_MESSAGE: "Failed to load workspace",
    WORKSPACE_LOAD_FAILED_RETRY_TEXT:
      "Failed to load workspace. Please try again.",
    ROWS_SAVED_MESSAGE: "Rows saved",
    SAVE_ROWS_FAILED_MESSAGE: "Failed to save rows",
    SHEET_LOAD_FAILED_MESSAGE: "Failed to load sheet",
    // Scroll loading + edit session
    MORE_ROWS_LOAD_FAILED_MESSAGE: "Failed to load more rows",
    // Server-side sort of Parquet-backed sheets
    SORT_BLOCKED_UNSAVED_MESSAGE: "Save or discard your changes before sorting this sheet.",
    // TC-8.20.13: a saved Sync on the Workspace page replaces the open sheet's rows, so unsaved edits block it.
    SYNC_BLOCKED_UNSAVED_TOOLTIP: "Save or discard your changes before syncing.",
    SORT_FAILED_MESSAGE: "Failed to sort the sheet",
    SHEET_CHANGED_RELOADED_MESSAGE:
      "This sheet was changed by someone else and has been reloaded. Please make your edits again.",
    UNSAVED_CHANGES_TITLE: "Discard unsaved changes?",
    UNSAVED_CHANGES_CONTENT:
      "You have unsaved changes in this sheet. Leaving discards them and releases your edit lock.",
    UNSAVED_CHANGES_OK: "Discard",
    KEY_SHEET_LABEL: "Key sheet",
    LOADING_MORE_ROWS_TEXT: "Loading more rows…",
    ROWS_LOADED_TEXT: (loaded: number, total: number) => `${loaded} of ${total} rows loaded`,
    SAVE_NOT_AVAILABLE_MESSAGE:
      "This sheet has no project deliverable sheet to save to yet",
    NO_TEMPLATE_ASSIGNED_MESSAGE: "No template assigned to this calculation",
    DOWNLOAD_NOT_INPUT_MESSAGE:
      "This sheet takes no input data, so there is no template to download. Select an input sheet.",
    DOWNLOAD_EXAMPLE_FAILED_MESSAGE: "Could not build the template file for this sheet",
    EXPORT_FAILED_MESSAGE: "Export failed",
    ROWS_IMPORTED_MESSAGE: (imported: number) => `${imported} rows imported`,
    RUN_TRIGGER_FAILED_MESSAGE:
      "Failed to trigger calculation. Please try again.",
    RUN_GATE_TITLE: "Run blocked: required input sheets are empty",
    RUN_GATE_OPEN_INPUTS_LABEL: "Open Inputs",
    RUN_GATE_DISMISS_LABEL: "Dismiss",
    JOBS_FAILED_MESSAGE: (count: number) =>
      `${count} calculation${count > 1 ? "s" : ""} failed`,

    // Top bar
    BREAK_PERIOD_SCOPE_TOOLTIP:
      "This workspace is scoped to this break period's own input/output data",
    RESOLVE_ISSUES_TOOLTIP: "Resolve the issues below before running",
    RUNNING_TEXT: "Running...",
    RUN_PROJECT_BUTTON_LABEL: "Run Project",
    PROJECT_NAME_FALLBACK: "—",
    CALCULATIONS_FROM_PARTNERSHIP_TEXT: (
      count: number,
      partnershipName: string,
      taxYear: number | string,
    ) =>
      `${count} calculation${count !== 1 ? "s" : ""} from ${partnershipName} ${taxYear} profile`,

    // Readiness panel
    READY_TO_RUN_BADGE_TEXT: "✓ Ready to run",
    READY_CHECK_FAIL_ICON: "❌",
    READY_CHECK_WARN_ICON: "⚠",

    // Left calculations panel
    CALCULATIONS_PANEL_TITLE: "Calculations",
    EXPAND_CALCULATIONS_TOOLTIP: "Expand calculations (Shift+[)",
    COLLAPSE_CALCULATIONS_TOOLTIP: "Collapse calculations (Shift+[)",
    EXPAND_CALCULATIONS_PANEL_ARIA_LABEL: "Expand calculations panel",
    COLLAPSE_CALCULATIONS_PANEL_ARIA_LABEL: "Collapse calculations panel",
    NO_CALCULATIONS_CONFIGURED_MESSAGE: "No calculations configured",

    // Main area / empty states
    SELECT_CALCULATION_TEXT: "Select a calculation to enter data",
    EMPTY_STATE_OUTPUT_ICON: "⚡",
    RUN_CALCULATION_TO_SEE_RESULTS_TEXT: "Run calculation to see results",
    ENTER_INPUT_DATA_HINT_TEXT: "Enter input data and click Run Project",
    EMPTY_STATE_NO_TEMPLATE_ICON: "📋",
    ASSIGN_TEMPLATE_HINT_TEXT:
      "Assign a template in Configurations → Calc Profile to define columns",
    NO_ROWS_LOADED_TEXT: "No rows loaded. Load data on the Inputs tab.",
    NOT_CALCULATED_TEXT: "Not calculated yet. Run the calculation.",
    VIEW_ONLY_NOTE_TEXT: "View only. Change data on the Inputs tab.",
    ENTITY_PICKER_LABEL: "Entity",
    K_FORM_CLASSIFICATIONS: ["K1", "K2", "K3"],
    K_FORM_LINES_PAGE_SIZE: 200,
    K_FORM_RECIPIENT_HEADER: "Partner",
    K_FORM_ISSUER_HEADER: "Issued by",
    K_FORM_LOAD_FAILED_MESSAGE: "Could not load the stored form lines",

    // Sheet sidebar groups / badges
    INPUT_SHEETS_GROUP_LABEL: "Input Sheets",
    CALCULATION_CHAIN_GROUP_LABEL: "Calculation Chain",
    OUTPUT_SHEETS_GROUP_LABEL: "Output Sheets",
    OUTPUT_BADGE_LABEL: "OUTPUT",
    CALC_BADGE_LABEL: "CALC",
    CHAINED_BADGE_LABEL: "CHAINED",
    CHAINED_SHEET_ICON_TITLE:
      "Automatically populated from another calculation",
    EXPAND_SIDEBAR_TOOLTIP: "Expand sidebar ([)",
    COLLAPSE_SIDEBAR_TOOLTIP: "Collapse sidebar ([)",
    EXPAND_SIDEBAR_ARIA_LABEL: "Expand sidebar",
    COLLAPSE_SIDEBAR_ARIA_LABEL: "Collapse sidebar",

    // Source column (grid)
    SOURCE_COLUMN_HEADER: "Source",
    SOURCE_COLUMN_WIDTH_PX: 110,
    CHAINED_ROW_LABEL: "Chained",
    UPLOADED_ROW_LABEL: "Uploaded",
    MANUAL_ROW_LABEL: "Manual",
    CHAINED_ROW_TOOLTIP: "Populated automatically from another calculation",

    NO_ROWS_OVERLAY_HTML:
      '<div style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:32px 16px;">' +
      '<span style="font-size:13px;color:rgba(255,255,255,0.40);">Upload partner data or click <strong>+ Add Row</strong> to get started</span>' +
      '<span style="font-size:20px;margin-top:4px;">↓</span>' +
      "</div>",
    OUTPUT_NO_ROWS_OVERLAY_HTML:
      '<div style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:32px 16px;">' +
      '<span style="font-size:20px;margin-top:4px;">⚡</span>' +
      '<span style="font-size:13px;color:rgba(242,101,34,0.70);">Run calculation to see results</span>' +
      "</div>",
    CALC_NO_ROWS_OVERLAY_HTML:
      '<div style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:32px 16px;">' +
      '<span style="font-size:20px;margin-top:4px;">🔗</span>' +
      '<span style="font-size:13px;color:rgba(245,158,11,0.75);">Run the project to see intermediate calculation results</span>' +
      "</div>",
    CHAINED_NO_ROWS_DEFAULT_SOURCE_TEXT: "another calculation",
    CHAINED_NO_ROWS_OVERLAY_HTML: (source: string) =>
      '<div style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:32px 16px;">' +
      '<span style="font-size:20px;margin-top:4px;">⛓</span>' +
      `<span style="font-size:13px;color:rgba(167,139,250,0.85);text-align:center;">This sheet is automatically populated from <strong>${source}</strong>.<br/>Run the project to see the chained data.</span>` +
      "</div>",

    // Table actions
    PREPARING_WORK_PAPERS_TEXT: "Preparing work papers...",
    DOWNLOAD_WORK_PAPERS_BUTTON_LABEL: "Download Work Papers",
    WORK_PAPERS_MODAL_TITLE: "Download work papers",
    WORK_PAPERS_MODAL_WIDTH_PX: 720,
    WORK_PAPERS_SELECT_ALL_LABEL: "Select all calculations",
    WORK_PAPERS_SELECTED_COUNT: (selected: number, total: number) => `${selected} of ${total} selected`,
    WORK_PAPERS_INPUT_SHEETS_LABEL: "Input sheets",
    WORK_PAPERS_OUTPUT_SHEETS_LABEL: "Output sheets",
    WORK_PAPERS_NO_INPUT_SHEETS_HINT: "This calculation has no input sheets",
    WORK_PAPERS_NO_OUTPUT_SHEETS_HINT: "This calculation has no output sheets",
    WORK_PAPERS_INCLUDE_FORMULAS_LABEL: "Include formulas",
    WORK_PAPERS_PICK_SHEET_WARNING: "Pick Input sheets, Output sheets or both for each selected calculation.",
    WORK_PAPERS_NO_CALCULATIONS_TEXT: "This project has no calculations to export.",
    WORK_PAPERS_CANCEL_LABEL: "Cancel",
    WORK_PAPERS_DOWNLOAD_LABEL: (count: number) => `Download (${count} ${count === 1 ? "calculation" : "calculations"})`,
    CALCULATING_TEXT: "Calculating...",
    UPLOAD_DATA_TEXT: "Upload Data",

    // Progress modal
    PROGRESS_MODAL_WIDTH_PX: 420,
    RUNNING_CALCULATIONS_TITLE: "Running Calculations",
    JOBS_COMPLETE_TEXT: (completed: number, totalLabel: number | string) =>
      `${completed} of ${totalLabel} complete`,
    ALL_CALCULATIONS_COMPLETE_TEXT: "All calculations complete",
    POLLING_LOST_CONNECTION_TEXT:
      "Lost connection while checking job status — progress shown may be stale.",
    ALL_CALCULATIONS_COMPLETE_CHECK_TEXT: "✓ All calculations complete",
    JOBS_FAILED_HEADER_TEXT: (count: number) =>
      `⚠️ ${count} job${count > 1 ? "s" : ""} failed`,
    JOB_ENTITY_DETAIL_TEXT: (entityId: number | string, detail: string) =>
      `Entity: ${entityId} — ${detail}`,
    JOB_ENTITY_UNKNOWN_LABEL: "Unknown",
    JOB_STATUS_FALLBACK_TEXT: (status: string) => `Status: ${status}`,

    // Upload modal
    IMPORTING_TEXT: "Importing...",
    CLICK_OR_DRAG_XLSX_TEXT: "Click or drag an .xlsx file here",
    SELECTED_FILE_PREFIX_TEXT: (fileName: string) => `Selected: ${fileName}`,

    // Trial balance panel
    TRIAL_BALANCE_BADGE_LABEL: "TRIAL BALANCE",
    TRIAL_BALANCE_VIA_COA_MAPPING_TEXT: (count: number) =>
      `via CoA Mapping — ${count} account(s)`,
    // WorkPapers_{project name}.zip; the project id stands in when the name is not known yet.
    WORK_PAPERS_FILE_NAME: (projectId: string, projectName?: string | null) => {
      const safeName = (projectName ?? "").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
      return `WorkPapers_${safeName || projectId}.zip`;
    },
  },

  BREAK_PERIODS: {
    EVENT_TYPE_OPTIONS: [
      { value: "Contribution", label: "Contribution" },
      { value: "Distribution", label: "Distribution" },
      { value: "Transfer", label: "Transfer of Interest" },
      { value: "Revaluation", label: "Revaluation" },
    ],
    TIMELINE: {
      ARIA_LABEL: (taxYear: number) => `FY${taxYear} timeline`,
      ITEM_ARIA_LABEL: (name: string, status: string) => `${name}, ${status}`,
      ITEM_TOOLTIP: (name: string, dates: string, status: string) => `${name} (${dates}) - ${status}`,
      DATE_RANGE_TEXT: (start: string, end: string) => `${start} - ${end}`,
      LABEL_DATES_TEXT: (dates: string) => `(${dates})`,
      EVENT_MARKER_TEXT: "E",
      STATUS_CALCULATED: "Calculated",
      STATUS_FAILED: "Failed",
      STATUS_NOT_CALCULATED: "Not calculated",
    },
    OPEN_WORKSPACE_BUTTON_LABEL: "Open Workspace",
    OPEN_WORKSPACE_ARIA_LABEL: (name: string) => `Open workspace for ${name}`,
    TABLE_ARIA_LABEL: "Break periods",
    COLUMN_NAME_LABEL: "Break Period",
    COLUMN_START_DATE_LABEL: "Start date",
    COLUMN_END_DATE_LABEL: "End date",
    COLUMN_RUN_STATUS_LABEL: "Run status",
    COLUMN_INPUT_STATUS_LABEL: "Input status",
    COLUMN_ACTIONS_LABEL: "Actions",
    DATE_DISPLAY_FORMAT: "MMM D, YYYY",
    EMPTY_VALUE_TEXT: "—",
    LOADING_PLACEHOLDER_TEXT: "…",
    RUN_STATUS_LABELS: {
      NOT_STARTED: "Not started",
      QUEUED: "Queued",
      PROCESSING: "Processing",
      COMPLETED: "Completed",
      FAILED: "Failed",
      BLOCKED: "Blocked",
    },
    INPUT_STATUS_READY_LABEL: "Ready",
    INPUT_STATUS_MISSING_LABEL: (count: number) => `${count} missing`,
    FAILED_TO_LOAD_MESSAGE: "Failed to load break periods",
    BACK_TO_PROJECT_BUTTON_LABEL: "Back to Project",
    PAGE_TITLE: "Break Periods",
    ADD_BREAK_PERIOD_BUTTON_LABEL: "Add Break Period",
    NO_BREAK_PERIODS_TEXT:
      "No break periods defined. Add a break period to split the tax year.",
    NO_BREAK_PERIODS_INPUTS_TEXT:
      "No break periods yet. Add a break period to sync and load this project's inputs; the calculation runs once one exists.",
    OPEN_WORKSPACE_NO_PERIOD_BUTTON_LABEL:
      "Open Workspace (whole tax year, no break period)",
    FAILED_TO_LOAD_CALCULATIONS_MESSAGE: "Failed to load calculations",
    BREAK_PERIOD_UPDATED_MESSAGE: "Break period updated",
    BREAK_PERIOD_ADDED_MESSAGE: "Break period added",
    FAILED_TO_SAVE_BREAK_PERIOD_MESSAGE: "Failed to save break period",
    CALCULATIONS_SAVED_MESSAGE: "Calculations saved",
    FAILED_TO_SAVE_CALCULATIONS_MESSAGE: "Failed to save calculations",
    EDIT_BREAK_PERIOD_MODAL_TITLE: "Edit Break Period",
    ADD_BREAK_PERIOD_MODAL_TITLE: "Add Break Period",
    SELECT_CALCULATIONS_MODAL_TITLE: "Select Calculations",
    NEXT_BUTTON_LABEL: "Next",
    NAME_LABEL: "Name",
    NAME_REQUIRED_MESSAGE: "Name is required",
    NAME_PLACEHOLDER: "e.g. Pre-Contribution Period",
    TYPE_LABEL: "Type",
    PERIOD_TYPE_LABEL: "Period",
    EVENT_TYPE_LABEL: "Event",
    DATE_RANGE_LABEL: "Date Range",
    DATE_RANGE_REQUIRED_MESSAGE: "Start and end date are required",
    END_DATE_AFTER_START_MESSAGE: "End date must be after start date",
    EVENT_DATE_LABEL: "Event Date",
    EVENT_DATE_REQUIRED_MESSAGE: "Event date is required",
    EVENT_TYPE_FIELD_LABEL: "Event Type",
    EVENT_TYPE_REQUIRED_MESSAGE: "Event type is required",
    SELECT_EVENT_TYPE_PLACEHOLDER: "Select event type",
    SELECT_CALCULATIONS_TEXT:
      "Select which standard calculations apply to this break period.",
    NO_CALCULATIONS_AVAILABLE_TEXT:
      "No calculations are available for this break period type.",
  },

  CASCADE: {
    PROGRESS_COLORS: {
      COMPLETE: "#4CAF50",
      IN_PROGRESS: "#F26522",
      NOT_STARTED: "rgba(255,255,255,0.20)",
    },
    K1_OUTPUT_AVAILABLE_LABEL: "K-1 Output available",
    CALCULATION_RUNNING_LABEL: "Calculation running",
    CALCULATION_COMPLETE_LABEL: "Calculation complete",
    CALCULATION_FAILED_LABEL: "Calculation failed",
    NOT_STARTED_LABEL: "Not started",
    WAITING_FOR_PROJECT_SETUP_LABEL: "Waiting for project setup",
    FLOWING_LABEL: "Flowing",
    PENDING_LABEL: "Pending",
    SUB_FUNDS_LABEL: (count: number) =>
      `${count} sub-fund${count !== 1 ? "s" : ""}`,
    PARTNERS_LABEL: (count: number) =>
      `${count} partner${count !== 1 ? "s" : ""}`,
    ENTITIES_LABEL: (count: number) =>
      `${count} ${count === 1 ? "entity" : "entities"}`,
    K1_TO_INCOME_INPUT_TEXT: "K-1 → Income Input",
    ROWS_SUFFIX: (count: number) => `${count} rows`,
    TIER_BADGE_LABEL: (level: number) => `Tier ${level}`,
    NODE_TOOLTIP_MORE_LINE: (count: number) => `+${count} more`,
    CALCS_COMPLETE_LABEL: (completed: number, total: number) =>
      `${completed}/${total} complete`,
    VIEW_PROJECT_BUTTON_LABEL: "View Project",
    VIEW_MAIN_PROJECT_BUTTON_LABEL: "View Main Project",
    CREATE_PROJECT_BUTTON_LABEL: "Create Project",
    VIEW_K1_OUTPUT_BUTTON_LABEL: "View K-1 Output",
    SELECT_PARTNERSHIP_TEXT: "Select a partnership to view the tier flow.",
    PAGE_TITLE: (partnershipName: string) => `${partnershipName} — Tier Flow`,
    RUN_TIER_FLOW_BUTTON_LABEL: "Run Tier Flow",
    NO_TIER_STRUCTURE_TEXT: "No tier structure found for this partnership.",
    TIER_NAV_PILL_TITLE: (level: number, count: number) =>
      `Tier ${level} — ${count} ${count === 1 ? "entity" : "entities"}`,
    CLOSE_BUTTON_LABEL: "Close",
    RUN_ORDER_TITLE: "Run order",
    RUN_ORDER_HINT:
      "Tiers run lowest first; each tier pulls the saved K-1 of the tiers below it. A failed tier skips only the tiers that depend on it.",
    RUN_PLAN_LOAD_FAILED_MESSAGE: "Could not load the run plan for this partnership.",
    RUN_PREPARE_FAILED_MESSAGE: "Could not save the default tier sources before running.",
    START_RUN_BUTTON_LABEL: "Start run",
    CANCEL_BUTTON_LABEL: "Cancel",
    EXTERNAL_NOT_CALCULATED_LABEL: "External — not calculated",
    K1_NEEDED_NOTE: "K-1 needed",
    EXTERNAL_K1_UPLOADED_NOTE: "Uploaded K-1 will be used",
    NO_PROJECT_FAILURE_REASON: "No project set up for this tier",
    TIER_STATUS_QUEUED_LABEL: "Queued",
    TIER_STATUS_RUNNING_LABEL: "Running",
    TIER_STATUS_COMPLETED_LABEL: "Completed",
    TIER_STATUS_FAILED_LABEL: "Failed",
    TIER_STATUS_SKIPPED_LABEL: (dependsOn: string) => `Skipped — depends on ${dependsOn}`,
    TIER_START_FAILED_REASON: "Failed to start the calculation",
    RUN_GATE_TITLE: "Run blocked: required input sheets are empty",
    TIER_NOT_COMPLETE_REASON: "Calculation did not complete",
    RUN_COMPLETE_TEXT: "Tier flow complete.",
    OPEN_PROJECT_LINK_LABEL: "Open project",
    OPEN_PROJECT_LINK_ARIA: (name: string) => `Open the project of ${name}`,
    RUN_COMPLETE_WITH_FAILURES_TEXT: "Tier flow finished with failures.",
    STALE_REASON_INPUT_CHANGED: "Inputs changed since the last run",
    STALE_REASON_UPSTREAM: (name: string) => `${name} changed`,
    STALE_REASON_NO_K1: (name: string) => `${name} has no K-1 yet`,
    STALE_REASON_NEWER_K1: (name: string) => `${name} has a newer K-1`,

    TIER_SOURCES_BUTTON_LABEL: "Tier Sources",
    TIER_SOURCES_MODAL_TITLE: "Tier Sources",
    TIER_SOURCES_EMPTY_TEXT: "No lower-tier partnerships issue K-1s to this entity.",
    TIER_SOURCES_DEFAULTED_HINT:
      "No sources saved yet — showing the partnerships that issue K-1s to this entity as a suggested default.",
    TIER_SOURCES_SAVE_BUTTON_LABEL: "Save Sources",
    TIER_SOURCES_SAVED_MESSAGE: "Tier sources saved.",
    TIER_SOURCES_SAVE_FAILED_MESSAGE: "Failed to save tier sources.",
    NO_PROJECT_LABEL: "No project",
    STALE_BADGE_LABEL: "Stale — source(s) updated",
    RERUN_BUTTON_LABEL: "Rerun",
  },

  STRUCTURE: {
    VIEW_MODE: { DIAGRAM: "Diagram", TABLE: "Table" } as const,
    PAGE_TITLE_TEXT: (clientName: string) => `${clientName} — Structure`,
    ENTITIES_FAILED_MESSAGE: "Entity list failed to load. ",
    RELATIONSHIPS_FAILED_MESSAGE:
      "Ownership relationships failed to load — entities are shown without ownership lines. ",
    PROJECTS_FAILED_MESSAGE:
      "Project status failed to load — entities are shown without status chips.",
    PROJECTS_PAGE_SIZE: 1000,
    SHAPE_GLYPH_SIZE_PX: 18,
    DEFAULT_SHAPE: "rectangle" as const,
    OWNER_PERCENT_TEXT: (percent: string) => ` (${percent})`,
    NODE_ACTIVATE_ARIA_LABEL: (name: string, status: string) =>
      `${name}, ${status}. Open details.`,
    STATUS_KEYWORD: {
      COMPLETE: "complete",
      PROGRESS: "progress",
      FLOW: "flow",
      FAIL: "fail",
      ERROR: "error",
      BLOCK: "block",
    } as const,
    NO_TIER_FILTER_VALUE: "none" as const,
    NO_PROJECT_FILTER_VALUE: "__no_project__",
    BUSY_STRUCTURE_SKIP_LEVEL_SHARE_THRESHOLD: 0.15,
    BUSY_STRUCTURE_ENTITY_COUNT_THRESHOLD: 100,
    MAX_HEADING_APEX_NAMES: 3,
    VIEW_MODE_DIAGRAM_LABEL: "Diagram",
    VIEW_MODE_TABLE_LABEL: "Table",
    ISOLATED_BAND_TITLE: "Not linked to any entity",
    STATUS_UNKNOWN_LABEL: "Unknown",
    NO_CLIENT_SELECTED_TEXT: "Select a client to view its structure.",
    NO_ENTITIES_FOR_CLIENT_TEXT: "No entities found for this client.",
    ENTITY_TYPE_FALLBACK_LABEL: "Entity",
    ENTITY_NAME_FALLBACK_LABEL: (id: number) => `Entity ${id}`,
    HEADING_NAME_SEPARATOR_TEXT: " & ",
    HEADING_WITH_MORE_TEXT: (names: string, more: number) => `${names} + ${more} more`,
    BUSY_STRUCTURE_NOTE:
      "Large or complex structure, shown as a table. Switch to Diagram to see the full picture.",
    SEARCH_PLACEHOLDER: "Search by entity name",
    SEARCH_ARIA_LABEL: "Search entities by name",
    EMPTY_DASH: "\u2014",
    NO_MATCHING_ENTITIES_TEXT: "No entities match the current search and filters.",
    COLUMN_ENTITY: "Entity",
    COLUMN_TYPE: "Type",
    COLUMN_TIER: "Tier",
    COLUMN_OWNED_BY: "Owned by",
    COLUMN_OWNS: "Owns",
    COLUMN_PROJECT: "Project",
    COLUMN_ACTION: "Action",
    PAGE_SIZE: 100,
    TIER_LABEL: (level: number) => `Tier ${level}`,
    EXTERNAL_BADGE_LABEL: "External",
    OWNS_LINE: (percent: string, ownedName: string) => `Owns ${percent} of ${ownedName}`,
    OWNED_BY_LINE: (percent: string, ownerName: string) => `${percent} owned by ${ownerName}`,
    OPEN_PROJECT_ARIA_LABEL: (name: string) => `Open project for ${name}`,
  },

  GENERATE_TAX_FORM: {
    VIEW: {
      SUMMARY: "summary",
      LINES: "lines",
    } as const,
    RECIPIENT_STATUS: {
      READY: "Ready",
      ERROR: "Error",
      NO_RECORD: "NoRecord",
    } as const,
    RECIPIENT_KIND: {
      READY: "ready",
      ERROR: "error",
      NO_RECORD: "noRecord",
      OTHER: "other",
    } as const,
    AVAILABILITY_STATUS: {
      IDLE: "idle",
      LOADING: "loading",
      READY: "ready",
      FAILED: "failed",
    } as const,
    HTTP_STATUS: {
      BAD_REQUEST: 400,
      FORBIDDEN: 403,
      NOT_FOUND: 404,
    } as const,
    PERIOD_REQUIRED_ISSUE_CODE: "K1E-400",
    FISCAL_YEAR_PREFIX: "FY",
    API_ERROR_MESSAGE: "API error",
    KEY_SEPARATOR: "|",
    COLUMN_KEY_SEPARATOR: ":",
    GROUP_LABEL_TEXT: (name: string, shortId: string) => `${name} · ${shortId}`,
    BREAK_PERIOD_SHORT_ID_LENGTH: 8,
    CONTENT_DISPOSITION: {
      FILENAME_STAR_REGEX: /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i,
      FILENAME_PLAIN_REGEX: /filename\s*=\s*("([^"]+)"|[^;]+)/i,
      SURROUNDING_QUOTES_REGEX: /^"|"$/g,
    },
    PARTNER_GRID_DEFAULT_COL_DEF: {
      resizable: true,
      sortable: true,
      filter: true,
    },
    STATUS_BAR: {
      statusPanels: [
        { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" as const },
        { statusPanel: "agTotalRowCountComponent", align: "center" as const },
      ],
    },
    PARTNER_COLUMN_MIN_WIDTH_PX: 200,
    ENTITY_TYPE_COLUMN_WIDTH_PX: 130,
    OWNERSHIP_COLUMN_WIDTH_PX: 160,
    INCOME_COLUMN_MIN_WIDTH_PX: 180,
    STATUS_COLUMN_WIDTH_PX: 180,
    ACTIONS_COLUMN_WIDTH_PX: 280,
    PARTNER_ROW_HEIGHT_PX: 48,
    GRID_HEADER_HEIGHT_PX: 36,
    OWNERSHIP_PERCENT_DECIMALS: 4,
    SKELETON_ROWS: 6,
    PREVIEW_MODAL_WIDTH_PERCENT: "80%",
    PARTNER_ID_TEXT: (id: number | string | null | undefined) => `ID-${id ?? ""}`,
    PERCENT_SUFFIX_TEXT: "%",
    GRID_THEME_PARAMS: {
      backgroundColor: "transparent",
      foregroundColor: "rgba(255,255,255,0.85)",
      borderColor: "rgba(255,255,255,0.08)",
      oddRowBackgroundColor: "rgba(255,255,255,0.015)",
      headerBackgroundColor: "var(--ce-surface-dark)",
      headerTextColor: "var(--ce-text-secondary)",
      rowHoverColor: "rgba(255,255,255,0.025)",
      accentColor: "#F26522",
      fontFamily: "Inter, sans-serif",
      fontSize: 13,
    },
    GOSYSTEM_EXPORT_FILE_NAME: (projectId: number) =>
      `GoSystem_PartnerPackage_${projectId}.xml`,
    EXPORT_FAILED_MESSAGE: "Export failed — please try again",
    // TC-PRJ-12.2 (FR-PRJ-12, OQ-18): K-1 PDF, K-1 ZIP and GoSystems XML are disabled for Test projects (same text as the API's 409).
    TEST_PROJECT_TYPE: "Test",
    TEST_FILING_BLOCKED_TOOLTIP:
      "Test projects can't be filed. Change the status to Active to generate K-1s or export to GoSystems.",
    FAILED_TO_LOAD_PREVIEW_MESSAGE: "Failed to load K-1 preview",
    K1_PDF_FILE_NAME: (partnerName: string) =>
      `K1_${partnerName.replace(/ /g, "_")}_2024.pdf`,
    PARTNER_COLUMN_HEADER: "PARTNER",
    ENTITY_TYPE_COLUMN_HEADER: "ENTITY TYPE",
    ENTITY_TYPE_VALUE: "LP",
    OWNERSHIP_PERCENT_COLUMN_HEADER: "OWNERSHIP %",
    INCOME_ALLOCATED_COLUMN_HEADER: "INCOME ALLOCATED",
    STATUS_COLUMN_HEADER: "STATUS",
    READY_TO_GENERATE_BADGE_TEXT: "Ready to generate",
    ACTIONS_COLUMN_HEADER: "ACTIONS",
    PREVIEW_K1_BUTTON_LABEL: "Preview K-1",
    SELECT_CLIENT_PARTNERSHIP_TEXT:
      "Select a client and partnership to generate K-1s.",
    NO_PROJECT_FOUND_TEXT: (
      partnershipName: string | undefined,
      taxYear: string,
    ) =>
      `No project found for ${partnershipName} · ${taxYear}. Create a project first.`,
    PROJECT_NOT_FOUND_TEXT: "This project could not be found.",
    NO_CALCULATION_RESULTS_TEXT:
      "No calculation results found. Run calculations from the Project screen first.",
    PAGE_TITLE: "Generate Tax Form",
    PAGE_SUBTITLE: (partnershipName: string, taxYear: string) =>
      `Schedule K-1 · ${partnershipName} · ${taxYear}`,
    SUMMARY_FORM_LABEL: "Schedule K-1 (Form 1065)",
    PARTNERS_STAT_LABEL: "PARTNERS",
    K1S_READY_STAT_LABEL: "K-1S READY",
    PARTNER_COUNT_TEXT: (count: number) =>
      `${count} partner${count !== 1 ? "s" : ""}`,
    PREPARING_EXPORT_TEXT: "Preparing export...",
    EXPORT_GOSYSTEM_XML_BUTTON_LABEL: "Export GoSystem XML",
    GENERATE_ALL_K1S_BUTTON_LABEL: "Generate all K-1s",
    K1_PREVIEW_MODAL_TITLE: (partnerName: string) =>
      `K-1 Preview — ${partnerName}`,
    DOWNLOAD_PDF_BUTTON_LABEL: "Download PDF",
    LOADING_K1_TEXT: "Loading K-1...",
    K1_PREVIEW_IFRAME_TITLE: "K-1 Preview",
    STATUS_ERROR_BADGE_TEXT: "Error",
    STATUS_NO_RECORD_BADGE_TEXT: "No K-1 record",
    NO_DIAGNOSTICS_TEXT: "No details were returned.",
    PREVIEW_UNAVAILABLE_TOOLTIP: "No K-1 can be shown for this partner until its status is Ready.",
    PREVIEW_FAILED_WITH_REASON: (reason: string) => `Failed to load K-1 preview: ${reason}`,
    GENERATING_ZIP_TEXT: "Preparing K-1s...",
    ZIP_FILE_NAME: (partnershipName: string, taxYear: string) =>
      `K1s_${partnershipName.replace(/[ ,]/g, "")}_${taxYear.replace("FY", "")}.zip`,
    ZIP_DOWNLOADED_MESSAGE: (count: number) =>
      `${count} K-1${count !== 1 ? "s" : ""} downloaded`,
    ZIP_PARTIAL_MESSAGE: (included: number, left: number) =>
      `${included} K-1${included !== 1 ? "s" : ""} downloaded; ${left} left out (see _errors.txt in the ZIP)`,
    ZIP_FAILED_MESSAGE: "The K-1s could not be generated",
    BREAK_PERIOD_PICKER_LABEL: "Break period",
    BREAK_PERIOD_PICKER_PLACEHOLDER: "Choose a break period",
    BREAK_PERIOD_REQUIRED_TEXT:
      "This project has several break periods. Choose the one to show K-1s for.",
    BREAK_PERIOD_LOAD_FAILED_MESSAGE: "The break periods could not be loaded",
    PARTNER_LIST_FAILED_MESSAGE: "The K-1 partner list could not be loaded",
    VIEW_SWITCH_ARIA_LABEL: "K-1 view",
    VIEW_PARTNER_SUMMARY_LABEL: "Partner summary",
    VIEW_LINE_DETAIL_LABEL: "K-1 line detail",
    LINE_DETAIL: {
      ROW_HEIGHT_PX: 40,
      COLUMN_MIN_WIDTH_PX: 150,
      LABEL_SEPARATOR_TEXT: " — ",
      TOOLTIP_SEPARATOR_TEXT: " · ",
      CELL_TEXT_VALUE_TEXT: (amount: string, textValue: string) => `${amount} (${textValue})`,
      PAGE_SIZE: 50,
      DEFAULT_COL_DEF: {
        resizable: true,
        enableValue: false,
        enableRowGroup: false,
        enablePivot: false,
      },
      ERROR_TEXT: "The K-1 line detail could not be loaded.",
      RETRY_LABEL: "Retry",
      EMPTY_TEXT:
        "No K-1 line values yet. Run the calculation from the Project screen first.",
      NO_MAPPED_LINES_TEXT: "The K-1 records have no mapped lines to show.",
      ERROR_RECORDS_TITLE: (count: number) =>
        `${count} K-1 record${count !== 1 ? "s" : ""} could not be built`,
      ERROR_RECORD_NO_MESSAGE_TEXT: "No message was recorded.",
      ERROR_RECORDS_TRUNCATED_TEXT:
        "More error records exist than are listed here.",
      CONFLICTS_TEXT: (count: number) =>
        `${count} stored value${count !== 1 ? "s" : ""} repeat${count !== 1 ? "" : "s"} a line already shown for the same partner and ${count !== 1 ? "are" : "is"} not displayed.`,
      UNKNOWN_LINE_HEADER: "Line no longer on the form",
      INACTIVE_LINE_SUFFIX: " (inactive)",
      GRID_ARIA_LABEL: "K-1 line values per partner",
      PARTNER_COUNT_TEXT: (shown: number, total: number) =>
        `Showing ${shown} of ${total} partner${total !== 1 ? "s" : ""}`,
    },
    FORM_DOWNLOAD: {
      NOT_SET_UP_CODE_SUFFIX: "E-404",
      FIELDS_CODE_SUFFIX: "E-409-FIELDS",
      NO_RECORD_CODE_SUFFIX: "E-409",
      UNKNOWN_PARTNER_TEXT: "this partner",
      PROBE_MAX_PAGES: 200,
      K2_BUTTON_LABEL: "Download K-2",
      K3_BUTTON_LABEL: "Download K-3",
      K2_PREPARING_TEXT: "Preparing K-2...",
      K3_NO_RECORD_TOOLTIP: "No K-3 for this partner yet.",
      K2_DOWNLOADED_MESSAGE: "K-2 downloaded",
      K3_DOWNLOADED_MESSAGE: (partnerName: string) => `K-3 downloaded for ${partnerName}`,
      K3_FILE_NAME: (partnerName: string, taxYear: string) =>
        `K3_${partnerName.replace(/[ ,]/g, "_")}_${taxYear.replace("FY", "")}.pdf`,
      K2_FILE_NAME: (partnershipName: string, taxYear: string) =>
        `K2_${partnershipName.replace(/[ ,]/g, "")}_${taxYear.replace("FY", "")}.pdf`,
      NO_RECORD_MESSAGE: {
        K2: "There is no K-2 for this project yet. Run the calculation, then try again.",
        K3: (partnerName: string) =>
          `There is no K-3 for ${partnerName} yet. Run the calculation for this project, then try again.`,
      },
      NOT_SET_UP_MESSAGE: (form: string, year: string) =>
        `The ${form} form for ${year} has not been set up. Ask an administrator to add it in Tax Form Lines.`,
      FIELDS_MESSAGE: (form: string) =>
        `The ${form} could not be produced because the form template is missing fields that are mapped. Ask an administrator to check the form mapping.`,
      ACCESS_MESSAGE: "You don't have access to this client's forms.",
      PERIOD_REQUIRED_MESSAGE:
        "This project has several break periods. Choose one above, then try again.",
      FAILED_MESSAGE: (form: string) => `The ${form} could not be downloaded. Try again.`,
    },
    FORM_SELECTOR: {
      FORM_KIND: { K1: "K1", K2: "K2", K3: "K3" } as const,
      TAX_YEAR_REGEX: /(\d{4})/,
      ARIA_LABEL: "Tax form",
      LABELS: { K1: "K-1", K2: "K-2", K3: "K-3" },
      FORM_KINDS: ["K1", "K2", "K3"] as const,
      NO_MAPPED_TEXT:
        "No lines on this form are mapped to a calculation output yet. Map them in Settings, Tax Form Lines.",
      NO_RESULTS_TEXT: (label: string) =>
        `No ${label} values yet. Run the calculation from the Project screen first.`,
      NO_MAPPED_LINES_TEXT: (label: string) => `The ${label} records have no mapped lines to show.`,
      LINE_DETAIL_ERROR_TEXT: (label: string) => `The ${label} line detail could not be loaded.`,
      RECIPIENTS_STAT_LABEL: { K2: "Records", K3: "Partners with a K-3" },
      DOWNLOADS_TITLE: "PDF",
      PREVIEW_BUTTON_LABEL: "Preview",
      PREVIEW_TITLE: (label: string, name: string) => `${label}${name ? ` - ${name}` : ""}`,
      ISSUER_ROW_TEXT: (name: string) => `${name} (this partnership)`,
      NO_PARTNERS_TEXT: "No partners have a K-3 for this project yet.",
      ADMIN_ONLY_TEXT: "PDF downloads of this form are for tax form administrators.",
    },
    TIE_OUT: {
      TITLE: "Template tie-out",
      NOTE: "Figures and status are calculated by the template and shown as returned.",
      ARIA_LABEL: "Template tie-out",
      NO_VALUE_TEXT: "No value",
      COLUMNS: {
        CODE: "Code",
        LABEL: "Label",
        ENTITY: "Entity",
        VALUE: "Value",
        CONTROL: "Control",
        VARIANCE: "Variance",
        STATUS: "Status",
        ISSUE: "Issue",
      },
    },
  },

  PROJECT: {
    ENGAGEMENT_NAME_COLUMN_HEADER: "Engagement Name",
    TIER_LABELS: {
      1: "Taxable Income",
      2: "Income Allocation",
      3: "Capital Account Roll-forward",
      4: "Period Depreciation (MACRS)",
      5: "Debt Allocation",
      6: "State Apportionment",
      7: "Contribution Event",
    } as Record<number, string>,
    STEP_FALLBACK_LABEL: (index: number) => `Step ${index}`,
    PERIOD_DATE_FORMAT: "MMM D, YYYY",
    RESULTS_COLLAPSE_THEME: {
      token: {
        colorText: "rgba(255,255,255,0.85)",
        colorTextHeading: "#ffffff",
        colorBorder: "rgba(255,255,255,0.08)",
        colorBorderSecondary: "rgba(255,255,255,0.08)",
      },
      components: {
        Collapse: {
          headerBg: "rgba(26,39,68,0.82)",
          contentBg: "rgba(26,39,68,0.50)",
        },
      },
    },
    K1_LINE_ITEMS: [
      { code: "K1_L1", line: "Line 1", label: "Ordinary Business Income" },
      { code: "K1_L5", line: "Line 5", label: "Interest Income" },
      { code: "K1_L9A", line: "Line 9a", label: "Net LT Capital Gain" },
      { code: "K1_L13", line: "Line 13", label: "Depreciation" },
      { code: "K1_L16", line: "Line 16", label: "Foreign Tax Credit" },
      { code: "K1_L20", line: "Line 20", label: "Ending Capital" },
    ],
    STATUS_COLORS: {
      Completed: "#22c55e",
      Archived: "#94a3b8",
    } as Record<string, string>,
    DEFAULT_STATUS_COLOR: "#f26522",
    ACTIVE_STATUS_LABEL: "Active",
    FAILED_TO_LOAD_PROJECT_MESSAGE: "Failed to load project",
    ALL_CALCULATIONS_COMPLETE_MESSAGE: "All calculations complete",
    DELETE_PROJECT_CONFIRM_TITLE: (name: string) => `Delete project "${name}"?`,
    DELETE_PROJECT_CONFIRM_CONTENT:
      "This cannot be undone. The project will be archived and removed from this view.",
    DELETE_FROM_LIST_CONFIRM_TITLE: (name: string) => `Delete "${name}"?`,
    DELETE_FROM_LIST_CONFIRM_CONTENT: "This cannot be undone.",
    PROJECT_DELETED_MESSAGE: "Project deleted",
    FAILED_TO_DELETE_PROJECT_MESSAGE: "Failed to delete project",
    PROJECT_NAME_COLUMN_HEADER: "Project Name",
    TAX_YEAR_COLUMN_HEADER: "Tax Year",
    STATUS_COLUMN_HEADER: "Status",
    PROJECTS_PAGE_TITLE: "Projects",
    // TC-PRJ-5.1: Head office = the Client record, Client = the partnership Entity (labels only).
    SELECT_PARTNERSHIP_TEXT: "Select the client",
    SCOPE_PICKER_ARIA_LABEL: "Change head office, client and tax year",
    SCOPE_CLIENT_LABEL: "Head office",
    SCOPE_PARTNERSHIP_LABEL: "Client",
    SCOPE_CHIP_TOOLTIP_TEXT: (label: string, value: string) => `${label}: ${value}`,
    SCOPE_CLIENT_PLACEHOLDER: "Select head office...",
    SCOPE_PARTNERSHIP_PLACEHOLDER: "Select client...",
    PARTNERSHIP_ENTITY_TYPE: "Partnership",

    NEW_PROJECT_BUTTON_LABEL: "New Project",
    NO_PROJECTS_TEXT:
      "No calculation projects for this client and tax year.",
    CREATE_PROJECT_BUTTON_LABEL: "Create Project",
    NO_CALCULATIONS_CONFIGURED_TEXT:
      "No calculations configured for this project.",
    CALCULATION_SEQUENCE_LABEL: "Calculation Sequence",
    ROWS_SUFFIX: "rows",
    FAILED_TEXT: "Failed",
    RUN_CALCULATIONS_TO_SEE_RESULTS_TEXT:
      "Run calculations to see results here.",
    OUTPUT_LINKS_TITLE: "Calculation outputs",
    OPEN_OUTPUT_LABEL: "Open output",
    OUTPUT_LINK_ENTITY_FALLBACK_LABEL: "Entity",
    CALCULATIONS_COMPLETED_TEXT: (count: number) =>
      `${count} calculations completed`,
    ALL_COMPLETE_CHECK_TEXT: "All Complete ✓",
    OUTPUT_ROWS_LABEL: "Output Rows",
    OUTPUT_ROWS_ALL_ENTITIES_LABEL: "Output Rows (all entities)",
    ENTITIES_LABEL: "Entities",
    PARTNERS_LABEL: "Partners",
    INCOME_ALLOCATED_LABEL: "Income Allocated",
    TAX_YEAR_LABEL: "Tax Year",
    GENERATE_K1S_WITH_COUNT_BUTTON_LABEL: (count: number) =>
      `Generate ${count} K-1s →`,
    GENERATE_K1S_BUTTON_LABEL: "Generate K-1s →",
    K1_ALLOCATION_SUMMARY_TITLE: "K-1 Allocation Summary",
    HEADLINE_LINE_CODE: "K1_L1",
    NO_TOTAL_TEXT: "No total",
    TIE_OUT_PAGE_SIZE: 1,
    SELECT_CLIENT_PARTNERSHIP_TEXT:
      "Select a head office and client to view calculations.",
    BACK_TO_PROJECTS_BUTTON_LABEL: "Back to Projects",
    BREADCRUMB_ARIA_LABEL: "Breadcrumb",
    PROJECT_NOT_FOUND_TITLE: "Project Not Found",
    PROJECT_NOT_FOUND_TEXT: "This project could not be found.",
    PROJECT_SELECTOR_LABEL: "PROJECT",
    DELETE_BUTTON_LABEL: "Delete",
    BREAK_PERIODS_TAB_LABEL: "Break Periods",
    CALCULATIONS_TAB_LABEL: "Calculations",
    RESULTS_TAB_LABEL: "Results",
    PROJECT_CODE_PREFIX: (id: string) => `PRJ-${id.slice(0, 8).toUpperCase()}`,
    TAX_YEAR_META_TEXT: (taxYear: number | string) => `Tax Year ${taxYear}`,
    // TC-8.20.7: three tabs and the run controls in the project header.
    // TC-PRJ-10.2: tabs read Break Periods then Tax Forms (URL keys unchanged).
    BREAK_PERIOD_TAB_LABEL: "Break Periods",
    K1_TAX_FORM_TAB_LABEL: "Tax Forms",
    READINESS_CHECK_LINE: (name: string, detail: string) => (detail ? `${name}: ${detail}` : name),
    RUN_FAILED_COUNT_TEXT: (count: number) => `${count} failed`,
    RUN_POLL_INTERVAL_MS: 3000,
    RUN_POLL_MAX_ATTEMPTS: 600,
    // TC-PRJ-10.1: header subtitle "{Client} · {Tax year} · {Status}" and the Test badge (FR-PRJ-30, FR-PRJ-35).
    HEADER_SUBTITLE_SEPARATOR: " · ",
    TEST_BADGE_TEXT: "Test",
    PROJECT_TYPES: { ACTIVE: "Active", TEST: "Test" },
    TAX_YEAR_DISPLAY: (taxYear: number | string) => `FY${String(taxYear).replace(/^FY/i, "")}`,
    TAX_YEAR_TO_NUMBER: (taxYear: string) => Number.parseInt(taxYear.replace(/^FY/i, ""), 10),
    // TC-PRJ-8.1 to 8.4: Projects grid (FR-PRJ-10 to FR-PRJ-16).
    PROJECTS_GRID: {
      PROJECT_NAME_HEADER: "Project name",
      STATUS_HEADER: "Status",
      TAX_YEAR_HEADER: "Tax year",
      // Grid column -> POST /api/project/list sortBy.
      SORT_FIELDS: { name: "name", projectType: "projectType", taxYear: "taxYear" } as Record<string, string>,
      TABLE_SCROLL: { x: "max-content", y: "100%" },
      STATUS_COLUMN_WIDTH_PX: 130,
      TAX_YEAR_COLUMN_WIDTH_PX: 130,
      ACTIONS_COLUMN_WIDTH_PX: 48,
      SORT_DIRECTIONS: { ascend: "asc", descend: "desc" } as Record<string, "asc" | "desc">,
      ANTD_SORT_ORDERS: { asc: "ascend", desc: "descend" } as Record<"asc" | "desc", "ascend" | "descend">,
      SELECT_CLIENT_TEXT: "Select a Client to see projects",
      NO_PROJECTS_TEXT: (clientName: string, taxYear: string) => `No projects yet for ${clientName} · ${taxYear}`,
      NEW_PROJECT_BUTTON_LABEL: "New project",
      FAILED_TO_LOAD_MESSAGE: "Failed to load projects",
      OPEN_ACTION_LABEL: "Open",
      EDIT_ACTION_LABEL: "Edit project",
      COPY_ACTION_LABEL: "Copy Project",
      COPY_BLOCKED_TOOLTIP: "You don't have permission to copy projects.",
      // TC-PRJ-6.2: no project matches the Status / Project name filters.
      NO_MATCHING_PROJECTS_TEXT: "No projects match these filters",
    },
    // TC-PRJ-6.2 (FR-PRJ-03, OQ-1): filter icon with Status and Project name.
    PROJECT_FILTERS: {
      BUTTON_ARIA_LABEL: "Filter projects",
      STATUS_LABEL: "Status",
      STATUS_PLACEHOLDER: "All statuses",
      NAME_LABEL: "Project name",
      NAME_PLACEHOLDER: "Search by project name",
      APPLY_BUTTON_LABEL: "Apply",
      CLEAR_BUTTON_LABEL: "Clear",
    },
    // TC-PRJ-9.1 to 9.3: New project pop-up (FR-PRJ-20 to FR-PRJ-28).
    NEW_PROJECT_MODAL: {
      TITLE: "New project",
      CREATE_BUTTON_LABEL: "Create",
      HEAD_OFFICE_LABEL: "Head office",
      CLIENT_LABEL: "Client",
      TAX_YEAR_LABEL: "Tax year",
      HEAD_OFFICE_LINE: (headOffice: string) => `Head office: ${headOffice}`,
      CLIENT_PLACEHOLDER: "Select client",
      TAX_YEAR_PLACEHOLDER: "Select tax year",
      FAILED_TO_LOAD_CLIENTS_MESSAGE: "Failed to load clients",
      FAILED_TO_LOAD_TAX_YEARS_MESSAGE: "Failed to load tax years",
      EMPTY_VALUE_TEXT: "—",
      ENGAGEMENT_LABEL: "Engagement",
      ENGAGEMENT_PLACEHOLDER: "Select engagement",
      NO_ENGAGEMENTS_TEXT: (headOffice: string, taxYear: string) => `No engagements for ${headOffice} · ${taxYear}`,
      FAILED_TO_LOAD_ENGAGEMENTS_MESSAGE: "Failed to load engagements",
      // The API names the Head office "client" (BackEnd naming); this refusal is shown in FrontEnd naming.
      ENGAGEMENT_OTHER_HEAD_OFFICE_API_MESSAGE: "Engagement belongs to another client",
      ENGAGEMENT_OTHER_HEAD_OFFICE_TEXT: "The engagement belongs to another Head office.",
      PROJECT_NAME_LABEL: "Project name",
      STATUS_LABEL: "Status",
      DESCRIPTION_LABEL: "Description",
      NAME_MAX_LENGTH: 100,
      DESCRIPTION_MAX_LENGTH: 500,
      DESCRIPTION_ROWS: 3,
      SUGGESTED_NAME: (taxYear: string, clientName: string) => `${taxYear} ${clientName} Return – Filing`,
      REQUIRED_MESSAGE: (field: string) => `${field} is required`,
      NAME_TOO_LONG_MESSAGE: "Project name must be at most 100 characters",
      DESCRIPTION_TOO_LONG_MESSAGE: "Description must be at most 500 characters",
      DUPLICATE_NAME_ERROR_CODE: "PROJ-409",
      DUPLICATE_NAME_MESSAGE: (clientName: string, taxYear: string) =>
        `A project with this name already exists for ${clientName} · ${taxYear}.`,
      CREATED_MESSAGE: (name: string) => `Project ${name} created`,
      FAILED_TO_CREATE_MESSAGE: "Failed to create project",
      DISCARD_TITLE: "Discard this project?",
      DISCARD_OK_TEXT: "Discard",
      DISCARD_CANCEL_TEXT: "Keep editing",
      // TC-PRJ-9.4: edit mode (FR-PRJ-13).
      EDIT_TITLE: "Edit project",
      SAVE_BUTTON_LABEL: "Save",
      UPDATED_MESSAGE: (name: string) => `Project ${name} updated`,
      FAILED_TO_UPDATE_MESSAGE: "Failed to update project",
      DISCARD_CHANGES_TITLE: "Discard your changes?",
      ALLOCATION_METHOD: "TargetAllocation",
      PARTNERSHIP_ENTITY_TYPE: "Partnership",
    },
    COPY_PROJECT_MODAL: {
      TITLE: "Copy project",
      SOURCE_LINE: (name: string, taxYear: string) => `Copying ${name} · ${taxYear}`,
      COPY_BUTTON_LABEL: "Copy",
      DEFAULT_NAME: (name: string) => `Copy of ${name}`,
      OPTIONS_LABEL: "What to copy",
      WITHOUT_DATA_LABEL: "Without data",
      WITHOUT_DATA_HINT:
        "Copies the calculation setup, break periods and blank deliverables (templates only). Calculation results and input data are not copied.",
      WITH_DATA_LABEL: "With data",
      WITH_DATA_HINT: "Copies everything: setup, break periods, calculation results, deliverables and input data.",
      COPIED_MESSAGE: (name: string) => `Project ${name} created`,
      FAILED_TO_COPY_MESSAGE: "Failed to copy project",
    },
  },
};

const ENTITY_DETAILS_ROUTE_PREFIX = `${ROUTE_CONSTANT.ENTITIES}/`;

//TieringView constants
export const TIERING_VIEW_CONSTANT = {
  REQUIRED_FIELD_MESSAGE: "Required",
  OWNERSHIP_PERCENT_LABEL: "Ownership %",
  ENTRY_DATE_LABEL: "Entry Date",
  ACTIONS_COLUMN_HEADER: "Actions",
  PAGE_TITLE: "Tiering Relationships",
  BACK_TO_ENTITY_DETAILS_BUTTON_LABEL: "Back to Client Details",
  ENTITY_DETAILS_ROUTE_PREFIX,
  ENTITY_DETAILS_ROUTE: (entityId: number | string) =>
    `${ENTITY_DETAILS_ROUTE_PREFIX}${entityId}`,
  TIERING_ROUTE: (entityId: number | string) => `/tiering/${entityId}`,

  DIAGRAM: {
    ZOOM_IN_TOOLTIP: "Zoom in",
    ZOOM_OUT_TOOLTIP: "Zoom out",
    FIT_TO_SCREEN_TOOLTIP: "Fit to screen",
    CENTER_ON_ROOT_TOOLTIP: "Center on root",
    LOADING_TEXT: "Loading structure...",
    PARTNER_COLUMN_HEADER: "Partner",
    EIN_COLUMN_HEADER: "EIN",
    CLASS_COLUMN_HEADER: "Class",
    OWNERSHIP_COLUMN_HEADER: "Ownership",
  },

  PARTNER_ROSTER: {
    // The Ownership % input and the remaining-capacity hint in the Add/Edit partner dialog. While false they are
    // not shown and no ownershipPercent is sent, so an edit keeps the stored value. Set to true to bring them back.
    SHOW_OWNERSHIP_FIELD: false,
    // The Look-through checkbox in the Edit partner dialog. While false it is hidden but an edit still sends the stored value.
    SHOW_LOOK_THROUGH_FIELD: false,
    // The "X% allocated · Y% remaining" part of the summary bar above the partner table.
    SHOW_ALLOCATION_SUMMARY: false,
    FAILED_TO_LOAD_MESSAGE: "Failed to load partner roster",
    EXIT_PARTNER_TEXT: "Exit Partner",
    EXIT_CONFIRM_CONTENT: (partnerName: string) =>
      `Mark "${partnerName}" as exited from this partnership? This keeps the historical record but removes them from active ownership.`,
    PARTNER_EXITED_MESSAGE: (partnerName: string) => `${partnerName} exited`,
    FAILED_TO_EXIT_MESSAGE: "Failed to exit partner",
    PARTNER_UPDATED_MESSAGE: "Partner updated",
    PARTNER_ADDED_MESSAGE: "Partner added",
    FAILED_TO_SAVE_PARTNER_MESSAGE: "Failed to save partner",
    TIERING_GENERATED_MESSAGE: (updated: number) =>
      `Tiering structure updated — ${updated} relationships configured`,
    FAILED_TO_GENERATE_TIERING_MESSAGE: "Failed to generate tiering structure",
    PARTNER_NAME_COLUMN_HEADER: "Partner Name",
    INTEREST_CLASS_LABEL: "Interest Class",
    EXIT_DATE_COLUMN_HEADER: "Exit Date",
    STATUS_COLUMN_HEADER: "Status",
    ACTIVE_STATUS_LABEL: "Active",
    EXITED_STATUS_LABEL: "Exited",
    LOOK_THROUGH_BADGE_LABEL: "Look-through",
    LOOK_THROUGH_CHECKBOX_LABEL: "Look-through (not counted toward 100%)",
    LOOK_THROUGH_SUMMARY_SUFFIX: (count: number) => ` (+${count} look-through)`,
    IMPORT_PARTNERS_TEXT: "Import Partners",
    GENERATE_TIERING_BUTTON_TEXT: "Generate Tiering Structure",
    ADD_PARTNER_BUTTON_TEXT: "+ Add Partner",
    NO_PARTNERS_MESSAGE: "No partners in this roster yet",
    EDIT_PARTNER_MODAL_TITLE: "Edit Partner",
    ADD_PARTNER_TEXT: "Add Partner",
    ADD_ENTITY_BUTTON_TEXT: "Add entity",
    PARTNER_FORM_LABEL: "Partner",
    SEARCH_ENTITIES_PLACEHOLDER: "Search entities...",
    SELECT_INTEREST_CLASS_PLACEHOLDER: "Select interest class",
    REMAINING_CAPACITY_PREFIX: "Remaining capacity: ",
  },

  TIER_LIST: {
    FAILED_TO_LOAD_MESSAGE: "Failed to load tiering relationships",
    DELETE_RELATIONSHIP_MODAL_TITLE: "Delete Relationship",
    DELETE_CONFIRM_CONTENT: (startName: string, endName: string) =>
      `Remove "${startName} → ${endName}"? This cannot be undone.`,
    RELATIONSHIP_DELETED_MESSAGE: "Relationship deleted",
    FAILED_TO_DELETE_MESSAGE: "Failed to delete relationship",
    RELATIONSHIP_UPDATED_MESSAGE: "Relationship updated",
    RELATIONSHIP_CREATED_MESSAGE: "Relationship created",
    FAILED_TO_SAVE_MESSAGE: "Failed to save relationship",
    UPPER_TIER_ENTITY_LABEL: "Upper Tier Entity",
    LOWER_TIER_ENTITY_LABEL: "Lower Tier Entity",
    TIER_LEVEL_COLUMN_HEADER: "Tier Level",
    DIRECTION_LABEL: "Direction",
    ADD_RELATIONSHIP_BUTTON_TEXT: "+ Add Relationship",
    NO_RELATIONSHIPS_MESSAGE: "No tiering relationships found",
    EDIT_RELATIONSHIP_MODAL_TITLE: "Edit Relationship",
    ADD_RELATIONSHIP_MODAL_TITLE: "Add Relationship",
    SEARCH_SELECT_ENTITY_PLACEHOLDER: "Search and select entity...",
    SELECT_DIRECTION_PLACEHOLDER: "Select direction",
    RELATIONSHIP_DIRECTION_OPTIONS: [
      { value: "Direct", label: "Direct" },
      { value: "Indirect", label: "Indirect" },
    ],
    INTEREST_CLASS_LABEL: "Interest Class",
    EXIT_DATE_LABEL: "Exit Date",
    OWNERSHIP_WARNING_TOOLTIP: (percent: number) =>
      `Total ownership for this partnership is ${percent}% — below 100%`,
    BULK_UPLOAD_BUTTON_TEXT: "Bulk Upload",
  },

};

//UserAccessView constants
export const USER_ACCESS_VIEW_CONSTANT = {
  OWNER_ACCESS_LABEL: "Owner",
  READ_ACCESS_LABEL: "Read",
  ACCESS_REMOVED_LABEL: "Access Removed",
  NO_ACCESS_LABEL: "No Access",
  INVITE_BUTTON_LABEL: "Invite",
  SEARCH_ICON_SRC: "/search.svg",
  SEARCH_ICON_ALT_TEXT: "Search Icon",
  NO_OPTIONS_FOUND_LABEL: "Options not found",
  SEARCH_PLACEHOLDER: "Search",
  EDIT_PAGE_BACK_TO_VIEW_ROUTE: (deliverableId?: string) =>
    `/deliverable/user-access/ ${deliverableId}?${GLOBAL_CONSTANT.ACTIVE_TABS_KEY}=${GLOBAL_CONSTANT.USER_ACCESS}`,
  VIEW_PAGE_TO_EDIT_ROUTE: (deliverableId?: string) =>
    `/deliverable/user-access/${deliverableId}/edit?${GLOBAL_CONSTANT.ACTIVE_TABS_KEY}=${GLOBAL_CONSTANT.USER_ACCESS}`,
};

//ProjectView constants
export const PROJECT_VIEW_CONSTANT = {
  // Only what the create-project modal still reads; the project list page that owned the rest is gone.
  PROJECT_LIST: {
    PROJECT_NAME_COLUMN_HEADER: "Project Name",
    ENGAGEMENT_COLUMN_HEADER: "Engagement",
    TAX_YEAR_COLUMN_HEADER: "Tax Year",
    CREATE_PROJECT_BUTTON_LABEL: "Create Project",
  },

  CREATE_PROJECT_MODAL: {
    MODAL_WIDTH: "42rem",
    CLIENT_LABEL: "Head office",
    PARTNERSHIP_LABEL: "Client",
    PARTNERSHIP_PLACEHOLDER: "Select client",
    EMPTY_VALUE_TEXT: "—",

    PARTNERSHIP_ENTITY_TYPE: "partnership",
    ALLOCATION_METHOD: "TargetAllocation",
    ENGAGEMENT_REQUIRED_MESSAGE: "Select an engagement",
    FAILED_TO_LOAD_ENGAGEMENTS_MESSAGE: "Failed to load engagements",
    TAX_YEAR_REQUIRED_MESSAGE: "Please enter a tax year",
    TAX_YEAR_MIN: 1900,
    TAX_YEAR_MAX: 2200,
    NO_ENGAGEMENTS_FOR_CLIENT_MESSAGE: "No engagements found for this head office",
    PROJECT_NAME_REQUIRED_MESSAGE: "Project Name is required",
    NO_PARTNERSHIP_SELECTED_MESSAGE:
      "No client selected — cannot create a project.",
    PROJECT_CREATED_MESSAGE: "Project created",
    FAILED_TO_CREATE_PROJECT_MESSAGE: "Failed to create project",
    DUPLICATE_PROJECT_NAME_ERROR_CODE: "PROJ-409",
    PROJECT_NAME_PLACEHOLDER:
      "e.g. Nexus Capital Partners — FY2025 Tax Compliance",
    NO_ENGAGEMENT_PLACEHOLDER: "No engagement",
    CREATING_TEXT: "Creating...",
    AUTO_PROJECT_NAME: (partnershipName: string, year: number) =>
      `${partnershipName} — FY${year} Tax Compliance`,

  },

};

// ClientOverview constants
export const CLIENT_OVERVIEW_CONSTANT = {
  STATUS_COLORS: {
    COMPLETE: "#4CAF50",
    IN_PROGRESS: "var(--colorPrimary)",
    NOT_STARTED: "var(--colorWhiteDisabled)",
  },
  COMPLETE_LABEL: "Complete",
  IN_PROGRESS_LABEL: "In Progress",
  NOT_STARTED_LABEL: "Not Started",
  PARTNERS_SUFFIX: "partners",
  CALCS_SUFFIX: "calcs",
  CALCULATIONS_CONFIGURED_SUFFIX: "calculations configured",
  VIEW_PROJECT_BUTTON_LABEL: "View Project",
  CONFIGURE_BUTTON_LABEL: "Configure",
  SELECT_CLIENT_TEXT: "Select a client to view the overview.",
  PAGE_TITLE: (clientName: string, taxYear: string) =>
    `${clientName} — ${taxYear} Overview`,
  PAGE_SUBTITLE: (totalPartnerships: number, totalPartners: string) =>
    `${totalPartnerships} partnerships · ${totalPartners} total partners`,
  NO_PARTNERSHIPS_FOUND_TEXT: "No partnerships found for this client.",
  TOTAL_PARTNERSHIPS_LABEL: "Total Partnerships",
  CALCULATIONS_CONFIGURED_LABEL: "Calculations Configured",
  OVERALL_PROGRESS_LABEL: "Overall Progress",
};

const ENGAGEMENT_TYPE = {
  AUDIT: "Audit",
  TAX: "Tax",
  COMPLIANCE: "Compliance",
  CONSULTING: "Consulting",
  ADVISORY: "Advisory",
} as const;
const ENGAGEMENT_TYPE_COLORS: Record<string, string> = {
  [ENGAGEMENT_TYPE.AUDIT]: "#8B2500",
  [ENGAGEMENT_TYPE.TAX]: "#2563eb",
  [ENGAGEMENT_TYPE.COMPLIANCE]: "#7c3aed",
  [ENGAGEMENT_TYPE.CONSULTING]: "#16a34a",
  [ENGAGEMENT_TYPE.ADVISORY]: "#0891b2",
};
const ENGAGEMENT_STATUS_COLORS: Record<string, string> = {
  Active: "#22c55e",
  Draft: "#f59e0b",
  Completed: "#94a3b8",
};
const ENGAGEMENT_ALL_FILTER_VALUE = "all";
const ENGAGEMENT_TYPE_ALL_OPTION_LABEL = "Type: All";
const ENGAGEMENT_STATUS_ALL_OPTION_LABEL = "Status: All";

//EngagementView constants
export const ENGAGEMENT_VIEW_CONSTANT = {
  ENGAGEMENT_TYPE,
  TYPE_COLORS: ENGAGEMENT_TYPE_COLORS,
  DEFAULT_TYPE_COLOR: "#64748b",
  STATUS_COLORS: ENGAGEMENT_STATUS_COLORS,
  ENGAGEMENT_DETAILS_ROUTE: (id: number | string) => `/engagements/${id}`,
  DEFAULT_STATUS_COLOR: "#ADADAD",
  ENTITY_TYPE_COLORS: {
    Partnership: "#2563eb",
    Partner: "#16a34a",
  } as Record<string, string>,
  BACK_TO_ENGAGEMENTS_BUTTON_LABEL: "Back to Engagements",
  START_DATE_REQUIRED_MESSAGE: "Please select a start date",
  END_DATE_REQUIRED_MESSAGE: "Please select an end date",
  END_DATE_BEFORE_START_DATE_MESSAGE: "End date must be on or after the start date",
  MODAL_BODY_MAX_HEIGHT_VH: 65,
  NOTES_MAX_LENGTH: 500,
  NOTES_TOO_LONG_MESSAGE: (maxLength: number) =>
    `Notes can be at most ${maxLength} characters`,
  SINGLE_YEAR_DURATION: "Single-year",
  MULTI_YEAR_DURATION: "Multi-year",
  toApiDate: (date: Dayjs): string => date.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
  computeEngagementDuration: (startDate: Dayjs, endDate: Dayjs): string =>
    endDate.year() > startDate.year()
      ? ENGAGEMENT_VIEW_CONSTANT.MULTI_YEAR_DURATION
      : ENGAGEMENT_VIEW_CONSTANT.SINGLE_YEAR_DURATION,

  LIST: {
    PAGE_TITLE: "Engagements",
    SEARCH_ENGAGEMENTS_PLACEHOLDER: "Search engagements...",
    TYPE_ALL_OPTION_LABEL: ENGAGEMENT_TYPE_ALL_OPTION_LABEL,
    STATUS_ALL_OPTION_LABEL: ENGAGEMENT_STATUS_ALL_OPTION_LABEL,
    CLEAR_FILTERS_TEXT: "Clear filters",
    ALL_FILTER_VALUE: ENGAGEMENT_ALL_FILTER_VALUE,
    TYPE_FILTER_OPTIONS: [
      { value: ENGAGEMENT_ALL_FILTER_VALUE, label: ENGAGEMENT_TYPE_ALL_OPTION_LABEL },
      ...Object.keys(ENGAGEMENT_TYPE_COLORS).map((type) => ({ value: type, label: type })),
    ],
    STATUS_FILTER_OPTIONS: [
      { value: ENGAGEMENT_ALL_FILTER_VALUE, label: ENGAGEMENT_STATUS_ALL_OPTION_LABEL },
      ...Object.keys(ENGAGEMENT_STATUS_COLORS).map((status) => ({ value: status, label: status })),
    ],
    SEARCH_DEBOUNCE_MS: 400,
    COLUMN_WIDTH_PX: {
      ID: 150,
      NAME: 320,
      TYPE: 140,
      START_DATE: 140,
      END_DATE: 140,
      ACTIONS: 48,
    },
    SELECT_CLIENT_TEXT: "Select a client to view engagements",
    ENGAGEMENT_ID_COLUMN_HEADER: "Engagement ID",
    ENGAGEMENT_NAME_COLUMN_HEADER: "Engagement Name",
    TYPE_COLUMN_HEADER: "Type",
    START_DATE_COLUMN_HEADER: "Start Date",
    END_DATE_COLUMN_HEADER: "End Date",
    VIEW_DETAILS_MENU_LABEL: "View details",
    TABLE_SCROLL: { y: "100%" },
    CREATE_ENGAGEMENT_DISABLED_TOOLTIP:
      "Select a client to create an engagement",
  },

  CREATE_MODAL: {
    ADD_ENGAGEMENT_MODAL_TITLE: "Add Engagement",
    CREATE_ENGAGEMENT_BUTTON_LABEL: "Create Engagement",
    ENGAGEMENT_CREATED_MESSAGE: "Engagement created",
    FAILED_TO_CREATE_ENGAGEMENT_MESSAGE: "Failed to create engagement",
    ENGAGEMENT_TYPE_LABEL: "Engagement Type",
    ENGAGEMENT_TYPE_REQUIRED_MESSAGE: "Please select an engagement type",
    SELECT_TYPE_PLACEHOLDER: "Select type",
    ENGAGEMENT_TYPE_OPTIONS: [
      "Compliance",
      "Tax",
      "Advisory",
      "Consulting",
      "Audit",
    ],
    ENGAGEMENT_NAME_LABEL: "Engagement Name",
    ENGAGEMENT_NAME_REQUIRED_MESSAGE: "Please enter an engagement name",
    ENGAGEMENT_NAME_PLACEHOLDER: "e.g. Partnership K-1 preparation",
    START_DATE_LABEL: "Start Date",
    ENTITY_OPTION_LABEL: (name: string, entityType: string) =>
      `${name} (${entityType})`,
    NO_ENTITIES_TO_ADD_TEXT: "No more entities to add",
    NO_ENTITIES_FOR_CLIENT_TEXT: "This client has no entities yet",
    NO_MATCHING_ENTITIES_TEXT: (search: string) => `No entity named "${search}"`,
    LOADING_ENTITIES_TEXT: "Loading entities…",
    ENTITIES_LINK_FAILED_MESSAGE: (failed: number, total: number) =>
      `Engagement created, but ${failed} of ${total} ${total !== 1 ? "entities" : "entity"} could not be linked. Add ${failed !== 1 ? "them" : "it"} from the engagement details.`,
    YEAR_UNIT: "year" as const,
    DAY_UNIT: "day" as const,
  },

  DETAILS: {
    BREADCRUMB_ROOT_LABEL: "Engagements",
    DETAIL_TABS: [
      { key: "projects", label: "Projects" },
      { key: "auditLog", label: "Audit Log" },
    ],
    EMPTY_VALUE_PLACEHOLDER: "—",
    ENGAGEMENT_UPDATED_MESSAGE: "Engagement updated",
    EDIT_ENGAGEMENT_MODAL_TITLE: "Edit Engagement",
    EDIT_MODAL_WIDTH_PX: 520,
    ENGAGEMENT_NAME_LABEL: "Engagement name",
    ENGAGEMENT_NAME_REQUIRED_MESSAGE: "Please enter an engagement name",
    ENGAGEMENT_NAME_PLACEHOLDER: "e.g. FY24 Annual Audit",
    ENGAGEMENT_TYPE_LABEL: "Engagement type",
    ENGAGEMENT_TYPE_REQUIRED_MESSAGE: "Please select a type",
    SELECT_TYPE_PLACEHOLDER: "Select type",
    STATUS_LABEL: "Status",
    STATUS_OPTIONS: ["Active", "In Progress", "Completed"],
    DURATION_LABEL: "Duration",
    SINGLE_YEAR_OPTION_LABEL: "Single-year",
    MULTI_YEAR_OPTION_LABEL: "Multi-year",
    START_DATE_LABEL: "Start Date",
    END_DATE_LABEL: "End Date",
    LEAD_PARTNER_LABEL: "Lead Partner",
    LEAD_PARTNER_PLACEHOLDER: "e.g. John Anderson",
    NOTES_LABEL: "Notes",
    NOTES_PLACEHOLDER: "Additional context for this engagement...",
    SAVING_TEXT: "Saving…",
    SAVE_CHANGES_BUTTON_LABEL: "Save Changes",
    ENGAGEMENT_NOT_FOUND_TEXT: "Engagement not found",
    ENGAGEMENT_NOT_FOUND_SUBTEXT:
      "No engagement with this ID was found in your tenant's engagement list.",
    DEACTIVATE_CONFIRM_TITLE: "Deactivate engagement?",
    DEACTIVATE_CONFIRM_CONTENT: (description: string) =>
      `This will mark "${description}" as inactive. You can reactivate it later.`,
    ENGAGEMENT_DEACTIVATED_MESSAGE: "Engagement deactivated",
    GANTT_TIMELINE_TOOLTIP:
      "Gantt Timeline (2.4) — flagged as a follow-up, not yet built",
    GANTT_TIMELINE_ALERT_MESSAGE:
      "Show Timeline (2.4 Gantt) is a follow-up component — not built in this pass.",
    SHOW_TIMELINE_BUTTON_LABEL: "Show Timeline",
    ENGAGEMENT_DETAILS_CARD_TITLE: "Engagement Details",
    COLLAPSE_ENGAGEMENT_DETAILS_ARIA_LABEL: "Collapse engagement details",
    EXPAND_ENGAGEMENT_DETAILS_ARIA_LABEL: "Expand engagement details",
    TYPE_LABEL: "Type",
    ENGAGEMENT_ID_LABEL: "Engagement ID",
    OPEN_ENDED_TEXT: "Open-ended",
    ADD_ENTITY_BUTTON_LABEL: "+ Add Entity",
    SELECT_ENTITY_TO_LINK_PLACEHOLDER: "Select an entity to link",
    REMOVE_BUTTON_LABEL: "Remove",
    REMOVE_ENTITY_ARIA_LABEL: (name: string) => `Remove ${name}`,
    REMOVE_ENTITY_CONFIRM_TITLE: "Remove entity?",
    REMOVE_ENTITY_CONFIRM_CONTENT: (name: string) =>
      `Remove "${name}" from this engagement?`,
    ENTITY_REMOVED_MESSAGE: "Entity removed",
    ENTITY_LINKED_MESSAGE: "Entity linked",
    FAILED_TO_LOAD_ENTITIES_MESSAGE: "Failed to load entities",
    NOTES_CARD_TITLE: "Notes",
    NOTES_DISPLAY_CHAR_LIMIT: 150,
    SHOW_MORE_TEXT: "…more",
    SHOW_LESS_TEXT: "Show less",
    NO_ENTITIES_IN_SCOPE_TEXT: "No entities in scope",
    NO_ENTITIES_IN_SCOPE_SUBTEXT:
      'Use "Add Entity" in the Linked Entities panel above to link a partnership or partner.',
    NO_PROJECTS_YET_TEXT: "No projects yet",
    NO_PROJECTS_YET_SUBTEXT:
      "Project listing scoped to an engagement is not yet supported in the backend.",
    NO_AUDIT_HISTORY_TEXT: "No audit history available",
    NO_AUDIT_HISTORY_SUBTEXT:
      "Engagement-level audit logging is not yet implemented in the backend.",
  },
};

const CLIENT_DETAIL_TAB_KEY = {
  ENGAGEMENTS: "engagements",
  PARTNERSHIPS: "partnerships",
  DETAILS: "details",
  PROJECTS: "projects",
  COA: "coa",
  CALC_PROFILES: "calcProfiles",
  AUDIT_LOG: "auditLog",
} as const;
const CLIENT_STATUS_FILTER = {
  ALL: "all",
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const;

//ClientView constants
export const CLIENT_VIEW_CONSTANT = {
  DETAIL_TAB_KEY: CLIENT_DETAIL_TAB_KEY,
  STATUS_FILTER: CLIENT_STATUS_FILTER,
  ACTIVE_STATUS_COLORS: {
    Active: "#22c55e",
    Inactive: "#f59e0b",
  } as Record<string, string>,
  ACTIVE_LABEL: "Active",
  INACTIVE_LABEL: "Inactive",
  ENGAGEMENT_STATUS: {
    ACTIVE: "Active",
    DRAFT: "Draft",
  } as const,
  HIDDEN_DETAIL_TAB_KEYS: [
    CLIENT_DETAIL_TAB_KEY.COA,
    CLIENT_DETAIL_TAB_KEY.CALC_PROFILES,
    CLIENT_DETAIL_TAB_KEY.AUDIT_LOG,
  ],

  ENGAGEMENTS_TAB: {
    ADD_ENGAGEMENT_BUTTON_LABEL: "+ Add Engagement",
    ENGAGEMENT_ID_COLUMN_HEADER: "Engagement ID",
    ENGAGEMENT_TYPE_COLUMN_HEADER: "Engagement Type",
    DESCRIPTION_COLUMN_HEADER: "Description",
    TAX_YEAR_COLUMN_HEADER: "Tax Year",
    EMPTY_VALUE_PLACEHOLDER: "—",
    COLUMN_COUNT: 5,
    STATUS_COLUMN_HEADER: "Status",
    NO_ENGAGEMENTS_TEXT:
      "No engagements yet. Click + Add Engagement to create one.",
  },

  PARTNERSHIPS_TAB: {
    PARTNERSHIP_NAME_COLUMN_HEADER: "Partnership Name",
    CATEGORY_COLUMN_HEADER: "Category",
    NO_PARTNERSHIPS_TEXT: "No partnerships found.",
    EMPTY_VALUE_PLACEHOLDER: "—",
    COLUMN_COUNT: 2,
    FAILED_TO_LOAD_MESSAGE: "Failed to load partnerships",
  },

  COA_TAB: {
    ACCOUNT_NUMBER_COLUMN_HEADER: "Account #",
    ACCOUNT_NAME_COLUMN_HEADER: "Account Name",
    TYPE_COLUMN_HEADER: "Type",
    CATEGORY_COLUMN_HEADER: "Category",
    BALANCE_COLUMN_HEADER: "Balance",
    SAMPLE_ROWS: [
      { number: "1000", name: "Cash & Cash Equivalents", type: "Asset", category: "Current Asset", balance: "$1,245,000" },
      { number: "1100", name: "Accounts Receivable", type: "Asset", category: "Current Asset", balance: "$387,500" },
      { number: "1500", name: "Investment in Partnerships", type: "Asset", category: "Non-Current", balance: "$8,750,000" },
      { number: "1800", name: "Property & Equipment", type: "Asset", category: "Fixed Asset", balance: "$2,100,000" },
      { number: "2000", name: "Accounts Payable", type: "Liability", category: "Current Liability", balance: "$215,400" },
      { number: "2500", name: "Notes Payable", type: "Liability", category: "Long-Term", balance: "$1,500,000" },
      { number: "3000", name: "Partners' Capital", type: "Equity", category: "Capital", balance: "$9,200,000" },
      { number: "4000", name: "Partnership Income", type: "Revenue", category: "Income", balance: "$3,450,000" },
      { number: "4500", name: "Interest Income", type: "Revenue", category: "Income", balance: "$125,000" },
      { number: "5000", name: "Management Fees", type: "Expense", category: "Operating", balance: "$480,000" },
      { number: "5500", name: "Depreciation Expense", type: "Expense", category: "Operating", balance: "$210,000" },
      { number: "6000", name: "Tax Provision", type: "Expense", category: "Tax", balance: "$340,000" },
    ],
  },

  CALC_PROFILES_TAB: {
    COMING_SOON_TEXT: "Calculation Profiles — coming soon",
    NO_ENDPOINT_SUBTEXT:
      "No backend endpoint exists for calculation profiles yet.",
  },

  DETAILS_TAB: {
    CLIENT_INFORMATION_CARD_TITLE: "Head Office Information",
    CLIENT_NAME_LABEL: "Head Office Name",
    CLIENT_CODE_LABEL: "Head Office Code",
    STATUS_LABEL: "Status",
    CREATED_LABEL: "Created",
    WEBSITE_LABEL: "Website",
    PRIMARY_CONTACT_LABEL: "Primary Contact",
    CONTACT_EMAIL_LABEL: "Contact Email",
    CONTACT_PHONE_LABEL: "Contact Phone",
    STREET_ADDRESS_LABEL: "Street Address",
    CITY_LABEL: "City",
    STATE_LABEL: "State",
    ZIP_CODE_LABEL: "Zip Code",
    NOTES_LABEL: "Notes",
  },

  PROJECTS_TAB: {
    PROJECT_NAME_COLUMN_HEADER: "Project Name",
    TAX_YEAR_COLUMN_HEADER: "Tax Year",
    STATUS_COLUMN_HEADER: "Status",
    NO_PROJECTS_TEXT: "No projects found.",
    COLUMN_COUNT: 3,
  },

  AUDIT_LOG_TAB: {
    NO_AUDIT_HISTORY_TEXT: "No audit history available.",
    NO_ENDPOINT_SUBTEXT:
      "Head Office-level audit logging is not yet implemented in the backend.",
  },

  DETAIL_TABS: [
    { key: CLIENT_DETAIL_TAB_KEY.ENGAGEMENTS, label: "Engagements" },
    { key: CLIENT_DETAIL_TAB_KEY.PARTNERSHIPS, label: "Partnerships" },
    { key: CLIENT_DETAIL_TAB_KEY.DETAILS, label: "Details" },
    { key: CLIENT_DETAIL_TAB_KEY.PROJECTS, label: "Projects" },
    { key: CLIENT_DETAIL_TAB_KEY.COA, label: "Chart of Accounts" },
    { key: CLIENT_DETAIL_TAB_KEY.CALC_PROFILES, label: "Calculation Profiles" },
    { key: CLIENT_DETAIL_TAB_KEY.AUDIT_LOG, label: "Audit Log" },
  ],

  DETAIL_PANEL: {
    CLOSE_PANEL_ARIA_LABEL: "Close panel",
    // One source for the pane's motion: the CSS custom properties and the unmount timer both use it.
    MOTION_DURATION_MS: 250,
    MOTION_EASING: "ease-in-out",
    PARTNERSHIPS_STAT_LABEL: "Partnerships",
    ENGAGEMENTS_STAT_LABEL: "Engagements",
    PROJECTS_STAT_LABEL: "Projects",
    PARTNERS_STAT_LABEL: "Partners",
  },

  FILTER_PANEL: {
    CLOSE_FILTERS_ARIA_LABEL: "Close filters",
    FILTERS_TITLE: "Filters",
    STATUS_LABEL: "Status",
    ALL_LABEL: "All",
    STATUS_OPTIONS: [
      { value: CLIENT_STATUS_FILTER.ALL, label: "All" },
      { value: CLIENT_STATUS_FILTER.ACTIVE, label: "Active" },
      { value: CLIENT_STATUS_FILTER.INACTIVE, label: "Inactive" },
    ],
    DATE_CREATED_RANGE_LABEL: "Date Created Range",
    DATE_SEPARATOR_TEXT: "–",
  },

  LIST: {
    FAILED_TO_LOAD_CLIENTS_MESSAGE: "Failed to load head offices",
    CLIENT_NAME_COLUMN_HEADER: "Head Office Name",
    SEARCH_DEBOUNCE_MS: 400,
    ACTIONS_COLUMN_WIDTH_PX: 48,
    DATE_END_OF_DAY_SUFFIX: "T23:59:59",
    SELECTION_COLUMN_SELECTOR: ".ant-table-selection-column",
    SELECTED_ROW_CLASS: "client-row-selected",
    PROJECTS_PAGE_SIZE: 100,
    STATUS_PARAM: {
      ACTIVE: "Active",
      INACTIVE: "Inactive",
      ALL: "All",
    } as const,
    SELECT_MENU_LABEL: "Select",
    DESELECT_MENU_LABEL: "Deselect",
    PAGE_TITLE: "Head office",
    CREATE_NEW_CLIENT_BUTTON_LABEL: "Create Head Office",
    SEARCH_CLIENTS_PLACEHOLDER: "Search head offices...",
    FILTERS_BUTTON_LABEL: "Filters",
    ACTIVE_ONLY_CHIP_TEXT: "Active only",
    INACTIVE_ONLY_CHIP_TEXT: "Inactive only",
    TABLE_SCROLL: { y: "100%" },
    DEACTIVATE_CLIENT_MODAL_TITLE: "Deactivate head office?",
    REACTIVATE_CLIENT_MODAL_TITLE: "Reactivate head office?",
    DEACTIVATE_NOT_SUPPORTED_MESSAGE:
      "Deactivate / reactivate is not yet supported (no backend endpoint). TODO: add PATCH /api/client/{id}/status.",
    DEACTIVATE_CONFIRM_CONTENT: (name?: string) =>
      `This will mark "${name}" as inactive. You can reactivate it later.`,
    REACTIVATE_CONFIRM_CONTENT: (name?: string) =>
      `This will reactivate "${name}".`,
  },

  FORM_MODE: {
    CREATE: "create",
    EDIT: "edit",
  } as const,

  CREATE_MODAL: {
    CLIENT_CREATED_MESSAGE: "Head Office created successfully",
    FAILED_TO_CREATE_CLIENT_MESSAGE: "Failed to create head office",
    CLIENT_ALREADY_EXISTS_MESSAGE: "Head Office already exists.",
    MODAL_TITLE: "Create New Head Office",
    EDIT_MODAL_TITLE: "Edit Head Office",
    SAVE_CHANGES_BUTTON_LABEL: "Save Changes",
    CLIENT_UPDATED_MESSAGE: "Head Office updated successfully",
    FAILED_TO_UPDATE_CLIENT_MESSAGE: "Failed to update head office",
    CLIENT_CODE_READONLY_HINT: "Cannot be changed",
    MODAL_WIDTH_PX: 560,
    CLIENT_NAME_LABEL: "Head Office Name",
    CLIENT_NAME_REQUIRED_MESSAGE: "Head Office Name is required",
    CLIENT_NAME_PLACEHOLDER: "e.g. Westfield Capital Partners LP",
    CLIENT_CODE_LABEL: "Head Office Code",
    CLIENT_CODE_REQUIRED_MESSAGE: "Head Office Code is required",
    CLIENT_CODE_HINT: "Short ID, e.g. WCP",
    CLIENT_CODE_PLACEHOLDER: "WCP",
    WEBSITE_LABEL: "Website",
    WEBSITE_PLACEHOLDER: "https://www.example.com",
    PRIMARY_CONTACT_NAME_LABEL: "Primary Contact Name",
    PRIMARY_CONTACT_NAME_PLACEHOLDER: "Jane Smith",
    EMAIL_LABEL: "Email",
    EMAIL_INVALID_MESSAGE: "Enter a valid email",
    EMAIL_PLACEHOLDER: "jane@example.com",
    PHONE_LABEL: "Phone",
    PHONE_PLACEHOLDER: "(555) 000-0000",
    STREET_ADDRESS_LABEL: "Street Address",
    STREET_ADDRESS_PLACEHOLDER: "1200 Innovation Way, Suite 400",
    CITY_LABEL: "City",
    CITY_PLACEHOLDER: "San Francisco",
    STATE_LABEL: "State",
    STATE_PLACEHOLDER: "CA",
    ZIP_CODE_LABEL: "ZIP Code",
    ZIP_CODE_PLACEHOLDER: "94105",
    NOTES_LABEL: "Notes",
    NOTES_PLACEHOLDER: "Additional notes about this head office...",
    CLIENT_NAME_MAX: 100,
    CLIENT_CODE_MAX: 20,
    WEBSITE_MAX: 200,
    CONTACT_NAME_MAX: 100,
    EMAIL_MAX: 254,
    ADDRESS_MAX: 200,
    PHONE_INPUT_MAX_LENGTH: 20,
    PHONE_MIN_DIGITS: 7,
    PHONE_MAX_DIGITS: 15,
    CLIENT_CODE_PATTERN: /^[A-Za-z0-9_-]+$/,
    WEBSITE_PATTERN: /^https?:\/\/[^\s/$.?#][^\s]*$/i,
    PHONE_DISALLOWED_CHARS_PATTERN: /[^0-9()+\-.\s]/g,
    PHONE_ALLOWED_PATTERN: /^[0-9()+\-.\s]+$/,
    CLIENT_NAME_MAX_MESSAGE: (max: number) =>
      `Head Office Name must be ${max} characters or fewer`,
    CLIENT_CODE_PATTERN_MESSAGE: "Use letters, numbers, - or _ only",
    WEBSITE_INVALID_MESSAGE:
      "Enter a valid URL starting with http:// or https://",
    WEBSITE_MAX_MESSAGE: (max: number) =>
      `Website must be ${max} characters or fewer`,
    CONTACT_NAME_MAX_MESSAGE: (max: number) =>
      `Must be ${max} characters or fewer`,
    EMAIL_MAX_MESSAGE: (max: number) =>
      `Email must be ${max} characters or fewer`,
    PHONE_INVALID_MESSAGE: "Enter a valid phone number",
    ADDRESS_MAX_MESSAGE: (max: number) =>
      `Address must be ${max} characters or fewer`,
    BODY_MAX_HEIGHT: "calc(100vh - 16.25rem)",
    BODY_SCROLLBAR_GUTTER: "1rem",
    DISABLED_BUTTON_OPACITY: 0.6,
  },
};

// Context filter bar: Tax year / Head office / Client selectors, filters toggle and notifications button.
export const CONTEXT_FILTER_BAR_CONSTANT = {
  // Whether the selectors start expanded; flip to false to start folded away.
  INITIAL_EXPANDED: true,
  // Degrees the filters icon is turned so its sliders read as vertical.
  FILTERS_ICON_ROTATION: 90,
  // Pages where the Client selector (a partnership under the head office) is left out because the
  // page has no use for that choice. Everywhere else the bar shows all three selectors.
  CLIENT_HIDDEN_ROUTES: ["/calculations/structure"],
  // Pages where the Tax year selector is left out. None at the moment: every page shows all
  // three selectors, even where the page itself does not use the value.
  TAX_YEAR_HIDDEN_ROUTES: [] as string[],
  // Class on the option list of each selector; its dark styling lives in the global stylesheet.
  POPUP_CLASS_NAME: "context-filter-popup",
  // Values of the inline visibility that switches the collapsed selectors off after their fade.
  VISIBILITY: { VISIBLE: "visible", HIDDEN: "hidden" } as const,
  GROUP_ARIA_LABEL: "Context filters",
  FILTERS_TOGGLE_ARIA_LABEL: "Toggle context filters",
  NOTIFICATIONS_ARIA_LABEL: "Notifications",
  // The bell shows this short notice; the key makes a second click replace the first notice.
  NOTIFICATIONS_TOAST_TEXT: "Under development",
  NOTIFICATIONS_TOAST_KEY: "context-filter-notifications",
  TAX_YEAR: {
    KEY: "tax-year",
    LABEL: "Tax year",
    PLACEHOLDER: "Select tax year",
  },
  HEAD_OFFICE: {
    KEY: "head-office",
    LABEL: "Head office",
    PLACEHOLDER: "Select head office",
  },
  CLIENT: {
    KEY: "client",
    LABEL: "Client",
    PLACEHOLDER: "Select client",
  },
};

// Onboarding step shown once after login: pick head office, tax year and client.
export const ONBOARDING_CONSTANT = {
  TITLE: "Fill the fields to complete Onboarding",
  MODAL_WIDTH: "34rem",
  HEAD_OFFICE_LABEL: "Head office",
  TAX_YEAR_LABEL: "Tax Year",
  CLIENT_LABEL: "Client",
  HEAD_OFFICE_PLACEHOLDER: "Select head office",
  TAX_YEAR_PLACEHOLDER: "Select tax year",
  CLIENT_PLACEHOLDER: "Select client",
  CLIENT_DISABLED_PLACEHOLDER: "Select a head office first",
  CLIENT_NO_OPTIONS_TEXT: "No clients found for this head office",
  REQUIRED_MESSAGE: (label: string) => `${label} is required`,
  CONFIRM_BUTTON_LABEL: "Confirm",
  RETRY_BUTTON_LABEL: "Retry",
  LOAD_ERROR_MESSAGE: "We could not load the options. Check your connection and try again.",
  // The client choice is a partnership entity under the chosen head office.
  CLIENT_ENTITY_TYPE: "Partnership",
  CLIENT_ENTITY_TYPE_MATCH: "partnership",
  SESSION_RESET_EVENT: "session:reset",
  SAVE_FAILED_MESSAGE: "We could not remember your onboarding choice in this browser.",
  CLEAR_FAILED_MESSAGE: "We could not clear your saved selection from this browser.",
};

// Breadcrumb shown at the top of a page: where the user is and the way back up.
export const PAGE_BREADCRUMB_CONSTANT = {
  ARIA_LABEL: "Breadcrumb",
  BACK_ARIA_LABEL: "Back",
  SEPARATOR: "/",
};

// Downloadable error report shared by the Clients list and Partner Roster import dialogs.
export const IMPORT_ERROR_REPORT_CONSTANT = {
  SHEET_NAME: "Import errors",
  MIME_TYPE: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  HEADERS: ["Row", "Name", "Field", "Reason"],
  COLUMN_KEYS: ["row", "name", "field", "reason"],
  COLUMN_WIDTHS: [8, 30, 16, 70],
  // Excel cells take ARGB: the opaque alpha byte goes in front of the theme colour.
  ARGB_OPAQUE_PREFIX: "FF",
  BORDER_STYLE: "thin",
  HEADER_ROW_NUMBER: 1,
  FIRST_COLUMN_NUMBER: 1,
} as const;

const ENTITY_CODE_PREFIX = "ENT-";
const ENTITY_CODE_PAD_LENGTH = 3;
const ENTITY_LIST_COLUMN_KEYS = ["name", "type", "taxEntityType", "status", "actions"] as const;

//EntityView constants
export const ENTITY_VIEW_CONSTANT = {
  PARTNERSHIP_CATEGORIES: [
    "Limited Partnership",
    "LLC",
    "S-Corporation",
    "C-Corporation",
    "General Partnership",
  ],
  STATUS_OPTIONS: ["Active", "Inactive"],
  ENTITY_NAME_LABEL: "Client Name",
  TAX_ENTITY_TYPE_LABEL: "Tax Entity Type",
  STATUS_LABEL: "Status",
  STREET_ADDRESS_LABEL: "Street Address",
  CITY_LABEL: "City",
  STATE_LABEL: "State",
  ZIP_CODE_LABEL: "ZIP Code",
  NOTES_LABEL: "Notes",
  SAVING_TEXT: "Saving…",
  BACK_TO_ENTITIES_BUTTON_LABEL: "Back to Clients",
  ENTITIES_ROUTE: ROUTE_CONSTANT.ENTITIES,
  ENTITY_KIND: {
    PARTNERSHIP: "partnership",
    PARTNER: "partner",
    OTHER: "other",
  } as const,
  FORM_MODE: {
    CREATE: "create",
    EDIT: "edit",
  } as const,
  // Shared create/edit form (EntityFormModal)
  FORM: {
    MODAL_WIDTH_PX: 560,
    API_ENTITY_TYPE_PARTNERSHIP: "Partnership",
    API_ENTITY_TYPE_PARTNER: "Partner",
    DEFAULT_PARTNERSHIP_CATEGORY: "Limited Partnership",
    DEFAULT_TAX_YEAR_START: "January 1",
    DEFAULT_STATUS: "Active",
    STATE_MAX_LENGTH: 2,
    NOTES_ROWS: 3,
    SSN_INPUT_PADDING_RIGHT: "2.4rem",
  },
  // Client bulk import (EntityImportModal)
  IMPORT: {
    MODAL_WIDTH_PX: 800,
    ROW_COLUMN_HEADER: "Row",
    ROW_COLUMN_WIDTH_PX: 60,
    REASON_SEPARATOR: "; ",
    // The values the server uses for an issue's severity and an item's status.
    ERROR_SEVERITY: "Error",
    UPDATE_STATUS: "Update",
    FIELD_COLUMN_HEADER: "Field",
    REASON_COLUMN_HEADER: "Reason",
    MAX_FILE_SIZE_BYTES: 10 * 1024 * 1024,
    FILE_EXTENSION: ".xlsx",
    PREVIEW_ERROR_COUNT: 5,
    MODE_VALIDATE: "validate" as const,
    MODE_IMPORT: "import" as const,
    MODAL_TITLE: "Import Clients",
    DOWNLOAD_TEMPLATE_BUTTON_TEXT: "Download Template",
    DOWNLOAD_TEMPLATE_FAILED_MESSAGE: "Failed to download template",
    // Saved file name of the downloaded template (the server's Content-Disposition is not readable from the browser).
    TEMPLATE_FILE_NAME: "Client_Import_Template.xlsx",
    SELECT_HINT: "Upload an .xlsx file of Partners and Partnerships. A file with no errors is imported straight away; otherwise you review it first.",
    SCOPED_HINT: (name: string) =>
      `Only partners of ${name} can be imported here. Rows with only partner details are added to ${name}, rows with only partnership details are created (if new) and added under ${name}, and rows for another partnership with a partner are reported as errors.`,
    SCOPED_HINT_GENERIC:
      "Only partners of this partnership can be imported here. Rows with only partner details are added to this partnership, rows with only partnership details are created (if new) and added under this partnership, and rows for another partnership with a partner are reported as errors.",
    CONFIRM_TITLE: "Ready to import",
    CONFIRM_TEXT: "No problems were found in this file. Nothing is saved until you confirm.",
    CONFIRM_IMPORT_LABEL: "Confirm Import",
    DRAGGER_TEXT: "Click or drag an .xlsx file here",
    FILE_TYPE_HINT: ".xlsx files only · max 10 MB",
    FILE_TOO_LARGE_MESSAGE: "File exceeds 10 MB limit",
    FILE_EMPTY_MESSAGE: "The selected file is empty",
    VALIDATING_TEXT: "Checking file…",
    IMPORTING_TEXT: "Importing clients…",
    FILE_ERROR_TITLE: "This file can't be imported",
    FILE_ERROR_FALLBACK_MESSAGE: "The file could not be checked. Please try again.",
    ERROR_ROW_TEXT: (rowNumber: number | string) => `Row ${rowNumber}`,
    MORE_ERRORS_TEXT: (count: number) =>
      `…and ${count} more. Download the error report to see all of them.`,
    SKIPPED_ROWS_TITLE: "Skipped rows",
    CANCEL_BUTTON_LABEL: "Cancel",
    CLOSE_BUTTON_LABEL: "Close",
    DONE_BUTTON_LABEL: "Done",
    CHOOSE_ANOTHER_FILE_LABEL: "Choose Another File",
    DOWNLOAD_ERROR_REPORT_LABEL: "Download Error Report",
    PREPARING_ERROR_REPORT_LABEL: "Preparing Report…",
    ERROR_REPORT_DOWNLOAD_FAILED_MESSAGE: "The error report could not be downloaded. Please try again.",
    // Saved name of the report the server builds from the uploaded file (the server sends no file name).
    UPLOADED_FILE_ERROR_REPORT_NAME: "Client_Import_Error_Report.xlsx",
    IMPORT_FAILED_MESSAGE: "Import failed. Please try again.",
    NO_CLIENT_MESSAGE: "Select a head office before importing clients.",
    ERROR_REPORT_FILE_NAME: "client-import-error-report.xlsx",
    IMPORT_BUTTON_LABEL: "Import",
    SKIP_BUTTON_LABEL: "Skip",
    ENTITIES_TO_CREATE_TEXT: (n: number) => `${n} ${n === 1 ? "client" : "clients"} to create`,
    ENTITIES_EXISTING_TEXT: (n: number) => `${n} already ${n === 1 ? "exists" : "exist"}`,
    LINKS_TO_CREATE_TEXT: (n: number) => `${n} partner ${n === 1 ? "link" : "links"} to create`,
    LINKS_TO_UPDATE_TEXT: (n: number) => `${n} existing partner ${n === 1 ? "link" : "links"} to update`,
    LINKS_EXISTING_TEXT: (n: number) => `${n} already linked`,
    UPDATES_TITLE: "Existing partners that will be updated",
    // The role label is shown only when the server names the row's role (entityRole).
    ENTITY_ROLE_LABELS: {
      Partnership: "Partnership",
      Partner: "Partner",
    } as Record<string, string>,
    UPDATE_LINE_TEXT: (rowNumber: number | string, entity: string, partnership: string, changes: string[] = [], role: string = "") =>
      `Row ${rowNumber}: ${role ? `${role} ` : ""}${entity}${partnership ? ` in ${partnership}` : ""} — ${changes.join("; ")}`,
    LINKS_UPDATED_TEXT: (n: number) => `${n} existing partner ${n === 1 ? "link" : "links"} updated`,
    ERROR_ROWS_TEXT: (n: number) => `${n} ${n === 1 ? "row has" : "rows have"} errors and will be skipped`,
    NOTHING_TO_IMPORT_TEXT: "Everything in this file is already in the system, so there is nothing to import.",
    NOTICES_TITLE: "Notes",
    NOTICE_PREVIEW_COUNT: 10,
    MORE_NOTICES_TEXT: (n: number) => `…and ${n} more.`,
    ROW_NOTICE_TEXT: (rowNumber: number | string, message: string) => `Row ${rowNumber}: ${message}`,
    CREATED_TEXT: (entities: number, links: number) =>
      `${entities} ${entities === 1 ? "client" : "clients"} and ${links} partner ${links === 1 ? "link" : "links"} created`,
    ALREADY_TEXT: (entities: number, links: number) =>
      `${entities} already existed and ${links} already linked, left unchanged`,
    SKIPPED_TEXT: (n: number) => `${n} ${n === 1 ? "row" : "rows"} skipped because of errors`,
    WARNINGS_TITLE: "Warnings",
  },
  EDIT_ENTITY_MODAL_TITLE: "Edit Client",
  DEACTIVATE_ENTITY_CONFIRM_TITLE: "Deactivate client?",
  ENTITY_DEACTIVATED_MESSAGE: "Client deactivated",
  IS_EXTERNAL_CHECKBOX_LABEL: "External (K-1 uploaded, not calculated)",
  IS_EXTERNAL_CHECKBOX_HINT: "This partnership's K-1 is received as a PDF from outside the platform, not produced by its own calculation.",

  ENTITY_PICKER: {
    PAGE_SIZE: 20,
    SEARCH_DEBOUNCE_MS: 400,
    LOAD_MORE_OFFSET: 48,
    LOADING_MORE_TEXT: "Loading more entities…",
  },

  LIST: {
    PARTNERSHIP_BADGE_LABEL: "Partnership",
    TYPE_FILTER_OPTIONS: [
      { label: "All", value: "all" },
      { label: "Partnerships", value: "partnership" },
      { label: "Partners", value: "partner" },
    ],
    // Sent as partnerCategory on every list fetch: the server applies it to Partner rows only.
    DEFAULT_PARTNER_CATEGORY: "Individual",
    TYPE_FILTER_ARIA_LABEL: "Filter by client type",
    TYPE_FILTER_ALL_VALUE: "all",
    SEARCH_PARAM_KEY: "q",
    TYPE_PARAM_KEY: "type",
    ENT_ID_PREFIX: ENTITY_CODE_PREFIX,
    ENT_ID_PAD_LENGTH: ENTITY_CODE_PAD_LENGTH,
    UNEXPECTED_LIST_RESPONSE_MESSAGE: "The entity list response was not in the expected paged format",
    TABLE_COLUMN_KEYS: ENTITY_LIST_COLUMN_KEYS,
    TABLE_COLUMN_COUNT: ENTITY_LIST_COLUMN_KEYS.length,
    ENTITY_CODE_LABEL: (id: string | number) =>
      `${ENTITY_CODE_PREFIX}${String(id).padStart(ENTITY_CODE_PAD_LENGTH, "0")}`,
    SEARCH_DEBOUNCE_MS: 400,
    NO_FILTER_MATCHES_TEXT: "No clients match your search or filter",
    NO_ENTITIES_FOR_CLIENT_TEXT:
      "No clients found for this head office. Click + Add client to add one.",
    TABLE_FOOTER_ENTITIES_TEXT: (shown: number, total: number) =>
      `Showing ${shown} of ${total} ${total !== 1 ? "clients" : "client"}`,
    PARTNER_BADGE_LABEL: "Partner",
    PARTNERSHIP_NAME_REQUIRED_MESSAGE: "Partnership name is required",
    EIN_LABEL: "EIN",
    EIN_HINT: "Format: XX-XXXXXXX",
    EIN_PLACEHOLDER: "12-3456789",
    ENTITY_NAME_PLACEHOLDER: "e.g. TechCorp Global LP",
    TAX_YEAR_START_LABEL: "Tax Year Start",
    TAX_YEAR_START_OPTIONS: ["January 1", "April 1", "July 1", "October 1"],
    PARTNERSHIP_ADDRESS_PLACEHOLDER: "1200 Innovation Way, Suite 400",
    PARTNERSHIP_CITY_PLACEHOLDER: "Palo Alto",
    PARTNERSHIP_STATE_PLACEHOLDER: "CA",
    PARTNERSHIP_ZIP_PLACEHOLDER: "94304",
    PARTNERSHIP_NOTES_PLACEHOLDER: "Notes about this client...",
    SAVE_ENTITY_BUTTON_LABEL: "Save Client",
    FULL_NAME_LABEL: "Full Name",
    FULL_NAME_REQUIRED_MESSAGE: "Full name is required",
    ENTITY_TYPES_LOAD_ERROR_MESSAGE: "Could not load the tax entity type list. Please reload and try again.",
    FULL_NAME_PLACEHOLDER: "e.g. James Worthington",
    SSN_LABEL: "SSN",
    SSN_ENCRYPTED_BADGE_TEXT: "Encrypted",
    SSN_PLACEHOLDER: "123-45-6789",
    SSN_HINT:
      "SSN is encrypted at rest. Only last 4 digits visible after save.",
    PARTNER_ADDRESS_PLACEHOLDER: "123 Main St",
    PARTNER_CITY_PLACEHOLDER: "New York",
    PARTNER_STATE_PLACEHOLDER: "NY",
    PARTNER_ZIP_PLACEHOLDER: "10001",
    PARTNER_NOTES_PLACEHOLDER: "Notes about this partner...",
    SAVE_PARTNER_BUTTON_LABEL: "Save Partner",
    ADD_ENTITY_MODAL_TITLE: "Add Client",
    ADD_ENTITY_MODAL_SUBTITLE: "What type of client would you like to create?",
    PARTNERSHIP_CARD_TITLE: "Partnership",
    PARTNERSHIP_CARD_SUBTEXT:
      "A legal entity (LP, LLC, S-Corp) that files its own return",
    PARTNER_CARD_TITLE: "Partner",
    PARTNER_CARD_SUBTEXT:
      "An individual or entity that holds interest in a partnership",
    ENTITY_SAVED_MESSAGE: (mode: "create" | "edit") =>
      `Client ${mode === "create" ? "created" : "updated"} successfully`,
    CREATE_PARTNERSHIP_MODAL_TITLE: "Create Partnership",
    CREATE_PARTNER_MODAL_TITLE: "Create Partner",
    FAILED_TO_LOAD_ENTITIES_MESSAGE: "Failed to load clients",
    PAGE_TITLE: "Clients",
    IMPORT_SELECT_CLIENT_MESSAGE: "Select a head office before importing clients.",
    IMPORT_BUTTON_LABEL: "Import",
    ADD_CLIENT_BUTTON_LABEL: "+ Add client",
    SEARCH_ENTITIES_PLACEHOLDER: "Search by name, type, category, city or state...",
    NO_ENTITIES_FOUND_TEXT:
      "No clients found. Click + Add client to add one.",
    CLIENT_NAME_COLUMN_HEADER: "Client name",
    TYPE_COLUMN_HEADER: "Type",
    TAX_ENTITY_TYPE_COLUMN_HEADER: "Tax entity type",
    STATUS_COLUMN_HEADER: "Status",
    NO_SEARCH_MATCHES_TEXT: "No partnerships match your search",
    NO_PARTNERSHIPS_FOUND_TEXT:
      "No partnerships found for this head office. Click + Add client to add one.",
    DEACTIVATE_ENTITY_CONFIRM_CONTENT: (name: string) =>
      `This will mark "${name}" as inactive.`,
    TABLE_FOOTER_TEXT: (shown: number, total: number) =>
      `Showing ${shown} of ${total} partnership${total !== 1 ? "s" : ""}`,
  },

  DETAILS: {
    DETAIL_TABS: [
      { key: "projects", label: "Projects" },
      { key: "engagements", label: "Engagements" },
      { key: "auditLog", label: "Audit Log" },
    ],
    ENTITY_NOT_FOUND_TEXT: "Client not found",
    ENTITY_NOT_FOUND_SUBTEXT:
      "No client with this ID was found in your tenant.",
    FAILED_TO_LOAD_OWNERSHIP_TEXT: "Failed to load ownership data",
    NO_OWNERSHIP_RELATIONSHIPS_TEXT: "No ownership relationships",
    NO_OWNERSHIP_RELATIONSHIPS_SUBTEXT:
      "No tiering relationships are defined for this client. Use the Tiering module to define ownership structure.",
    OPEN_IN_TIERING_VIEW_BUTTON_LABEL: "Open in Tiering View →",
    PARTNER_NAME_COLUMN_HEADER: "Partner Name",
    RELATIONSHIP_COLUMN_HEADER: "Relationship",
    OWNERSHIP_PERCENT_COLUMN_HEADER: "Ownership %",
    TIER_LEVEL_COLUMN_HEADER: "Tier Level",
    TIER_LEVEL_VALUE: (level: number) => `Level ${level}`,
    FAILED_TO_LOAD_PROJECTS_TEXT: "Failed to load projects",
    NO_PROJECTS_YET_TEXT: "No projects yet",
    NO_PROJECTS_YET_SUBTEXT: "No projects are linked to this client.",
    PROJECTS_TAB_ARIA_LABEL: (label: string, count: number) =>
      `${label}, ${count} project${count !== 1 ? "s" : ""}`,
    PROJECT_NAME_COLUMN_HEADER: "Project Name",
    TAX_YEAR_COLUMN_HEADER: "Tax Year",
    STATUS_COLUMN_HEADER: "Status",
    NAME_REQUIRED_MESSAGE: "Name is required",
    ENTITY_UPDATED_MESSAGE: "Client updated",
    CATEGORY_LABEL: "Category",
    ENTITY_NAME_INPUT_PLACEHOLDER: "Client name",
    ADDRESS_PLACEHOLDER: "123 Main St",
    CITY_PLACEHOLDER: "New York",
    STATE_PLACEHOLDER: "NY",
    ZIP_PLACEHOLDER: "10001",
    SAVE_CHANGES_BUTTON_LABEL: "Save Changes",
    EDIT_MODAL_WIDTH_PX: 520,
    ENTITY_DETAILS_CARD_TITLE: "Client Details",
    COLLAPSE_INFO_CARDS_ARIA_LABEL: "Collapse client details",
    EXPAND_INFO_CARDS_ARIA_LABEL: "Expand client details",
    BREADCRUMB_ROOT_LABEL: "Clients",
    NAME_LABEL: "Name",
    ENTITY_ID_LABEL: "Client ID",
    CATEGORY_FIELD_LABEL: "Category",
    LOCATION_SYSTEM_CARD_TITLE: "Location & System",
    TAX_YEAR_START_LABEL: "Tax Year Start",
    DATE_CREATED_LABEL: "Date Created",
    CITY_FIELD_LABEL: "City",
    STATE_FIELD_LABEL: "State",
    ADDRESS_FIELD_LABEL: "Address",
    ENGAGEMENTS_COMING_SOON_TITLE: "Engagements",
    AUDIT_LOG_COMING_SOON_TITLE: "Audit Log",
    DEACTIVATE_ENTITY_CONFIRM_CONTENT: (name: string) =>
      `This will mark "${name}" as inactive. You can reactivate it later.`,
  },
};

//InputReadiness constants
export const INPUT_READINESS_CONSTANT = {
  PANEL_TITLE: "Input readiness",
  EXPAND_ARIA_LABEL: "Show input readiness",
  COLLAPSE_ARIA_LABEL: "Hide input readiness",
  LOADING_TEXT: "Checking inputs...",
  UNAVAILABLE_TEXT: "Input readiness is unavailable right now.",
  NO_SHEETS_TEXT: "There are no input sheets to report on.",
  INPUT_SECTION_LABEL: "Input sections",
  SECTION_MISSING_TOOLTIP: "Input missing",
  SECTION_LOADED_TOOLTIP: "Input loaded",
  SELECT_SHEET_TOOLTIP: "Open this sheet in the workspace",
  STATUS_LABELS: {
    missing: "Missing",
    partial: "Partial",
    ready: "Ready",
    chained: "Chained",
    optional: "Optional",
  },
  SUMMARY_TEXT: (missing: number, ready: number, chained: number, optional: number) =>
    `${missing} missing · ${ready} ready · ${chained} chained · ${optional} optional`,
  ROW_COUNT_TEXT: (count: number) => `${count} ${count === 1 ? "row" : "rows"}`,
  LAST_LOADED_TEXT: (when: string) => `Loaded ${when}`,
  NEVER_LOADED_TEXT: "No data loaded",
  LOADED_ROWS_NOT_SHOWN_TEXT: "Rows loaded from the Inputs tab are not shown here. Save or Upload Data will replace them.",
  INPUTS_TAB_ROWS_NOTICE_TEXT: "These rows were loaded from the Inputs tab. To change them, use Replace data on the Inputs tab.",
  INPUTS_TAB_ROWS_ACTION_DISABLED_TITLE: "These rows were loaded from the Inputs tab. Use Replace data on the Inputs tab to change them.",
  SOURCE_TEXT: {
    manual: "entered in workspace",
    upload: "uploaded",
    roster: "filled from the partner list",
    rollforward: "rolled forward from last year",
  } as Record<string, string>,
};

//InputWizard constants
export const INPUT_WIZARD_CONSTANT = {
  PROJECT_INPUTS: {
    HTTP_STATUS: {
      BAD_REQUEST: 400,
      FORBIDDEN: 403,
      NOT_FOUND: 404,
      SERVER_ERROR_MIN: 500,
    } as const,
    HASH_MISMATCH_PATTERN: /hash mismatch/i,
    COMMIT_STATUS: { CURRENT: "Current", REJECTED: "Rejected", NO_CHANGE: "NoChange" } as const,
    DIAGNOSTICS_STATUS: { ERRORS: "Errors" } as const,
    READINESS_STATUS: { READY: "ready", CHAINED: "chained" } as const,
    SHEET_SOURCE: {
      ROSTER: "roster",
      ROLLFORWARD: "rollforward",
      UPLOAD: "upload",
      MANUAL: "manual",
    } as const,
    EXTERNAL_K1_STATUS: { UPLOADED: "Uploaded" } as const,
    SSO_RESULT: {
      LOADED: "loaded",
      UNMATCHED: "unmatched",
      FAILED: "failed",
      NO_SESSION: "no-session",
    } as const,
  },
  ROLL_FORWARD_PAGE: {
    DIALOG_WIDTH_PX: 860,
    PREVIEW_ROW_COLUMN_WIDTH_PX: 70,
    PREVIEW_TABLE_SCROLL_HEIGHT_PX: 220,
    DROPPED_KEY_SEPARATOR_TEXT: " · ",
    PREVIEW_STATUS: { SOURCE_REQUIRED: "SourceRequired" } as const,
  },
  WIZARD_PAGE: {
    FORBIDDEN_STATUS_CODE: 403,
    NOT_FOUND_STATUS_CODE: 404,
    SERVER_ERROR_MIN_STATUS_CODE: 500,
    MODAL_WIDTH_PX: 900,
    CLOSE_CONFIRM_Z_INDEX: 1100,
    NEEDS_TABLE_SCROLL_HEIGHT_PX: 220,
    MAP_TABLE_SCROLL_HEIGHT_PX: 320,
    PREVIEW_TABLE_SCROLL_HEIGHT_PX: 240,
    PREVIEW_ROW_COLUMN_WIDTH_PX: 70,
    DIAG_ROW_COLUMN_WIDTH_PX: 80,
    DIAG_COLUMN_COLUMN_WIDTH_PX: 160,
    DIAG_TYPE_COLUMN_WIDTH_PX: 100,
    DIAG_PAGE_SIZE: 10,
    MAP_SAMPLES_SHOWN: 3,
    ACCEPTED_FILE_TYPES: ".xlsx,.csv",
    FILE_TYPE_REGEX: /\.(xlsx|csv)$/i,
    DIAG_FILTER_ALL_VALUE: "All" as const,
    RESULT_STATUS: { CURRENT: "Current", REJECTED: "Rejected", NO_CHANGE: "NoChange" } as const,
    DIAGNOSTICS_STATUS: { ALL_CLEAR: "AllClear", WARNINGS: "Warnings", ERRORS: "Errors" } as const,
    SEVERITY: { ERROR: "Error", WARNING: "Warning", INFO: "Info" } as const,
    MAP_ORIGIN_VALUE: { USER: "user", NONE: "none" } as const,
    CSV: {
      BOM: "\uFEFF",
      MIME_TYPE: "text/csv;charset=utf-8",
      DELIMITER: ",",
      LINE_BREAK: "\r\n",
      QUOTE: "\"",
      ESCAPED_QUOTE: "\"\"",
      FORMULA_ESCAPE_PREFIX: "'",
      QUOTE_REGEX: /"/g,
      NEEDS_QUOTING_REGEX: /[",\n\r]/,
    },
  },
  TAB_LABEL: "Inputs",
  EXTERNAL_K1_KIND: "ExternalK1",
  NUMERIC_DATA_TYPES: ["Decimal", "Integer"],
  ISSUE_CODE: {
    ROSTER_CHANGED: "IMP-400-ROSTER-CHANGED",
    ROSTER_UNSUPPORTED: "IMP-400-ROSTER-UNSUPPORTED",
    ROLLFORWARD_CHANGED: "IMP-400-ROLLFWD-CHANGED",
    ROLLFORWARD_UNSUPPORTED: "IMP-400-ROLLFWD-UNSUPPORTED",
    ROLLFORWARD_NO_SOURCE: "IMP-400-ROLLFWD-NO-SOURCE",
    ROLLFORWARD_SOURCE_NOT_COMPLETED: "IMP-400-ROLLFWD-SOURCE-NOT-COMPLETED",
    ROLLFORWARD_SOURCE_REQUIRED: "IMP-400-ROLLFWD-SOURCE-REQUIRED",
  },
  LOADING_TEXT: "Checking your inputs...",
  UNAVAILABLE_TEXT: "We couldn't load the list of inputs right now.",
  TRY_AGAIN_TEXT: "Try again",
  NO_SHEETS_TEXT: "This project has no input sheets to load.",
  NO_LOAD_PERMISSION_TEXT: "You do not have permission to load data on this project.",
  REFRESH_PERMISSIONS_TEXT: "Refresh permissions",
  REFRESHING_PERMISSIONS_TEXT: "Refreshing...",
  REFRESH_UNMATCHED_TEXT: "Your account could not be matched to a user profile. Contact an administrator.",
  REFRESH_FAILED_TEXT: "We couldn't refresh your permissions right now. Try again.",
  REFRESH_NO_SESSION_TEXT: "Permissions refresh is only available when signed in with single sign-on.",
  REFRESH_NO_CHANGE_TEXT: "Your permissions were refreshed. This account has no permission to load data on this project.",
  READINESS_MESSAGE_PREFIX: "Note: ",
  BANNER_READY: "Ready to run",
  BANNER_NEEDS_DATA: (missing: number, unloadable: number) =>
    `${missing} required ${missing === 1 ? "sheet needs" : "sheets need"} data` +
    (unloadable > 0 ? ` (${unloadable} can't be loaded here yet)` : ""),
  PERIOD_LABEL: "Period",
  PERIOD_PLACEHOLDER: "Choose a period",
  PERIOD_REQUIRED_HINT: "Choose a period to see and load its inputs.",
  CHIP: {
    missing: "Missing",
    loaded: "Loaded",
    optionalEmpty: "Optional, not loaded",
    chained: "Filled automatically",
    cannotLoad: "Can't load here",
  },
  REQUIRED_TEXT: "Required",
  OPTIONAL_TEXT: "Optional",
  NO_DATA_TEXT: "No data yet",
  ROWS_TEXT: (count: number) => `${count} ${count === 1 ? "row" : "rows"}`,
  SOURCE_IMPORTED: (fileName: string, rows: string, when: string | null, who?: string | null) =>
    `Imported from ${fileName} · ${rows}${when ? ` · ${when}` : ""}${who ? ` · by ${who}` : ""}`,
  SOURCE_ROSTER: (rows: string, when: string | null, who?: string | null) =>
    `Filled from the partner list · ${rows}${when ? ` · ${when}` : ""}${who ? ` · by ${who}` : ""}`,
  SOURCE_ROLLFORWARD: (rows: string, when: string | null, who?: string | null) =>
    `Rolled forward from last year · ${rows}${when ? ` · ${when}` : ""}${who ? ` · by ${who}` : ""}`,
  SOURCE_MANUAL: (rows: string, when: string | null) =>
    `Entered by hand in the workspace · ${rows}${when ? ` · ${when}` : ""}`,
  SOURCE_UPLOADED_IN_WORKSPACE: (rows: string, when: string | null) =>
    `Uploaded in the workspace · ${rows}${when ? ` · ${when}` : ""}`,
  SOURCE_LOADED_OTHER: (rows: string, when: string | null) =>
    `Loaded · ${rows}${when ? ` · ${when}` : ""}`,
  FILLED_FROM: (hint: string) => `Filled automatically from ${hint}`,
  FILLED_FROM_OTHER: "Filled automatically from another calculation",
  ACTION_LOAD: "Load data",
  ACTION_REPLACE: "Replace data",
  ACTION_ARIA: (verb: string, sheet: string) => `${verb} for ${sheet}`,
  VIEW_ROWS_ACTION: "View rows",
  VIEW_ROWS_HIDE_ACTION: "Hide rows",
  VIEW_ROWS_ARIA: (sheet: string) => `View rows of ${sheet}`,
  VIEW_ROWS_HIDE_ARIA: (sheet: string) => `Hide rows of ${sheet}`,
  VIEW_ROWS_REGION_ARIA: (sheet: string) => `Rows of ${sheet}`,
  VIEW_ROWS_LIMIT: 100,
  VIEW_ROWS_LOADING: "Loading the rows...",
  VIEW_ROWS_FAILED: "We couldn't load the rows. Please try again.",
  VIEW_ROWS_RETRY: "Try again",
  VIEW_ROWS_EMPTY: "There are no rows to show for this sheet.",
  VIEW_ROWS_READ_ONLY: "Read only. Use Replace data to change these rows.",
  VIEW_ROWS_COUNT: (shown: number, total: number) =>
    shown < total
      ? `Showing the first ${shown.toLocaleString()} of ${total.toLocaleString()} rows.`
      : `${total.toLocaleString()} ${total === 1 ? "row" : "rows"}.`,
  VIEW_ROWS_COLUMN_FALLBACK: (index: number) => `Column ${index}`,
  GROUP_ARIA: (calc: string) => `Input sheets for ${calc}`,

  FILL_ACTION: "Fill from partner list",
  FILL_ARIA: (sheet: string) => `Fill from partner list for ${sheet}`,
  FILL_DIALOG_TITLE: (sheet: string) => `Fill ${sheet} from the partner list`,
  FILL_READING: "Reading the partner list...",
  FILL_SUMMARY: (added: number, updated: number, kept: number) =>
    `${added} ${added === 1 ? "partner" : "partners"} will be added, ${updated} updated and ${kept} kept on the sheet.`,
  FILL_ROWS_TOTAL: (rows: number) => `The sheet will have ${rows} ${rows === 1 ? "row" : "rows"}.`,
  FILL_OWNERSHIP: (pct: string) => `The partners filled in own ${pct}% in total.`,
  FILL_LOOK_THROUGH: (count: number, names: string) =>
    `${count} look-through ${count === 1 ? "partner is" : "partners are"} left out: ${names}.`,
  FILL_PARTNERSHIPS: (count: number) =>
    `${count} ${count === 1 ? "partner on the list is itself a partnership and is" : "partners on the list are themselves partnerships and are"} filled in like any other partner.`,
  FILL_NOTES_HEADING: "How the sheet will be filled",
  FILL_CLASS_CHANGE_TITLE: "Some partner IDs will change",
  FILL_CLASS_CHANGE_INTRO: "A partner's class changed, which changes its ID on this sheet. Rows on other sheets that still use the old ID need updating:",
  FILL_CLASS_CHANGE_TICK: "I understand these IDs will change and the rows listed need updating.",
  FILL_BLOCKED_ADVICE: "Fix these on the partner list or on the sheet, then check again.",
  FILL_CAPPED_PART: (shown: number, total: number, noun: string) => `${shown} of ${total} ${noun}`,
  FILL_CAPPED: (parts: string) => `Showing ${parts}. Fix the first ones, then check again.`,
  FILL_NO_CHANGE: "The sheet already matches the partner list.",
  FILL_CONFIRM_BUTTON: "Fill the sheet",
  FILL_CHECK_AGAIN: "Check again",
  FILL_FILLING: "Filling the sheet...",
  FILL_RESULT_NO_CHANGE: "The sheet already matches the partner list. Nothing was changed.",
  FILL_RESULT_REJECTED: "Nothing was filled because of problems.",
  FILL_ROSTER_CHANGED_NOTICE: "The partner list or the sheet changed since you previewed, so we checked again. Please review before filling.",
  FILL_FAILED: "We couldn't read the partner list. Please try again.",
  FILL_UNSUPPORTED: "This sheet cannot be filled from the partner list.",
  FILL_UNKNOWN_OUTCOME_TITLE: "We couldn't confirm whether the sheet was filled.",

  ROLLFORWARD_ACTION: "Roll forward from last year",
  ROLLFORWARD_ARIA: (sheet: string) => `Roll forward from last year for ${sheet}`,
  ROLLFORWARD_DIALOG_TITLE: (sheet: string) => `Roll ${sheet} forward from last year`,
  ROLLFORWARD_READING: "Reading last year's results...",
  ROLLFORWARD_SOURCE: (name: string, year: number | null) => `From ${name}${year !== null ? ` (${year})` : ""}.`,
  ROLLFORWARD_SUMMARY: (copied: number, updated: number, kept: number) =>
    `${copied} ${copied === 1 ? "row" : "rows"} will be copied from last year, ${updated} replaced and ${kept} kept on the sheet.`,
  ROLLFORWARD_ROWS_TOTAL: (rows: number) => `The sheet will have ${rows} ${rows === 1 ? "row" : "rows"}.`,
  ROLLFORWARD_DROPPED: (count: number) =>
    `${count} ${count === 1 ? "row" : "rows"} from last year ${count === 1 ? "was" : "were"} left out because ${count === 1 ? "that partner is" : "those partners are"} not on this year's partner list:`,
  ROLLFORWARD_DROPPED_SHOWN: (shown: number, total: number) => `Showing ${shown} of ${total}.`,
  ROLLFORWARD_UNMATCHED: (count: number) =>
    `${count} ${count === 1 ? "row" : "rows"} from last year did not apply to this year's partner list and ${count === 1 ? "was" : "were"} not copied.`,
  ROLLFORWARD_PARTNER_LIST_FOOTNOTE: "If rows were left out and that looks wrong, fill or update the partner list first, then check again.",
  ROLLFORWARD_NOTES_HEADING: "How last year's values are matched",
  ROLLFORWARD_BLOCKED_ADVICE: "Fix these, then check again.",
  ROLLFORWARD_NO_CHANGE: "The sheet already matches last year's results.",
  ROLLFORWARD_CONFIRM_BUTTON: "Roll forward",
  ROLLFORWARD_CHECK_AGAIN: "Check again",
  ROLLFORWARD_ROLLING: "Rolling the sheet forward...",
  ROLLFORWARD_RESULT_NO_CHANGE: "The sheet already matches last year's results. Nothing was changed.",
  ROLLFORWARD_RESULT_REJECTED: "Nothing was rolled forward because of problems.",
  ROLLFORWARD_CHANGED_NOTICE: "The sheets or last year's results changed since you previewed, so we checked again. Please review before rolling forward.",
  ROLLFORWARD_FAILED: "We couldn't read last year's results. Please try again.",
  ROLLFORWARD_UNSUPPORTED: "This sheet cannot be rolled forward.",
  ROLLFORWARD_NO_SOURCE: "There is no prior-year project for this partnership to roll forward from.",
  ROLLFORWARD_SOURCE_NOT_COMPLETED: "Last year's project has no completed calculation yet.",
  ROLLFORWARD_CANDIDATE_SUFFIX_TEXT: (completed: string) => ` · ${completed}`,
  ROLLFORWARD_PICK_HEADING: "More than one project from a prior year could be used. Choose the one to roll forward from:",
  ROLLFORWARD_PICK_ARIA: "Prior-year projects",
  ROLLFORWARD_PICK_COMPLETED: (when: string) => `Completed ${when}`,
  ROLLFORWARD_PICK_NONE: "We couldn't find a project to choose from. Please try again.",
  ROLLFORWARD_CONTINUE: "Continue",
  ROLLFORWARD_UNKNOWN_OUTCOME_TITLE: "We couldn't confirm whether the sheet was rolled forward.",

  WIZARD_TITLE: (sheet: string) => `Load data into ${sheet}`,
  STEP_TITLES: ["Choose your file", "Match your columns", "Check results", "Done"],
  STEP_PROGRESS: (n: number, total: number, title: string) => `Step ${n} of ${total}: ${title}`,
  SPEC_LOADING: "Reading what this sheet needs...",
  SPEC_FAILED: "We couldn't read what this sheet needs.",
  NEEDS_HEADING: "What this sheet needs",
  NEEDS_COLUMNS: ["Column", "Type", "Required?", "Example", "Allowed values"],
  NEEDS_ROW_LIMIT: (limit: number) => `Up to ${limit.toLocaleString()} rows per file.`,
  TYPE_LABELS: {
    Text: "Text",
    Decimal: "Number",
    Integer: "Whole number",
    Date: "Date",
    List: "One of a list",
  } as Record<string, string>,
  YES_TEXT: "Yes",
  NO_TEXT: "No",
  EXAMPLE_FILE_BUTTON: "Download an example file (CSV)",
  EXAMPLE_FILE_NAME: (sheet: string) => `${sheet} - example.csv`,
  REPLACE_NOTICE: (rows: number) =>
    `This sheet already has ${rows} ${rows === 1 ? "row" : "rows"}. Loading a new file replaces them.`,
  CHOOSE_FILE_BUTTON: "Choose a file",
  CHOOSE_FILE_HINT: "Excel (.xlsx) or CSV (.csv), up to 20 MB.",
  FILE_CHOSEN: (name: string) => `Selected file: ${name}`,
  MAX_FILE_BYTES: 20 * 1024 * 1024,
  FILE_TOO_BIG: "This file is larger than 20 MB. Split it into smaller files.",
  FILE_WRONG_TYPE: "Only Excel (.xlsx) and CSV (.csv) files can be loaded.",
  FILE_EMPTY: "This file is empty.",
  CHECKING_FILE: "Checking your file...",
  MAP_INTRO: "Tell us which column in your file holds each piece of information. Required items are marked *.",
  MAP_COLUMNS: ["What we need", "Column in your file", "Examples from your file", "How it was matched"],
  MAP_LEAVE_EMPTY: "Leave empty",
  MAP_COLUMN_FALLBACK_LABEL: (position: number) => `(column ${position})`,
  MAP_REQUIRED_MARKER_TEXT: " *",
  MAP_SAMPLES_SEPARATOR_TEXT: " · ",
  MAP_SELECT_ARIA: (target: string) => `Column in your file for ${target}`,
  MAP_ORIGIN: {
    saved: "Remembered from last time",
    auto: "Matched automatically",
    user: "You chose this",
    none: "Not matched yet",
  } as Record<string, string>,
  MAP_REQUIRED_BLOCK: "Match every required column (marked *) to continue.",
  MAP_DIRTY_BLOCK: "You changed the matches. Choose “Update check” to re-check your file before continuing.",
  MAP_UPDATE_BUTTON: "Update check",
  HEADER_ROW_NOTE: (row: number) => `We used row ${row} of your file as the heading row.`,
  BUTTON_BACK: "Back",
  BUTTON_NEXT: "Next",
  BUTTON_CANCEL: "Cancel",
  BUTTON_CLOSE: "Close",
  BUTTON_DONE: "Done",
  BUTTON_CHOOSE_OTHER_FILE: "Choose a different file",
  BUTTON_CHANGE_MATCHES: "Change column matches",
  BUTTON_APPROVE: "Approve and load",
  REVIEW_STATUS: {
    AllClear: "Looks good",
    Warnings: "Looks good, with warnings",
    Errors: "Problems to fix",
  } as Record<string, string>,
  REVIEW_COUNTS: (errors: number, warnings: number) =>
    `${errors} ${errors === 1 ? "problem" : "problems"} · ${warnings} ${warnings === 1 ? "warning" : "warnings"}`,
  REVIEW_CAPPED: (shown: number, total: number) =>
    `Showing the first ${shown} of ${total}. Fix the first ones, then check again.`,
  REVIEW_WILL_LOAD: (rows: number) => `${rows.toLocaleString()} ${rows === 1 ? "row" : "rows"} will be loaded.`,
  REVIEW_WILL_REPLACE: (rows: number) =>
    `This replaces ${rows.toLocaleString()} ${rows === 1 ? "row" : "rows"} currently in this sheet.`,
  REVIEW_SKIPPED: (rows: number) => `${rows} blank ${rows === 1 ? "row was" : "rows were"} skipped.`,
  REVIEW_BLOCKED: "Fix the problems above before you can load this file.",
  TOTALS_HEADING: "Totals in your file",
  TOTALS_COLUMNS: ["Column", "Group", "Rows", "Total"],
  PREVIEW_HEADING: "First rows",
  PREVIEW_NOTE: (shown: number, total: number) => `Showing the first ${shown} of ${total.toLocaleString()} rows.`,
  REMEMBER_MATCHES: "Remember these column matches for next time",
  DIAG_COLUMNS: ["Row", "Column", "Type", "What happened"],
  DIAG_FILTER_ALL: "All",
  DIAG_FILTER_ARIA: "Show",
  DIAG_CAPTION: "Problems and notes found in your file",
  DIAG_NO_ROW: "Whole file",
  DIAG_DOWNLOAD: "Download this list (CSV)",
  DIAG_CSV_HEADER: ["Row", "Column", "Type", "Code", "What happened"],
  DIAG_CSV_NAME: (sheet: string) => `${sheet} - problems.csv`,
  SEVERITY_LABELS: { Error: "Problem", Warning: "Warning", Info: "Note" } as Record<string, string>,
  COMMITTING: "Loading your data...",
  COMMITTING_LONG: "This is taking longer than usual. Large files can take a minute or two. Please keep this window open.",
  COMMIT_LONG_AFTER_MS: 20000,
  RESULT_LOADED: (rows: number, sheet: string) => `Loaded ${rows.toLocaleString()} ${rows === 1 ? "row" : "rows"} into ${sheet}.`,
  RESULT_REPLACED_PREVIOUS: "The previous file's rows were replaced.",
  RESULT_MAPPING_SAVED: "Your column matches were saved for next time.",
  RESULT_NO_CHANGE: "This is the same file that is already loaded. Nothing was changed.",
  RESULT_REJECTED: "Nothing was loaded because of problems in the file.",
  RESULT_DOWNLOAD_REPORT: "Download the problem list",
  REPORT_FILE_NAME: (sheet: string) => `${sheet} - problems.csv`,
  UNKNOWN_OUTCOME_TITLE: "We couldn't confirm whether your data was loaded.",
  UNKNOWN_OUTCOME_LOADED: "We checked: the data was loaded. The list behind this window is up to date.",
  UNKNOWN_OUTCOME_NOT_LOADED: "We checked: the data was not loaded. You can try again.",
  UNKNOWN_OUTCOME_UNCHECKED: "We couldn't check either. Close this window and look at the list to see whether the data is there before trying again.",
  HASH_MISMATCH_NOTICE: "Your file changed since the check, so we checked it again. Please review the results before loading.",
  ERR_FORBIDDEN: "You don't have access to this client's data.",
  ERR_NOT_FOUND: "This project could not be found.",
  ERR_PREVIEW_FAILED: "We couldn't check your file. Please try again.",
  CLOSE_CONFIRM_TITLE: "Leave without loading?",
  CLOSE_CONFIRM_BODY: "Your file and column matches haven't been loaded and will be lost.",
  CLOSE_CONFIRM_STAY: "Keep working",
  CLOSE_CONFIRM_LEAVE: "Leave",

  DIAGNOSTICS: {
    "IMP-E-ROSTER-EMPTY": { title: "The partner list is empty", advice: "Add partners to this partnership's partner list, then check again." },
    "IMP-E-ROSTER-NO-NAME": { title: "A partner has no name", advice: "Give it a name on the partner list." },
    "IMP-E-ROSTER-NO-CLASS": { title: "A partner has no interest class", advice: "Set a class on the partner list." },
    "IMP-E-ROSTER-DUPLICATE-NAME": { title: "Two partners have the same name", advice: "Rename one of them on the partner list." },
    "IMP-E-SHEET-DUPLICATE-NAME": { title: "The sheet has two rows with the same name", advice: "Remove one of the rows from the sheet." },
    "IMP-E-ROSTER-SHEET-MISMATCH": { title: "The saved rows don't match the template's columns", advice: "Load the sheet again from a file, then fill it." },
    "IMP-E-ROSTER-NO-ID-FORMAT": { title: "We can't tell how IDs are written on this sheet", advice: "Check how the template sets up its IDs." },
    "IMP-W-ROSTER-CLASS-CHANGED": { title: "A partner's ID will change", advice: "Update the rows on other sheets that use the old ID." },
    "IMP-W-ROSTER-ROW-KEPT": { title: "A row on the sheet is not on the partner list", advice: "The row was kept. Remove it yourself if it shouldn't be there." },
    "IMP-W-ROSTER-CLASS-NOT-FOUND": { title: "A class isn't on the class list sheet", advice: "Add it to the class list sheet or change the class." },
    "IMP-I-ROSTER-LOOK-THROUGH": { title: "Look-through partners were left out", advice: "" },
    "IMP-I-ROSTER-CLASS-LIST-NOT-LOADED": { title: "The class list sheet has no data yet", advice: "Classes were not checked. Load that sheet to check them." },
    "IMP-I-ROSTER-CLASS-NOT-CONFIGURED": { title: "This template doesn't name a class list sheet", advice: "Classes were not checked." },
    "IMP-I-ROSTER-MERGE": { title: "Partners added, updated and kept", advice: "" },
    "IMP-I-ROSTER-NO-CHANGE": { title: "The sheet already matches the partner list", advice: "" },
    "IMP-I-ROSTER-ID-TAG-IGNORED": { title: "A template ID setting was ignored", advice: "The sheet's own ID format was kept." },
    "IMP-W-SOURCE-ROSTER": { title: "This sheet was filled from the partner list", advice: "Loading this file replaces those rows. The IDs given to partners can change, and rows on other sheets that use the old IDs are not updated." },
    "IMP-W-SOURCE-MANUAL": { title: "This sheet has rows entered or edited by hand", advice: "Loading this file replaces them. Make sure you don't need them." },
    "IMP-W-ORPHAN-REFERENCE": { title: "Other sheets use values this sheet will no longer have", advice: "The load does not update those rows. Fix them afterwards." },
    "IMP-W-ROSTER-ROW-KEPT-LOOK-THROUGH": { title: "A look-through partner still has a row on the sheet", advice: "A fill leaves look-through partners out, so the row was kept. Remove it yourself if it shouldn't be there." },
    "IMP-W-ROSTER-ROW-KEPT-EXITED": { title: "A partner who has exited still has a row on the sheet", advice: "The row was kept. Remove it if they should no longer be calculated." },
    "IMP-W-ROSTER-POSSIBLE-RENAME": { title: "A partner may have been renamed", advice: "A kept row may be an added partner under an old name, so the sheet could now have both, with a new ID for the partner. Remove the old row." },
    "IMP-I-ROSTER-ID-HISTORY": { title: "New partner IDs continue after earlier IDs", advice: "New partner IDs continue after the highest ID this sheet has ever had, so an ID is never reused." },
    "IMP-E-ROLLFWD-SHEET-MISMATCH": { title: "The saved rows don't match the template's columns", advice: "Load the sheet again from a file, then roll it forward." },
    "IMP-E-ROLLFWD-DUPLICATE-KEY": { title: "Two rows have the same key", advice: "Remove or correct the duplicate rows, then check again." },
    "IMP-E-ROLLFWD-MULTIPLE-SOURCE-JOBS": { title: "Last year's project has more than one result for the same calculation", advice: "Check last year's project, then check again." },
    "IMP-W-ROLLFWD-KEY-UNMATCHED": { title: "A row from last year doesn't apply to this year's partner list", advice: "Check that the partner is on the partner list, then check again." },
    "IMP-W-ROLLFWD-DROPPED": { title: "A row from last year was left out", advice: "Check that this partner is on the partner list, then check again." },
    "IMP-W-ROLLFWD-ROW-KEPT": { title: "A row on the sheet has no value from last year", advice: "The row was kept as it is." },
    "IMP-I-ROLLFWD-NO-CHANGE": { title: "The sheet already matches last year's results", advice: "" },
    "IMP-I-ROLLFWD-NEW-PARTNER": { title: "A partner has no row from last year", advice: "" },
    "IMP-E-FILE": { title: "We couldn't read this file", advice: "Save it again as .xlsx or .csv and try again." },
    "IMP-E-NO-DATA": { title: "No data rows found", advice: "Check that the file has rows under the headings." },
    "IMP-E-ROW-LIMIT": { title: "Too many rows", advice: "Split the file into smaller files and load them one at a time." },
    "IMP-E-MISSING-COLUMN": { title: "A required column is missing", advice: "Match it to a column in your file, or add it to your file." },
    "IMP-E-MAPPING": { title: "A column match isn't valid", advice: "Go back and match the columns again." },
    "IMP-E-REQUIRED": { title: "A required value is empty", advice: "Fill in that cell." },
    "IMP-E-NUMBER": { title: "This should be a number", advice: "Remove letters and symbols from the cell." },
    "IMP-E-INTEGER": { title: "This should be a whole number", advice: "Remove any decimals, letters and symbols." },
    "IMP-E-DATE": { title: "This isn't a date we can read", advice: "Use a format like 2024-12-31." },
    "IMP-E-LIST": { title: "This isn't one of the allowed values", advice: "Use one of the values listed for this column." },
    "IMP-E-RANGE": { title: "This number is out of range", advice: "Check the value." },
    "IMP-E-LENGTH": { title: "This text is too long", advice: "Shorten it." },
    "IMP-E-DUPLICATE-KEY": { title: "This row repeats another row", advice: "Each row needs a unique value in the key column." },
    "IMP-W-UNUSED-COLUMN": { title: "A column in your file isn't used", advice: "It will be ignored." },
    "IMP-W-EMPTY-COLUMN": { title: "A column is empty", advice: "Check that this is expected." },
    "IMP-W-SKIPPED-ROW": { title: "A row was skipped", advice: "Blank rows are not loaded." },
    "IMP-W-IDENTICAL-ROWS": { title: "Two rows are identical", advice: "Check that this is expected." },
    "IMP-W-UNKNOWN-REFERENCE": { title: "This value isn't on a related sheet", advice: "Check the spelling or load the related sheet." },
    "IMP-W-SAVED-HEADER-MISSING": { title: "Your remembered match isn't in this file", advice: "Match that column again." },
    "IMP-I-REFERENCE-NOT-LOADED": { title: "A related sheet has no data yet", advice: "Load that sheet too." },
    "IMP-I-REPLACES": { title: "This will replace existing rows", advice: "" },
    "IMP-I-HEADER-ROW": { title: "We chose the heading row", advice: "" },
    "IMP-I-MAPPING-NOT-SAVED": { title: "Rows were loaded, but your column matches weren't saved", advice: "Match the columns again next time." },
  } as Record<string, { title: string; advice: string }>,
};

//LowerTier constants
export const LOWER_TIER_CONSTANT = {
  SECTION_TITLE: "Lower Tier",
  SECTION_HINT: "Values copied from each lower tier's current K-1. Read only.",
  LOADING_TEXT: "Loading lower tier values...",
  UNAVAILABLE_TEXT: "We couldn't load the lower tier values right now.",
  STALE_CHECK_FAILED_MESSAGE: "Could not check whether these values are out of date. Reload and try again.",
  STALE_BANNER_TITLE: "These values may be out of date. Rerun this tier to refresh them.",
  K1_DATE_PREFIX: "K-1 dated ",
  NO_LINES_TEXT: "No lines",
  AMOUNT_COLUMN_LABEL: "Amount",
  STATUS_REASONS: {
    NoProject: "No project exists for this lower tier yet.",
    NoCurrentK1: "This lower tier has no current K-1 yet.",
    NoLinesForRecipient: "The current K-1 has no lines for this partnership.",
  } as Record<string, string>,
  STATUS_REASON_FALLBACK: "No values are available from this lower tier.",
  SHEET_NOTE: "Amounts pulled from the lower-tier K-1s at calculation time. Read only.",
  TAG_CALCULATED: "Calculated",
  TAG_EXTERNAL: "External",
  K1_NEEDED_TEXT: "K-1 needed",
  NO_K1_AVAILABLE_TEXT: "No K-1 available",
  LINE_COLUMN_LABEL: "Line",
};

//CellTrace constants
export const CELL_TRACE_CONSTANT = {
  MENU_ITEM: "Trace Cell Value",
  DRAWER_TITLE: "Cell Value Trace",
  DISABLED_BUSY: "Save or discard your edits before tracing a value.",
  DISABLED_UNSAVED_ROW: "Save the sheet first: this row is not stored yet.",
  DISABLED_BAD_ROW: "This row has no valid stored row index.",
  DISABLED_NO_VERSION: "This sheet has no saved version to trace.",
  DISABLED_NO_DELIVERABLE: "This sheet has no deliverable to trace.",
  DISABLED_NO_COLUMN: "This column cannot be traced.",
  DISABLED_MULTI_SHEET: "Tracing is not available on sheets with several key sheets.",
  COLUMN_UNAVAILABLE: "Unavailable",
  COLUMN_LOADING: "Loading…",
  LOADING: "Tracing the stored value…",
  ERROR_GENERIC: "The value could not be traced.",
  VERSION_CHANGED_TITLE: "The sheet changed since it was loaded",
  VERSION_CHANGED_BODY: "Reload the sheet to see its current data, then trace the cell again.",
  RELOAD_SHEET: "Reload sheet",
  RELOAD_BLOCKED: "Save or discard your edits before reloading the sheet.",
  NO_DEPENDENCIES: "This value has no dependencies.",
  NULL_VALUE: "(null)",
  EMPTY_VALUE: "(empty)",
  SECTION_SELECTED: "Selected cell",
  EXPAND_ALL: "Expand all",
  COLLAPSE_ALL: "Collapse all",
  SHEET_NAME_LABEL: "Sheet Name:",
  COLUMN_NAME_LABEL: "Column Name:",
  STORED_VALUE_LABEL: "Stored Value:",
  CONTRIBUTING_VALUES_TEXT: (count: number) => `${count} contributing ${count === 1 ? "value" : "values"}`,
  TOGGLE_ROW_ARIA_LABEL: (isCollapsed: boolean, sheetName: string, logicalIdentifier: string, rowIndex: number) =>
    `${isCollapsed ? "Expand" : "Collapse"} ${sheetName} ${logicalIdentifier} row ${rowIndex}`,
  DRAWER_FAILED: "The trace could not be displayed.",
  HIGHLIGHT_CLEAR_FAILED_MESSAGE: "The highlighted cell could not be cleared.",
  MENU_FAILED_MESSAGE: "The trace option is unavailable right now. Try again.",
  ERROR_SESSION: "Your session has expired. Sign in again to trace this value.",
  ERROR_FORBIDDEN: "You do not have access to trace this value.",
  ERROR_NOT_FOUND: "The cell, sheet or deliverable to trace was not found.",
  GO_TO_CELL: "Show this cell in the sheet",
  COLLAPSE_TRACE_LABEL: "Collapse Trace",
  EXPAND_TRACE_LABEL: "Expand Trace",
  CLOSE_TRACE_TOOLTIP: "Close Trace",
  CLOSE_TRACE_ARIA_LABEL: "Close Trace",
  NAVIGATION_FAILED: {
    sheet: "That sheet is not in this calculation.",
    load: "That sheet could not be loaded.",
    row: "That row is not loaded in the sheet yet. Scroll the sheet to load more rows, then select the value again.",
    column: "That column is not shown in the sheet.",
    ambiguous: "Several columns match that value, so its cell cannot be located.",
    version: "That sheet has changed since the trace. Trace the cell again.",
    multiSheet: "Cells of a sheet with several key sheets cannot be located.",
  } as Record<string, string>,
  NAVIGATION_MAX_RETRIES: 20,
  NAVIGATION_RETRY_MS: 50,
  TRACE_API_BASE_PATH: "/api/trace-value/deliverable",
  TRACE_ISSUE: { METADATA: "TRCE-409-METADATA", VERSION: "TRCE-409-VERSION" },
  SHEET_COLUMNS_REQUEST: { page: 1, pageSize: 1 },
  PARQUET_SOURCE: "parquet",
  DRAWER_WIDTH: "85vw",
  DRAWER_WRAPPER_MIN_WIDTH: "min(360px, 100vw)",
  ROLE_STYLE: {
    SourceRecord: { label: "Original source value", className: "traceRoleSourceRecord" },
    LookupKey: { label: "Value used to find a record", className: "traceRoleLookupKey" },
    MatchedKey: { label: "Matching value", className: "traceRoleMatchedKey" },
    Returned: { label: "Value retrieved", className: "traceRoleReturned" },
    Reference: { label: "Contributing value", className: "traceRoleReference" },
  } as Record<string, { label: string; className: string }>,
  STATUS_SEVERITY: {
    Partial: "warning",
    MissingSource: "error",
    UnsupportedFormula: "error",
    CycleDetected: "warning",
    LimitExceeded: "warning",
  } as Record<string, "warning" | "error">,
  SEVERITY_CLASS: { warning: "traceCardWarning", error: "traceCardError" } as Record<string, string>,
  NO_NAMES: new Map(),
  UNRESOLVED_COLUMN: { name: null, formatValue: null },
  CLOSED_STATE: { open: false, loading: false, result: null, failure: null, columnNames: new Map(), namesPending: false },
  GENERIC_FAILURE: { statusCode: null, issueCode: null, description: "The value could not be traced.", versionChanged: false },
};

// Project-level Tax Form Lines (Project > K1 Tax Form). Only the K-1 and K-2 forms; lines are this project's own.
export const PROJECT_TAX_FORM_LINES_CONSTANT = {
  FORM_SELECT_LABEL: "PDF form",
  FORM_SELECT_PLACEHOLDER: "Select a PDF form",
  EDIT_MODE_LABEL: "Edit mode",
  ADD_LINE_ITEM_LABEL: "Add line item",
  NO_FORMS_MESSAGE: "No K-1 or K-2 form is available for this project.",
  LOAD_FORMS_FAILED_MESSAGE: "Failed to load the project's forms.",
  LOAD_LINES_FAILED_MESSAGE: "Failed to load this form's lines.",
  LOAD_FIELDS_FAILED_MESSAGE: "Failed to load the PDF's fields.",
  NO_ACCESS_TITLE: "You don't have access to this project's tax form lines.",
  NO_ROWS_MESSAGE: "This form has no lines.",
  LINE_HEADER: "Line",
  SECTION_HEADER: "Section",
  MAPPING_HEADER: "Mapping",
  ACTIONS_HEADER: "Actions",
  UNMAPPED_LABEL: "Unmapped",
  YES_LABEL: "Yes",
  NO_LABEL: "No",
  EDIT_ARIA_LABEL: "Edit line",
  FIELD_ON_PDF_PAGE: (page: number) => `The highlighted field is on PDF page ${page}.`,
  FIELD_ON_TABLE_PAGE: (page: number) => `That line is on table page ${page}.`,
  TOTAL_LINES: (total: number) => `${total} lines`,
  LOCK_LOST_MESSAGE: "Your edit lock on this form was lost. Turn Edit mode on again to continue.",
  LOCK_HELD_BY_OTHER: (until: string) => `Another user is editing this form in this project until ${until}.`,
  LOCK_HELD_GENERIC: "Another user is editing this form in this project.",
  LOCK_FORBIDDEN_MESSAGE: "You don't have permission to edit this project's lines.",
  LOCK_FAILED_MESSAGE: "Couldn't start Edit mode. Try again.",
  WRITES_DISABLED_MESSAGE: "Editing tax form lines for a project is not switched on yet.",
  ADD_MODAL_TITLE: "Add line item",
  ADD_SUBMIT_LABEL: "Add line",
  ADD_SUCCESS_MESSAGE: "Line item added to this project.",
  ADD_FAILED_MESSAGE: "The line item could not be added.",
  NO_FREE_FIELDS_MESSAGE: "Every PDF field on this form already has a line. To change one, edit that line.",
  PDF_FIELD_LABEL: "PDF field",
  PDF_FIELD_PLACEHOLDER: "Choose a PDF field without a line",
  PDF_FIELD_REQUIRED_MESSAGE: "Choose a PDF field.",
  LINE_FIELD_LABEL: "Line",
  LABEL_FIELD_LABEL: "Label",
  LABEL_REQUIRED_MESSAGE: "A label is required.",
  MAPPING_TYPE_LABEL: "Mapping type",
  MAPPING_TYPE_PLACEHOLDER: "Unmapped",
  LINE_CODES_LABEL: "Output codes",
  LINE_CODES_PLACEHOLDER: "Type a code and press Enter",
  EXPECTED_VALUE_LABEL: "Checkbox value",
  NEEDS_REVIEW_LABEL: "Needs review",
  SECTION_LABEL: "Section",
  SUB_SECTION_LABEL: "Sub-section",
  SORT_LABEL: "Sort",
  EDIT_MODAL_TITLE: "Edit line",
  DELETE_LABEL: "Delete line",
  DELETE_CONFIRM_TITLE: "Delete this line from the project?",
  DELETE_SUCCESS_MESSAGE: "Line deleted from this project.",
  DELETE_FAILED_MESSAGE: "The line could not be deleted.",
  RESET_LABEL: "Reset to default",
  RESET_CONFIRM_TITLE: "Use the shared mapping again for this project?",
  RESET_SUCCESS_MESSAGE: "The line uses the shared mapping again.",
  RESET_FAILED_MESSAGE: "The line could not be reset.",
  CONFIRM_OK_LABEL: "Yes",
  CANCEL_LABEL: "Cancel",
  EDIT_SAVE_LABEL: "Save",
  EDIT_SUCCESS_MESSAGE: "Line saved for this project.",
  EDIT_FAILED_MESSAGE: "The line could not be saved.",
  MODAL_WIDTH: "35rem",
  PDF_NOT_FOUND_MESSAGE: "No PDF is available for this form.",
} as const;
